// Sistema di traduzione di Oncer — leggero, senza librerie esterne, per
// restare coerente con lo stile del resto del progetto.
//
// Come funziona:
//  - LANG viene calcolata una sola volta all'avvio, leggendo la lingua
//    impostata sul telefono (navigator.language). Se non è una delle 4
//    lingue supportate, si torna all'italiano.
//  - t("qualche_chiave") restituisce la stringa tradotta nella lingua
//    rilevata. Se manca la traduzione in quella lingua, torna all'italiano;
//    se manca anche lì, restituisce la chiave stessa (così si nota subito
//    in giro per l'app se una stringa non è stata tradotta, invece di
//    sparire silenziosamente).
//  - t("chiave",{giorno:5}) sostituisce dentro la stringa i segnaposto tipo
//    {giorno} con il valore passato — utile per le frasi con numeri o nomi
//    incorporati, così ogni lingua può tenere l'ordine delle parole giusto
//    per lei invece di incollare pezzi a forza.
//
// Le chiavi sono organizzate per schermata (un blocco per file), così è
// facile trovarle ed estenderle mano a mano che si traduce ogni vista.
// IMPORTANTE: importare sempre come `import { t as tr } from '../lib/i18n.js'`
// nei file delle viste — la variabile `t` è già usata ovunque per il tema.

const SUPPORTED=["it","en","es","fr"];

function detectLang(){
  try{
    const nav=(navigator.language||navigator.userLanguage||"it").toLowerCase();
    const short=nav.split("-")[0];
    if(SUPPORTED.includes(short)) return short;
  }catch(e){}
  return "it";
}

export const LANG=detectLang();

// Utile ovunque serva formattare date con toLocaleDateString nella lingua giusta.
export const LOCALE=({it:"it-IT",en:"en-US",es:"es-ES",fr:"fr-FR"})[LANG]||"it-IT";

export const STR={
  it:{
    // ── Feed ──────────────────────────────────────────────────────────
    feed_page_singular:"pagina",
    feed_page_plural:"pagine",
    feed_page_abbr:"pag.",
    feed_from_followed_books:"📖 Dai libri di chi segui",
    feed_followed_empty_1:"Qui vedrai le pagine dei micro-libri aperti delle persone che segui.",
    feed_followed_empty_2:"Segui qualcuno, o aspetta che pubblichi qualcosa.",
    feed_share_external_title:"Condividi fuori da Oncer",
    feed_share_watermark_note:"🔖 Ogni immagine condivisa porta con sé la firma Oncer — non è rimovibile.",
    feed_share_preparing:"Preparazione...",
    feed_share_photo_btn:"↗ Condividi la foto",
    feed_share_no_native_note:"Il tuo browser non apre il pannello di condivisione con la foto — con le opzioni qui sotto si condivide solo un link, senza immagine.",
    feed_share_or_choose:"Oppure scegli",
    feed_share_save_image:"⬇ Salva immagine",
    feed_share_ig_tiktok_note:"Per Instagram e TikTok: salva l'immagine, poi caricala come Storia o Reel.",
    feed_share_message:"La mia pagina su Oncer 🔖 Be a Oncer. — oncer.app",
    feed_native_share_text:"Be a Oncer. 🔖 oncer.app",
    feed_send_page_to:"Invia pagina a...",
    feed_sending:"Invio...",
    feed_follow_to_send:"Segui qualcuno per poter mandare le pagine.",
    feed_share_label:"Condividi",
    feed_send_label:"Invia",
    feed_you:"tu",
    feed_following:"chi segui",
    feed_find_your_style:"🎞 Trova il tuo stile",
    feed_discover_filters:"Scopri i filtri di tendenza o crea il tuo →",
    feed_no_photo_today:"Non hai ancora scattato oggi",
    feed_reminder_pre:"Avevi scelto le",
    feed_reminder_post:"come promemoria — tocca per scattare ora",
    feed_year_summary_cta:"Tocca per vedere il riepilogo del tuo anno →",
    feed_special_moment:"Un momento speciale per scattare oggi",
    feed_year_ago_today:"✨ Un anno fa, oggi",
    feed_toast_share_failed:"Non sono riuscito ad aprire la condivisione, riprova.",
    feed_toast_reaction_remove_failed:"Non sono riuscito a togliere la reazione, riprova.",
    feed_toast_reaction_save_failed:"La reazione non si è salvata, riprova.",
    feed_toast_send_failed:"Non sono riuscito a inviarla, riprova.",
    // ── Capture ───────────────────────────────────────────────────────
    capture_title:"Scatta",
    capture_already_done_title:"Hai già scattato oggi per il libro annuale!",
    capture_already_done_sub1:"Pagina {day} del {year} è scritta.",
    capture_already_done_sub2:"Torna domani per la prossima pagina del libro.",
    capture_shoot_for_micro:"📎 Scatta comunque per un micro-libro",
    capture_add_another_to_event:"📸 Aggiungi un'altra foto a questo libro",
    capture_event_book_note:"Libro evento · foto illimitate oggi",
    capture_view_your_book:"Vedi il tuo libro",
    capture_tap_to_shoot:"tocca per scattare",
    capture_think_twice_1:"🔒 Pensaci bene: sarà lo scatto scelto per la tua giornata.",
    capture_think_twice_2:"Non si cancella. Resta per sempre.",
    capture_write_thought_only:"Oggi niente foto — scrivi solo un pensiero",
    capture_where_to_save:"dove salvi questo scatto?",
    capture_words_only_suffix:"solo parole, oggi",
    capture_which_font:"Con che carattere?",
    capture_drag_to_reframe:"trascina per spostare l'inquadratura",
    capture_filter_label:"🎞 filtro",
    capture_browse_all:"Sfoglia tutti →",
    capture_sticker_label:"🎨 adesivo (opzionale)",
    capture_annual_book_label:"libro annuale",
    capture_already_done_today:"Già fatto per oggi",
    capture_only_micro_note:"questa foto va solo nel micro-libro scelto sotto",
    capture_also_diary_label:"Vuoi inserirla anche nel tuo diario personale?",
    capture_also_diary_hint:"Se attivo, conterà come foto del giorno e comparirà anche nel Feed.",
    capture_always_in_book:"sarà sempre nel tuo libro",
    capture_annual_book_year:"Libro annuale {year}",
    capture_permanent:"permanente",
    capture_choose_micro_required:"scegli un micro-libro (obbligatorio)",
    capture_add_to_micro_optional:"aggiungila anche a un micro-libro? (facoltativo)",
    capture_micro_book_word:"micro-libro",
    capture_create_new_micro:"Crea nuovo micro-libro",
    capture_micro_title_placeholder:"nome del micro-libro, es. Viaggio a Lisbona",
    capture_cancel:"Annulla",
    capture_creating:"Creazione...",
    capture_create:"Crea",
    capture_add_to_shared_optional:"aggiungila anche a un libro condiviso? (facoltativo)",
    capture_with_prefix:"con",
    capture_your_thought_today:"✏️ il tuo pensiero di oggi",
    capture_add_a_caption:"aggiungi una frase",
    capture_thought_placeholder:"cosa hai in mente oggi...",
    capture_caption_placeholder:"cosa resterà di oggi...",
    capture_thought_not_removable:"✦ Questo pensiero non potrà essere eliminato.",
    capture_photo_not_removable:"✦ Questa foto non potrà essere eliminata.",
    capture_toast_micro_create_failed:"Non sono riuscito a creare il micro-libro, riprova.",
    capture_toast_choose_micro_first:"Scegli un micro-libro prima di salvare.",
    capture_toast_already_today_reload:"Hai già una pagina per oggi — ricarica per vederla.",
    capture_toast_saved_local_not_uploaded:"Salvata sul telefono, non caricata online — controlla la connessione.",
    capture_toast_micro_not_synced:"Non sono riuscito a salvarla — il micro-libro scelto non è ancora sincronizzato online. Riprova tra poco.",
    capture_toast_already_today_local_only:"Hai già una pagina per oggi — questa è salvata solo sul telefono.",
    capture_toast_saved_local_not_synced:"Salvata sul telefono, ma non sincronizzata online. Controlla la connessione.",
    capture_toast_shared_publish_failed:"Non sono riuscito a pubblicarla nel libro condiviso.",
    capture_toast_shared_already_today:"Hai già pubblicato la tua foto di oggi per questo libro — riprova domani.",
    capture_toast_camera_failed:"Non sono riuscito ad aprire la fotocamera — controlla i permessi dell'app.",
    capture_saving:"Salvataggio...",
    capture_write_something_first:"Scrivi qualcosa prima di salvare",
    capture_choose_micro_before_save:"Scegli un micro-libro prima di salvare",
    capture_save_in_micro:"Salva nel micro-libro",
    capture_save_in_page:"Salva nella pagina {day}",
    capture_page_published:"Pagina {day} pubblicata!",
    capture_tell_world:"Fai sapere al mondo che sei un Oncer — condividi questo momento anche fuori dall'app.",
    capture_share_now:"↗ Condividi ora",
    capture_continue_in_app:"Continua nell'app",
    capture_kind_photo:"foto",
    capture_blocked_title:"Questa {kind} non può essere pubblicata",
    capture_blocked_desc:"Il nostro controllo automatico l'ha segnalata come possibile contenuto violento o forte. Se pensi sia un errore, scrivici — la controlliamo noi di persona.",
    capture_write_to_us:"Scrivici, penso sia un errore",
    capture_got_it:"Ho capito",
    capture_blocked_email_subject:"Foto bloccata per errore",
    // ── Navigation (barra in basso) ──────────────────────────────────
    nav_feed:"feed",
    nav_capture:"scatta",
    nav_micro:"micro",
    nav_notebook:"notebook",
    nav_discover:"scopri",
    nav_profile:"profilo",
    // ── Temi (nomi e categorie) ───────────────────────────────────────
    theme_neon:"Neon Nights", theme_pastel:"Soft Hours", theme_retro:"Anni 90", theme_clean:"Pure",
    theme_concrete:"Concrete", theme_graffiti:"Graffiti", theme_subway:"Subway",
    theme_vinyl:"Vinyl", theme_rave:"Rave", theme_jazzclub:"Jazz Club",
    theme_noir:"Noir", theme_coldcase:"Cold Case",
    theme_industrial:"Industrial", theme_darkroom:"Darkroom",
    theme_sakura:"Sakura", theme_zen:"Zen", theme_akiba:"Akihabara",
    theme_redgold:"Red & Gold", theme_ink:"Chinese Ink", theme_jade:"Jade Palace",
    theme_aurora:"Aurora", theme_ambra:"Ambra",
    cat_originali:"Originali", cat_urban:"Urban", cat_musica:"Musica",
    cat_poliziesco:"Poliziesco", cat_underground:"Underground",
    cat_japan:"Japan", cat_chinese:"Chinese", cat_aggiuntivi:"Aggiuntivi",
    // ── Preset micro-libro ──────────────────────────────────────────
    mp_vacation:"Vacanza", mp_trip:"Viaggio", mp_festival:"Festival", mp_wedding:"Matrimonio",
    mp_birthday:"Compleanno", mp_road:"Road Trip", mp_concert:"Concerto", mp_custom:"Custom",
    // ── Micro-libri ──────────────────────────────────────────────────
    micro_page_singular:"pagina", micro_page_plural:"pagine",
    micro_with_prefix:"con {list}",
    micro_loading_pages:"Caricamento pagine...",
    micro_load_error_title:"Non sono riuscito a caricare le pagine",
    micro_no_pages_title:"Ancora nessuna pagina",
    micro_no_pages_sub:"Scatta la prima foto per iniziare.",
    micro_shoot_now:"✦ Scatta ora",
    micro_all_pages:"Tutte le pagine",
    micro_the_pages:"Le pagine",
    micro_shoot_today:"📷 Scatta oggi",
    micro_print_book:"🖨 Stampa micro-libro",
    micro_share_outside:"Condividi fuori da Oncer",
    micro_page_locked_note:"🔒 Questa pagina è permanente e non può essere modificata.",
    micro_header_title:"📎 Micro-libri",
    micro_new_btn:"+ Nuovo",
    micro_invite_msg:"ti ha invitato a un libro condiviso",
    micro_accept:"Accetta",
    micro_decline:"Rifiuta",
    micro_accept_failed:"Non sono riuscito ad accettare, riprova.",
    micro_decline_failed:"Non sono riuscito a rifiutare, riprova.",
    micro_what_are_title:"Cosa sono i micro-libri?",
    micro_what_are_desc:"Libri per momenti specifici — vacanze, viaggi, feste. Nessun limite di pagine, stampabili separatamente.",
    micro_in_progress:"Micro-libri personali",
    micro_photos:"foto",
    micro_add_pill:"✦ Aggiungi",
    micro_planned_progress:"{pages}/{max} foto pianificate",
    micro_shared_books:"Libri condivisi",
    micro_pag_short:"pag.",
    micro_archived:"Archiviati",
    micro_empty_title:"Nessun micro-libro",
    micro_empty_sub:"Crea il primo per un momento speciale.",
    micro_create_btn:"+ Crea micro-libro",
    micro_customize_title:"✏️ Personalizza",
    micro_what_moment:"Che momento è?",
    micro_title_label:"Titolo",
    micro_title_placeholder:"es. Sardegna estate, Tokyo marzo…",
    micro_who_writes:"Chi lo scrive?",
    micro_only_me:"Solo io",
    micro_shared_kind:"👥 Condiviso",
    micro_kind_timed:"📎 A tempo",
    micro_kind_family:"👨‍👩‍👧‍👦 Famiglia, senza fine",
    micro_kind_evento:"💍 Evento",
    micro_event_type_note:"Chi partecipa entra tramite QR/link, senza bisogno di avere già l'app o un account Oncer.",
    micro_event_limit_title:"Limite foto e prezzo",
    micro_event_limit_sub:"Scegli quante foto includere: il prezzo di stampa è mostrato subito, senza sorprese.",
    micro_event_limit_nolimit:"Nessun limite",
    micro_event_limit_nolimit_sub:"calcolato a fine evento",
    micro_event_limit_price_loading:"Calcolo…",
    micro_event_limit_price_unavailable:"Prezzo non disponibile ora",
    micro_event_progress_label:"foto raccolte",
    micro_event_fairuse_reminder:"Il limite si sta per esaurire: ricorda agli invitati di lasciare spazio anche agli altri.",
    micro_closed_badge:"chiuso",
    micro_event_invite_title:"QR e link per gli invitati",
    micro_copy_link:"Copia link",
    micro_link_copied:"Copiato ✓",
    micro_close_event_btn:"Chiudi il libro dell'evento",
    micro_closing:"Chiusura in corso…",
    micro_delete_btn:"Elimina micro-libro",
    micro_delete_shared_btn:"Elimina libro condiviso",
    micro_delete_confirm:"Eliminare questo micro-libro? Tutte le foto al suo interno verranno cancellate. Non si può annullare.",
    micro_delete_shared_confirm:"Eliminare questo libro condiviso? Verrà rimosso per tutti i partecipanti, che riceveranno una notifica. Non si può annullare.",
    micro_delete_success:"Micro-libro eliminato",
    micro_delete_shared_success:"Libro condiviso eliminato",
    micro_delete_failed:"Eliminazione non riuscita, riprova",
    micro_deleting:"Eliminazione in corso…",
    micro_close_event_confirm:"Chiudere il libro? Dopo non si potranno più aggiungere foto, e a tutti i partecipanti arriverà una mail con il libro pronto da vedere.",
    micro_close_event_success:"Libro chiuso — abbiamo avvisato tutti via email.",
    micro_close_event_failed:"Non siamo riusciti a chiudere il libro, riprova.",
    micro_who_else:"Chi altro ne fa parte?",
    micro_search_username:"Cerca username...",
    micro_cover_theme:"Tema copertina",
    micro_creating:"Creazione...",
    micro_create_cta:"Crea micro-libro →",
    micro_toast_need_login:"Devi essere connesso per creare un libro condiviso.",
    micro_toast_create_failed:"Non sono riuscito a creare il libro condiviso, riprova.",
    micro_toast_local_only:"Creato solo sul telefono, non sincronizzato online.",
    // ── Stili Notebook (calligrafia) ──────────────────────────────────
    nbs_cursive_name:"Corsivo", nbs_cursive_desc:"Elegante e fluido",
    nbs_print_name:"Stampatello", nbs_print_desc:"Chiaro e ordinato",
    nbs_marker_name:"Pennarello", nbs_marker_desc:"Deciso e bold",
    nbs_romantic_name:"Romantico", nbs_romantic_desc:"Morbido e poetico",
    nbs_retro_name:"Macchina", nbs_retro_desc:"Macchina da scrivere",
    nbs_minimal_name:"Minimal", nbs_minimal_desc:"Semplice e personale",
    // ── Carte Notebook ───────────────────────────────────────────────
    nbp_ivory_name:"Avorio", nbp_white_name:"Bianco", nbp_kraft_name:"Kraft",
    nbp_night_name:"Notte", nbp_sage_name:"Salvia",
    // ── Notebook ─────────────────────────────────────────────────────
    nb_time_today:"oggi",
    nb_writes_in:"scrive in {style}",
    nb_page_label:"📄 pagina {day}",
    nb_placeholder:"scrivi qui...",
    nb_your_writing:"✏️ La tua scrittura",
    nb_paper_type:"Tipo di carta",
    nb_sample_text:"esempio di testo...",
    nb_sample_greeting:"Ciao, come stai oggi?",
    nb_blocked_title:"Questo messaggio non può essere inviato",
    nb_blocked_desc:"Il nostro controllo automatico l'ha segnalato come possibile linguaggio d'odio. Se pensi sia un errore, scrivici — la controlliamo noi di persona.",
    nb_blocked_contact:"Scrivici, penso sia un errore",
    nb_blocked_dismiss:"Ho capito",
    nb_toast_send_failed:"Messaggio non inviato, riprova.",
    nb_opening_chat:"Apro la conversazione...",
    nb_empty_title:"Nessuna conversazione",
    nb_empty_sub:"Cerca un Oncer e inizia a scrivere.",
    nb_new_conversation:"+ Nuova conversazione",
    nb_new_conversation_title:"Nuova conversazione",
    nb_no_messages_yet:"Nessun messaggio ancora — scrivi tu il primo",
    nb_you_prefix:"tu: ",
    nb_shared_page_preview:"📄 pagina condivisa — pag. {day}",
    nb_search_placeholder:"Cerca un Oncer...",
    nb_already_chatting:"già in chat",
    nb_searching:"Cerco...",
    nb_no_users_found:"Nessun Oncer trovato.",
    nb_toast_profile_not_found:"Non sono riuscito a trovare questo profilo.",
    nb_toast_open_failed:"Non sono riuscito ad aprire la conversazione.",
    nb_delete_action:"Elimina",
    nb_toast_delete_failed:"Non sono riuscito a cancellare la conversazione, riprova.",
    // ── Conversazione guida "Oncer Experience" ──────────────────────
    nb_exp_msg1:"Ciao, benvenuto su Oncer 👋 Sono qui per mostrarti cosa puoi fare, con calma — leggi quando vuoi, questa conversazione resta qui per sempre.",
    nb_exp_msg2:"L'idea di base è semplice: una foto al giorno, per sempre. Non si cancella, non si modifica — è la pagina di oggi del tuo libro dell'anno. Se un giorno preferisci le parole a una foto, puoi scrivere solo un pensiero, scegliendo anche il carattere che preferisci.",
    nb_exp_msg3:"📚 La tua Libreria raccoglie tutte le pagine, giorno per giorno, dal primo gennaio. Anche i giorni in cui non hai scattato hanno un piccolo disegno tutto loro — nessuna pagina resta davvero vuota.",
    nb_exp_msg4:"📎 I micro-libri sono raccolte più piccole dentro il tuo mondo — un viaggio, una vacanza, un progetto. Si affiancano al libro principale, non lo sostituiscono: ogni foto puoi metterla in entrambi insieme. Puoi crearne uno anche insieme a un amico o a tutta la famiglia, così ci scattate dentro tutti insieme.",
    nb_exp_msg5:"🎯 In Obiettivi trovi piccole sfide stagionali — completandole sblocchi ricompense: coccarde per il profilo, adesivi per le foto, e altro ancora.",
    nb_exp_msg6:"⏳ Oncer Future è per i messaggi che scrivi oggi e arrivano più avanti — a te stesso tra un anno, a un amico, alla tua famiglia. Si sbloccano solo alla data che scegli tu.",
    nb_exp_msg7:"✏️ Il Notebook — questo, dove siamo ora — è dove scrivi agli amici, come un vero taccuino: puoi scegliere la carta e la calligrafia per ogni conversazione, per renderla tua.",
    nb_exp_msg8:"🎞 Nella schermata Filtri trovi quelli di tendenza, ma puoi anche crearne uno tuo e renderlo pubblico per tutta la community.",
    nb_exp_msg9:"Un'ultima cosa: la tua libreria (lo scaffale con tutti i tuoi libri) si può riordinare trascinando i dorsi dei libri — mettila come preferisci tu.",
    nb_exp_msg10:"🖨 Vedrai anche un pulsante per stampare i tuoi libri — è in arrivo: presto potrai trasformare un anno intero in un oggetto vero, da tenere in mano.",
    nb_exp_msg11:"Questo è tutto quello che c'è da sapere per iniziare. Il resto lo scopri scattando — un giorno alla volta. Be a Oncer. 🔖",
    // ── Scopri ───────────────────────────────────────────────────────
    disc_header_title:"🔭 Scopri",
    disc_your_library:"La tua libreria",
    disc_followers_needed:"ancora {n} follower per aprire",
    disc_follower_count:"{n} follower",
    disc_to_unlock:"{n} per aprire",
    disc_tab_people:"👤 Persone",
    disc_tab_libraries:"📖 Librerie",
    disc_tab_themes:"🎨 Temi",
    disc_tab_micro:"📎 Micro-libri",
    disc_tab_filters:"🎞 Filtri",
    disc_search_placeholder:"Cerca un Oncer per username...",
    disc_search_hint:"Cerca username per trovare altri Oncer e seguirli.",
    disc_searching:"Cerco...",
    disc_no_people_found:"Nessun Oncer trovato con questo username.",
    disc_loading:"Caricamento...",
    disc_no_libraries:"Nessun altro Oncer da mostrare ancora.",
    disc_verified_badge:"📖 Verificato",
    disc_follow_btn:"+ Segui",
    disc_libraries_count:"{n} librerie",
    disc_exclusive_title:"✨ Sbloccabili solo qui",
    disc_unlocked_hint:"Sbloccato — scegli dalle Impostazioni",
    disc_tap_to_unlock:"Tocca per sbloccarlo",
    disc_no_micro:"Nessun micro-libro completato da mostrare ancora.",
    disc_micro_by:"di @{u} · {pages} pagine",
    disc_filters_hero_title:"🎞 Trova il tuo stile",
    disc_filters_hero_desc:"Ogni filtro racconta chi sei. Sfoglia la galleria, crea il tuo, fatti scoprire da altri Oncer.",
    disc_filters_open_gallery:"Apri la galleria →",
    disc_filters_trending_title:"🔥 Di tendenza questo mese",
    disc_filter_uses:"{n} usi",
    // ── Profilo ──────────────────────────────────────────────────────
    prof_add_bio:"Aggiungi una bio →",
    prof_streak_badge_title:"{n} giorni di fila con tutti gli obiettivi giornalieri completati",
    prof_streak_badge_off_title:"Completa tutti gli obiettivi di oggi per accendere la fiamma",
    prof_loading_profile:"Caricamento profilo...",
    prof_open_library_badge:"📖 Libreria Aperta",
    prof_stat_followers:"follower",
    prof_stat_following:"seguiti",
    prof_stat_books:"libri",
    prof_following_already:"✓ Segui già",
    prof_request_sent:"Richiesta inviata",
    prof_goals_label:"Obiettivi",
    prof_annual_books_label:"libri annuali",
    prof_private_library_title:"Libreria privata",
    prof_private_desc1:"@{username} non ha ancora una libreria pubblica.",
    prof_private_desc2:"Segui per vedere di più quando l'aprirà.",
    prof_toast_generic_fail:"Non ci sono riuscito, riprova tra poco.",
    prof_book_meta:"{theme} · {pages} pagine",
    prof_in_progress_badge:"IN CORSO",
    prof_micro_books_label:"micro-libri",
    prof_micro_meta:"📎 micro-libro · {pages} pagine",
    prof_shared_books_label:"libri condivisi",
    prof_pages_count:"{n} pagine",
    // ── Nomi e categorie filtri ──────────────────────────────────────
    filtname_none:"Originale", filtname_golden:"Golden Hour", filtname_noir:"Cinema Noir",
    filtname_dream:"Dreamy", filtname_vivid:"Vivid Pop", filtname_vintage83:"'83 Vintage",
    filtname_moody:"Moody Blue", filtname_clean:"Clean White",
    filtname_c_sofia1:"Petalo", filtname_c_luca1:"Afterhours", filtname_c_marco1:"Detective",
    filtname_ft_solstice:"Solstizio", filtname_ft_frost:"Brina",
    filtname_os_portra:"Portra Calda", filtname_os_ektar:"Ektar Vivido",
    filtname_os_superia:"Superia Verde", filtname_os_velvia:"Velvia Paesaggio",
    filtname_os_gold:"Gold Nostalgia", filtname_os_polaroid:"Polaroid Sbiadita",
    filtname_os_ektachrome:"Ektachrome Sogno", filtname_os_agfa:"Agfa Europa",
    filtname_os_trix:"Tri-X Contrasto", filtname_os_pro160s:"Pro160S Soffusa",
    filtcat_setup:"Oncer Setup", filtcat_trend:"Tendenza", filtcat_vintage:"Vintage",
    filtcat_bn:"B&N", filtcat_vivid:"Vivido", filtcat_pastel:"Pastello",
    filtcat_cinema:"Cinema", filtcat_community:"Community",
    // ── Schermata Filtri ─────────────────────────────────────────────
    filt_header_title:"🎞 Filtri",
    filt_create_btn:"+ Crea",
    filt_hero_title:"✨ Trova il tuo stile distintivo",
    filt_hero_desc:"Ogni Oncer ha un modo diverso di raccontare i suoi giorni. Crea un filtro tuo e fatti scoprire da tutta la community.",
    filt_pick_hint:"Scegli un filtro da applicare al tuo scatto di oggi.",
    filt_search_placeholder:"Cerca un filtro o un creator...",
    filt_cat_all:"Tutti",
    filt_results_count:"Risultati ({n})",
    filt_all_filters:"Tutti i filtri",
    filt_by_creator:"di @{creator}",
    filt_use_this:"Usa questo →",
    filt_seasonal_goal_fallback:"Obiettivo stagionale",
    filt_none_found:"Nessun filtro trovato.",
    filt_create_title:"Crea il tuo filtro",
    filt_name_label:"Nome del filtro",
    filt_name_placeholder:"es. Nostalgia, Sunset Mood...",
    filt_base_label:"Base",
    filt_slider_saturation:"🎨 Saturazione",
    filt_slider_contrast:"◐ Contrasto",
    filt_slider_brightness:"☀️ Luminosità",
    filt_slider_warmth:"🌡️ Calore",
    filt_warmth_warm:"+{v} caldo",
    filt_warmth_cold:"{v} freddo",
    filt_warmth_neutral:"neutro",
    filt_advanced_label:"Avanzate",
    filt_slider_hue:"🌈 Tonalità",
    filt_slider_sepia:"📜 Seppia",
    filt_slider_vignette:"🖤 Vignetta",
    filt_slider_grain:"🎞 Grana",
    filt_absent:"assente",
    filt_toast_local_only:"Creato solo sul telefono, non condiviso con la community.",
    filt_publishing:"Pubblicazione...",
    filt_publish_btn:"Pubblica filtro",
    filt_published_title:"Filtro pubblicato!",
    filt_published_desc:"\"{name}\" è ora visibile a tutta la community Oncer. Ora sta a te farlo scoprire.",
    filt_view_gallery_btn:"Vedi nella galleria",
    // ── Chiusura Anno ────────────────────────────────────────────────
    yc_title:"Il tuo {year} sta per chiudersi",
    yc_subtitle:"Un anno di scatti, un giorno alla volta. Ecco cosa hai costruito.",
    yc_stat_pages:"pagine scritte",
    yc_stat_streak:"striscia più lunga",
    yc_stat_top_month:"il tuo mese più attivo",
    yc_stat_filters:"filtri diversi usati",
    yc_print_btn:"🖨 Stampa il libro del {year}",
    yc_footer_note:"Il {nextYear} comincia da solo, alla tua prossima pagina — nessun pulsante da premere. Il libro di quest'anno resta per sempre così com'è.",
    // ── Oncer Future ─────────────────────────────────────────────────
    of_tab_write:"Scrivi",
    of_tab_received:"Ricevuti",
    of_write_intro:"Scrivi qualcosa oggi. Non lo leggerai (né lo leggerà chi scegli) finché non arriva la data che scegli tu — a te stesso, a un amico, o a un ricordo di famiglia.",
    of_recipient_label:"Per chi è?",
    of_recipient_self:"Te stesso",
    of_recipient_friend:"Un amico",
    of_recipient_family:"Famiglia",
    of_family_placeholder:"Per chi in famiglia? (facoltativo, es. 'per mamma')",
    of_unlock_when_label:"Quando si sblocca?",
    of_unlock_6m:"Tra 6 mesi",
    of_unlock_1y:"Tra 1 anno",
    of_unlock_nextnewyear:"Al prossimo 1° gennaio",
    of_unlock_custom:"Scegli data",
    of_content_placeholder:"Scrivi qui...",
    of_sending:"Invio...",
    of_send_btn:"Sigilla e invia al futuro",
    of_toast_send_failed:"Non sono riuscito a inviarlo, riprova.",
    of_unlocked_label:"Sbloccati",
    of_written_by_you:"✍️ Scritto da te",
    of_from_user:"💌 Da @{username}",
    of_in_transit_label:"In viaggio verso il futuro",
    of_unlocks_on:"Si sblocca il {date}",
    of_empty_state:"Non hai ancora scritto nulla al futuro.",
    // ── Schermata Obiettivi ──────────────────────────────────────────
    goals_header_title:"🎯 Obiettivi",
    goals_rewards_link:"Ricompense →",
    goals_season_banner:"{icon} Stagione: {name}",
    goals_event_banner:"✨ {icon} È tempo di {event}! Obiettivi speciali disponibili.",
    goals_season_default_banner:"Completa gli obiettivi trimestrali per sbloccare ricompense a tema.",
    goals_section_today:"Oggi",
    goals_daily_subtitle:"Cambiano ogni giorno",
    goals_daily_all_done:"Giornata completa ✨",
    goals_daily_streak_toast:"🔥 Giornata perfetta! Serie di {n} giorni",
    goals_section_month:"Questo mese",
    goals_section_season:"Obiettivi di stagione",
    goals_footer_note:"Nuovi obiettivi e ricompense arrivano a ogni cambio di stagione — inverno, primavera, estate, autunno — con edizioni speciali per Pasqua, Ringraziamento e Natale.",
    goals_claimed_title:"Hai sbloccato: {name}",
    goals_claim_sticker:"Disponibile ora quando scatti una nuova pagina.",
    goals_claim_badge:"Visibile ora sul tuo profilo.",
    goals_claim_divider:"Puoi equipaggiarlo sul tuo scaffale dall'inventario.",
    goals_claim_filter:"Nuovo filtro esclusivo nella galleria.",
    goals_claim_cta:"Fantastico!",
    // ── Schermata Ricompense ─────────────────────────────────────────
    rewards_header_title:"🎁 Le tue ricompense",
    rewards_unlocked_count:"{n} di {total} sbloccate",
    rewards_cat_all:"Tutti",
    rewards_cat_sticker:"🎨 Adesivi",
    rewards_cat_badge:"🎖️ Coccarde",
    rewards_cat_divider:"📚 Fermalibri",
    rewards_cat_filter:"🎞 Filtri",
    reward_usage_sticker:"Si usa quando scatti una foto",
    reward_usage_badge:"Compare sul tuo profilo",
    reward_usage_divider:"Si usa nello scaffale libreria",
    reward_usage_filter:"Si usa nella fotocamera",
    rewards_equip_title:"Equipaggia sullo scaffale (2 posti)",
    rewards_slot_label:"Posto {n}",
    rewards_none_option:"— nessuno —",
    rewards_empty_state:"Ancora nessuna ricompensa qui — completa gli obiettivi per sbloccarle.",
    rewards_still_locked_label:"Ancora da sbloccare",
    // ── Obiettivi giornalieri/mensili/trimestrali ────────────────────
    goal_d_post:"Scatta la pagina di oggi",
    goal_d_caption:"Scrivi una didascalia oggi",
    goal_d_react1:"Reagisci ad almeno 1 pagina",
    goal_d_react2:"Reagisci a 2 pagine di amici",
    goal_d_react3:"Reagisci a 3 pagine di amici",
    goal_d_filter:"Usa un filtro oggi",
    goal_d_sticker:"Metti uno sticker oggi",
    goal_d_caption_long:"Scrivi una didascalia più lunga (20+ caratteri)",
    goal_d_combo:"Completa la pagina con didascalia e sticker",
    goal_d_early:"Pubblica la tua pagina prima di mezzogiorno",
    goal_m_streak7:"Striscia di 7 giorni consecutivi",
    goal_m_pages15:"15 pagine questo mese",
    goal_m_filters3:"Usa 3 filtri diversi",
    goal_q1_pages60:"60 pagine questo inverno",
    goal_q1_easter:"Scatta durante la settimana di Pasqua",
    goal_q1_streak14:"Striscia di 14 giorni consecutivi",
    goal_q1_shared:"Fai parte di un libro condiviso",
    goal_q1_sticker:"Usa un adesivo su una pagina",
    goal_q2_pages60:"60 pagine questa primavera",
    goal_q2_micro:"Crea un micro-libro",
    goal_q2_streak14:"Striscia di 14 giorni consecutivi",
    goal_q2_filters5:"Usa 5 filtri diversi",
    goal_q2_ownfilter:"Crea e pubblica un filtro tuo",
    goal_q3_pages60:"60 pagine questa estate",
    goal_q3_micro3:"3 micro-libri in totale",
    goal_q3_streak21:"Striscia di 21 giorni consecutivi",
    goal_q3_shared2:"Fai parte di 2 libri condivisi",
    goal_q3_ownfilter2:"Crea 2 filtri tuoi",
    goal_q4_pages60:"60 pagine questo autunno",
    goal_q4_thanksgiving:"Scatta il giorno del Ringraziamento",
    goal_q4_christmas:"Scatta il giorno di Natale",
    goal_q4_streak30:"Striscia di 30 giorni consecutivi",
    goal_q4_micro5:"5 micro-libri in totale",
    // ── Nomi ricompense ──────────────────────────────────────────────
    reward_sk_fire:"Fiamma", reward_sk_star:"Stella", reward_sk_heart:"Cuore d'oro",
    reward_sk_sparkle:"Scintilla", reward_sk_snow:"Fiocco di neve", reward_sk_egg:"Uovo di Pasqua",
    reward_sk_turkey:"Tacchino", reward_sk_tree:"Albero di Natale", reward_sk_flower:"Fiore",
    reward_bd_streak7:"Settimana Piena", reward_bd_streak30:"Mese di Ferro",
    reward_bd_pages100:"Cento Pagine", reward_bd_explorer:"Esploratore di Filtri",
    reward_bd_yearfull:"Anno Pieno",
    reward_dv_owl:"Gufo Saggio", reward_dv_plant:"Vaso Fiorito",
    reward_dv_lamp:"Lampada Calda", reward_dv_gold:"Trofeo Dorato",
    reward_sk_family:"Famiglia", reward_sk_camera:"Obiettivo",
    reward_bd_social:"Cerchia Stretta", reward_dv_book:"Libro Aperto",
    // ── Stagioni ed eventi ───────────────────────────────────────────
    season_winter:"Inverno", season_spring:"Primavera", season_summer:"Estate", season_autumn:"Autunno",
    event_easter:"Pasqua", event_thanksgiving_christmas:"Ringraziamento & Natale",
    // ── Impostazioni ──────────────────────────────────────────────────
    set_header_title:"Impostazioni",
    set_avatar_uploading:"Caricamento...",
    set_avatar_hint:"Tocca per cambiare la foto profilo",
    set_cover_change:"Cambia copertina",
    set_cover_add:"Aggiungi una copertina",
    set_sec_account:"Account",
    set_sec_notifications:"Notifiche",
    set_sec_privacy:"Privacy",
    set_sec_data:"I tuoi dati",
    set_sec_customization:"Personalizzazione",
    set_sec_subscription:"Abbonamento",
    set_sec_style:"Stile",
    set_sec_print:"Stampa",
    set_sec_admin:"Amministrazione",
    set_sec_info:"Info",
    set_row_username:"Username",
    set_row_bio:"Bio",
    set_bio_empty:"Nessuna bio impostata",
    set_bio_cancel:"Annulla",
    set_bio_edit:"Modifica",
    set_bio_placeholder:"Scrivi qualcosa su di te...",
    set_bio_save:"Salva",
    set_row_email:"Email",
    set_row_change_password:"Cambia password",
    set_row_daily_notif:"Notifica giornaliera",
    set_daily_notif_sub:"Ogni giorno alle {time}",
    set_row_push_notif:"Notifiche push",
    set_row_email_notif:"Notifiche email",
    set_row_library_public:"Libreria pubblica",
    set_library_public_sub:"Visibile a tutti dopo 1.000 follower",
    set_row_who_message:"Chi può scrivermi",
    set_who_everyone:"Tutti",
    set_who_following:"Solo chi seguo",
    set_row_blocked_users:"Utenti bloccati",
    set_blocked_none:"Nessuno",
    set_soon_badge:"Presto",
    set_row_export:"Esporta i tuoi dati",
    set_export_preparing:"Preparazione in corso...",
    set_export_sub:"Scarica una copia aggiornata di tutto ciò che hai creato",
    set_row_data_location:"Dove vivono i tuoi dati",
    set_data_location_sub:"Sui server di Supabase, protetti e mai venduti a terzi",
    set_row_theme:"Tema libreria",
    set_row_edit_library:"Modifica la tua libreria",
    set_edit_library_sub:"Riordina i libri, posiziona coccarde e divisori",
    set_row_nb_style:"Stile di scrittura Notebook",
    set_row_nb_paper:"Tipo di carta Notebook",
    set_row_plan:"Il tuo piano",
    set_plan_sub:"{plan} · Gestisci o effettua l'upgrade",
    set_row_filters:"Galleria filtri",
    set_filters_sub:"Scopri, crea e fatti scoprire con i tuoi filtri",
    set_row_print:"Stampa i tuoi libri",
    set_print_sub:"Trasforma un anno in un oggetto fisico",
    set_row_moderation:"Contenuti bloccati",
    set_moderation_sub:"Registro delle foto e dei messaggi bloccati dalla moderazione",
    set_row_terms:"Termini e condizioni",
    set_row_privacy_policy:"Privacy Policy",
    set_row_feedback:"Invia un feedback",
    set_row_version:"Versione",
    set_row_logout:"Esci",
    set_row_delete_account:"Elimina account",
    set_logout_confirm:"Uscire dal tuo account Oncer?",
    set_delete_confirm:"Per cancellare definitivamente il tuo account e tutti i tuoi dati dal database, scrivi a hello@oncer.app dall'email con cui ti sei registrato — lo facciamo entro 48 ore, come previsto dal diritto alla cancellazione.\n\nVuoi intanto uscire e cancellare i dati salvati su questo dispositivo?",
    set_time_sheet_title:"Quando ti ricordiamo?",
    set_theme_sheet_title:"🎨 Tema libreria",
    set_theme_locked_hint:"Non hai ancora sbloccato nessun tema aggiuntivo — vai su Scopri, sezione \"Temi\", per trovarli.",
    set_style_sheet_title:"✏️ Stile di scrittura Notebook",
    set_style_sample:"Ciao, come stai?",
    set_paper_sheet_title:"📄 Tipo di carta Notebook",
    set_pw_sheet_title:"🔑 Cambia password",
    set_pw_new_label:"Nuova password",
    set_pw_new_placeholder:"almeno 6 caratteri",
    set_pw_repeat_label:"Ripeti la nuova password",
    set_pw_repeat_placeholder:"ripeti",
    set_pw_err_short:"Almeno 6 caratteri.",
    set_pw_err_mismatch:"Le due password non coincidono.",
    set_pw_save:"Salva la nuova password",
    set_pw_saving:"Salvataggio...",
    set_toast_bio_offline:"Bio salvata solo sul telefono, non online — controlla la connessione.",
    set_toast_pw_updated:"Password aggiornata ✓",
    set_toast_avatar_save_fail:"Non sono riuscito a salvare la foto, riprova.",
    set_toast_avatar_upload_fail:"Non sono riuscito a caricare la foto, riprova.",
    set_toast_cover_save_fail:"Non sono riuscito a salvare la copertina, riprova.",
    set_toast_cover_upload_fail:"Non sono riuscito a caricare la copertina, riprova.",
    // ── Batch 2: Editor scaffale, Legal, Abbonamento, Accesso, Notifiche, Moderazione, Tour, App ──
    shelf_header_title:"🛠 Modifica la tua libreria",
    shelf_instructions:"Trascina i libri per riordinarli. Trascina le coccarde e i divisori ovunque sullo scaffale, anche sui bordi. Qui nulla si apre per sbaglio — puoi modificare con calma.",
    shelf_empty_decor:"Non hai ancora sbloccato coccarde o divisori — si sbloccano completando obiettivi stagionali.",
    shelf_decor_section_title:"Le tue coccarde e divisori",
    shelf_decor_equipped:"Sullo scaffale",
    shelf_decor_tap_to_add:"Tocca per aggiungere",
    sub_header_title:"Abbonamento",
    sub_title:"Scegli il tuo Oncer",
    sub_subtitle:"Più temi, più libri, più modi per non dimenticare.",
    sub_current_badge:"Piano attuale",
    sub_btn_active:"Piano attivo",
    sub_btn_switch_free:"Passa a Free",
    sub_btn_activate:"Attiva {plan}",
    sub_footer_note:"Puoi annullare o cambiare piano in qualsiasi momento.",
    sub_coming_title:"Modalità in arrivo",
    sub_coming_body:"Stiamo preparando i pagamenti — {plan} sarà attivabile a breve. Ti avviseremo appena sarà pronto.",
    sub_coming_ok:"Va bene",
    plan_period_free:"per sempre",
    plan_period_month:"/mese",
    plan_badge_recommended:"Consigliato",
    plan_free_f1:"1 foto al giorno",
    plan_free_f2:"Libro annuale illimitato",
    plan_free_f3:"5 temi base",
    plan_free_f4:"1 micro-libro attivo",
    plan_free_f5:"2 stili di scrittura Notebook",
    plan_free_f6:"2 tipi di carta",
    plan_plus_f1:"Tutti i 20 temi",
    plan_plus_f2:"Micro-libri illimitati",
    plan_plus_f3:"Tutti i 6 stili di scrittura",
    plan_plus_f4:"Tutte le 5 carte del Notebook",
    plan_plus_f5:"Sticker personalizzati",
    plan_plus_f6:"Statistiche annuali",
    plan_plus_f7:"Badge esclusivo sul profilo",
    plan_pro_f1:"Tutto di Oncer+",
    plan_pro_f2:"Export PDF alta qualità illimitato",
    plan_pro_f3:"Sconto 20% su ogni stampa",
    plan_pro_f4:"Accesso anticipato ai temi stagionali",
    plan_pro_f5:"Badge Pro esclusivo",
    plan_pro_f6:"Supporto prioritario",
    notif_header_title:"Notifiche",
    notif_empty_title:"Nessuna notifica",
    notif_empty_body:"Quando succede qualcosa, lo trovi qui.",
    notif_accept:"Accetta",
    notif_decline:"Rifiuta",
    notif_action_failed:"Non ci sono riuscito, riprova.",
    notif_time_now:"adesso",
    notif_time_min:"{n}m fa",
    notif_time_hour:"{n}h fa",
    notif_time_day:"{n}g fa",
    notif_desc_follow_request:"vuole seguirti",
    notif_desc_follow_accepted:"ha accettato la tua richiesta",
    notif_desc_reaction_plain:"ha reagito a una tua pagina",
    notif_desc_reaction_with_emoji:"ha reagito con {emoji} a una tua pagina",
    notif_desc_shared_invite:"ti ha invitato al libro condiviso",
    notif_desc_shared_deleted:"ha eliminato il libro condiviso",
    mod_locked_message:"Questa sezione non è disponibile.",
    mod_header_title:"🛡 Contenuti bloccati",
    mod_empty:"Nessun blocco registrato finora.",
    mod_image_loading:"Caricamento immagine...",
    tour_step1_title:"Il Feed",
    tour_step1_body:"Qui vedi le foto di chi segui, i tuoi micro-libri in cima, e quanto manca a riempire il libro di quest'anno.",
    tour_step2_title:"Scatta",
    tour_step2_body:"Una foto al giorno, per sempre — non si cancella e non si modifica. Se oggi non hai uno scatto, puoi scrivere solo un pensiero, scegliendo anche il carattere.",
    tour_step3_title:"Libreria",
    tour_step3_body:"Ogni giorno diventa una pagina. Qui vedi l'intero libro dell'anno, giorno per giorno, dal primo gennaio — anche i giorni mancanti hanno il loro piccolo disegno.",
    tour_step4_title:"Micro-libri",
    tour_step4_body:"Raccolte più piccole — un viaggio, una vacanza — che si affiancano al libro principale, senza sostituirlo. Puoi crearne uno anche insieme a un amico o a tutta la famiglia.",
    tour_step5_title:"Notebook",
    tour_step5_body:"Scrivi agli amici come in un vero taccuino — scegli la carta e la calligrafia che preferisci per ogni conversazione.",
    tour_step6_title:"Il tuo profilo",
    tour_step6_body:"Qui personalizzi tema, foto e copertina — ed è dove trovi Obiettivi, Ricompense e Oncer Future, i messaggi che scrivi oggi per il tuo domani.",
    tour_skip:"Salta il giro",
    tour_next:"Avanti →",
    tour_finish:"Ho capito, si parte! ✦",
    app_print_soon_title:"Presto disponibile",
    app_print_soon_body_1:"Stiamo preparando la stampa dei tuoi libri.",
    app_print_soon_body_2:"Torna a trovarci presto.",
    app_print_soon_cta:"Torna al Feed",
    app_notif_reminder_body:"Non hai ancora scattato la pagina di oggi 📖",
    app_swipehint_title:"Un consiglio veloce",
    app_swipehint_body1:"Scorri a sinistra o destra con il dito per passare da una sezione all'altra, come sfogliare un libro.",
    app_swipehint_body2:"Dentro una chat o un menu, scorri verso destra per tornare indietro.",
    app_swipehint_cta:"Ho capito",
    auth_pwrecovery_title:"Nuova password",
    auth_pwrecovery_subtitle:"Scegline una nuova per il tuo account Oncer.",
    auth_pw_repeat_label:"Ripeti la password",
    auth_pwrecovery_save:"Salva la nuova password ✦",
    auth_tagline_1:"La tua storia,",
    auth_tagline_2:"una pagina al giorno.",
    auth_welcome_cta:"Crea il tuo Oncer →",
    auth_welcome_have_account:"Ho già un account",
    auth_welcome_terms_notice:"Continuando accetti i Termini e l'Informativa Privacy, consultabili in qualsiasi momento dalle Impostazioni.",
    auth_checkemail_title:"Controlla la tua email",
    auth_checkemail_sent_to:"Ti abbiamo mandato un link di conferma a",
    auth_checkemail_instructions:"Apri quel link — ti riporterà direttamente qui per scegliere il tuo username, il tema e una foto. Un solo passaggio, non due.",
    auth_checkemail_spam_hint:"Non la trovi? Controlla anche nello spam.",
    auth_checkemail_confirmed_link:"Ho già confermato, portami all'accesso →",
    auth_login_eyebrow:"Accedi",
    auth_login_title:"Bentornato.",
    auth_login_id_label:"Email o username",
    auth_login_id_placeholder:"tu@esempio.it oppure username",
    auth_login_username_hint:"Non ricordi lo username? Nessun problema — l'email basta.",
    auth_password_label:"Password",
    auth_login_forgot:"Password dimenticata?",
    auth_login_submit:"Accedi →",
    auth_login_submitting:"Accesso...",
    auth_err_wrong_creds:"Email/username o password non corretti.",
    auth_err_locked_out:"Troppi tentativi. Riprova tra {minutes} minuti.",
    auth_err_captcha_required:"Completa prima la verifica di sicurezza qui sotto.",
    auth_err_email_not_confirmed:"Devi prima confermare la tua email — controlla la posta e apri il link che ti abbiamo mandato.",
    auth_err_no_account_username:"Nessun account con questo username.",
    auth_forgot_eyebrow:"Recupero password",
    auth_forgot_title:"Ti mandiamo un link.",
    auth_forgot_subtitle:"Scrivi l'email con cui ti sei iscritto — ti arriverà un link per scegliere una nuova password.",
    auth_forgot_email_placeholder:"tu@esempio.it",
    auth_forgot_success:"✓ Controlla la tua email — ti abbiamo mandato un link per scegliere una nuova password.",
    auth_forgot_cant_access:"Non riesci ad accedere in nessun modo?",
    auth_forgot_write_us:"Scrivici",
    auth_forgot_submit:"Manda il link →",
    auth_forgot_submitting:"Invio...",
    auth_signup0_eyebrow:"Crea l'accesso",
    auth_signup0_title:"Iniziamo da qui",
    auth_signup0_subtitle:"Ti mandiamo subito un'email di conferma — username, tema e foto li scegli dopo averla aperta, in un solo passaggio.",
    auth_signup0_password_placeholder:"Almeno 6 caratteri",
    auth_err_validation:"Email valida e password di almeno 6 caratteri.",
    auth_continue:"Continua →",
    auth_creating:"Creazione...",
    auth_signup1_eyebrow:"1 di 4 · Email confermata!",
    auth_signup1_title:"Come ti chiami?",
    auth_signup1_subtitle:"Il tuo username sarà visibile agli altri Oncer.",
    auth_signup1_username_placeholder:"es. matte__",
    auth_signup1_min_chars:"Almeno 2 caratteri",
    auth_signup1_checking:"Verifico disponibilità...",
    auth_signup1_available:"✓ Username disponibile",
    auth_signup1_taken:"Questo username è già in uso, scegline un altro.",
    auth_signup2_eyebrow:"2 di 4",
    auth_signup2_title:"Scegli il tuo tema",
    auth_signup2_subtitle:"Definisce la tua libreria. Aggiornabile sempre.",
    auth_signup2_selected:"✓ Selezionato",
    auth_signup3_eyebrow:"3 di 4",
    auth_signup3_title:"Quando scatti di solito?",
    auth_signup3_subtitle:"Ti mandiamo una notifica ogni giorno. Solo una.",
    auth_signup3_submit:"Inizia a scattare ✦",
    auth_signup4_eyebrow:"4 di 4",
    auth_signup4_title:"Metti una foto?",
    auth_signup4_subtitle:"Così chi ti segue ti riconosce subito. Facoltativo — puoi farlo anche più avanti.",
    auth_signup4_saved:"Foto salvata",
    auth_signup4_tap_hint:"Tocca per scegliere una foto",
    auth_signup4_finish_with_photo:"Fatto, si parte ✦",
    auth_signup4_finish_no_photo:"Continua ✦",
    auth_signup4_skip:"oppure procedi senza →",
    auth_err_email_registered:"Questa email è già registrata. Prova ad accedere.",
    auth_err_username_taken:"Username già in uso, scegline un altro.",
    auth_err_session_expired:"Sessione scaduta, riprova ad accedere da capo.",
    auth_err_unexpected:"Errore imprevisto, riprova — se continua a succedere fai una schermata e mandacela.",
    priv_title:"Informativa sulla Privacy",
    priv_last_updated:"Ultimo aggiornamento: Settembre 2026 · Ai sensi del Regolamento (UE) 2016/679 (GDPR)",
    priv_beta_notice:"⚠️ Questa informativa descrive il funzionamento attuale di Oncer, ancora in fase beta. Non sostituisce una consulenza legale: prima del lancio pubblico va rivista da un professionista abilitato, specie in vista dell'attivazione dei pagamenti reali.",
    priv_s1_title:"1. Titolare del trattamento",
    priv_s1_body:"Il titolare del trattamento dei dati è il team di Oncer, raggiungibile all'indirizzo email indicato in fondo a questa pagina.",
    priv_s2_title:"2. Come funziona Oncer",
    priv_s2_pre:"Oncer utilizza ",
    priv_s2_bold:"un vero backend (Supabase)",
    priv_s2_post:" per salvare i tuoi contenuti — foto, didascalie, temi, conversazioni, obiettivi, libri condivisi — così puoi accedere al tuo diario da più dispositivi e le persone che inviti possono vedere i libri condivisi con loro. I dati sono conservati sui server di Supabase, il nostro fornitore di database e autenticazione, protetti da regole di accesso che permettono a ciascun utente di vedere solo i propri dati e quelli condivisi esplicitamente con lui.",
    priv_s3_title:"3. Dati trattati",
    priv_s3_body:"Email e password (gestita in modo sicuro da Supabase Auth, mai visibile a noi in chiaro), username, bio, foto caricate, didascalie, tema grafico scelto, filtri creati, messaggi del Notebook, obiettivi e ricompense sbloccate, contenuti dei libri condivisi. Se ordini un libro stampato: nome, indirizzo di spedizione, città, CAP, paese, telefono ed email usati per la consegna.",
    priv_s4_title:"4. Finalità e base giuridica",
    priv_s4_body:"I dati sono trattati per far funzionare l'app secondo le sue funzionalità (diario fotografico, personalizzazione, gamification, libri condivisi) e, per chi ordina un libro stampato, per eseguire l'ordine (produzione, spedizione, pagamento). La base giuridica è l'esecuzione di un servizio richiesto dall'utente (art. 6.1.b GDPR) e, dove applicabile, l'adempimento di obblighi di legge in materia fiscale/contabile (art. 6.1.c GDPR).",
    priv_s5_title:"5. Servizi di terze parti",
    priv_s5_body:"Per funzionare, Oncer si appoggia a: Supabase (database, autenticazione e archiviazione delle foto); Google Fonts (caratteri tipografici); api.qrserver.com (genera il codice QR per invitare altre persone a un libro condiviso, riceve solo il link di invito). Se ordini un libro stampato, coinvolgiamo anche: Stripe (elabora il pagamento — non riceviamo mai i dati della tua carta, restano interamente a Stripe); Prodigi e Gelato (fornitori di stampa e spedizione, ricevono nome, indirizzo di spedizione e il file del libro per produrlo e spedirlo). Ognuno di questi fornitori tratta i dati secondo la propria informativa privacy.",
    priv_s6_title:"6. Conservazione dei dati",
    priv_s6_body:"I dati del profilo e dei contenuti restano sui nostri server finché non elimini l'account (Impostazioni → Elimina account), che li rimuove in modo permanente. I dati di un ordine di stampa già inviato a Prodigi, Gelato o Stripe restano soggetti alle loro tempistiche di conservazione (es. obblighi fiscali o di garanzia) anche dopo l'eliminazione dell'account.",
    priv_s7_title:"7. I tuoi diritti",
    priv_s7_pre:"In base agli articoli 15-22 del GDPR hai diritto di: accedere ai tuoi dati, correggerli, cancellarli, limitarne il trattamento, richiederne la portabilità e opporti al trattamento. Puoi esercitare accesso e cancellazione direttamente da: ",
    priv_s7_bold1:"Impostazioni → Esporta i tuoi dati",
    priv_s7_mid:" (portabilità) e ",
    priv_s7_bold2:"Impostazioni → Elimina account",
    priv_s7_post:" (cancellazione). Per rettifica, limitazione o opposizione scrivici all'indirizzo in fondo a questa pagina.",
    priv_s8_title:"8. Sicurezza",
    priv_s8_body:"I tuoi dati sono conservati sui server di Supabase e protetti da autenticazione e regole di accesso che isolano i dati di ciascun utente. Le comunicazioni tra l'app e il server sono cifrate (HTTPS/TLS). Ti consigliamo comunque di proteggere il tuo telefono/computer con blocco schermo e di non condividere la tua password.",
    priv_s9_title:"9. Minori",
    priv_s9_body:"Oncer non è destinato a persone sotto i 14 anni. Se hai tra i 14 e i 18 anni, l'uso dell'app richiede il consenso di chi esercita la responsabilità genitoriale.",
    priv_s10_title:"10. Reclami",
    priv_s10_body:"Hai diritto di proporre reclamo al Garante per la Protezione dei Dati Personali ([www.garanteprivacy.it](https://www.garanteprivacy.it)) se ritieni che il trattamento violi il GDPR.",
    priv_s11_title:"11. Modifiche",
    priv_s11_body:"Questa informativa viene aggiornata quando cambia in modo sostanziale il modo in cui trattiamo i tuoi dati — l'ultima revisione riflette il passaggio a un backend reale (Supabase), l'introduzione degli ordini di stampa e il coinvolgimento dei relativi fornitori terzi (Stripe, Prodigi, Gelato).",
    priv_s12_title:"12. Contatti",
    priv_s12_body:"Per qualsiasi richiesta relativa ai tuoi dati: hello@oncer.app",
    terms_title:"Termini e Condizioni",
    terms_last_updated:"Ultimo aggiornamento: Settembre 2026",
    terms_beta_notice:"⚠️ Versione beta a scopo dimostrativo. Prima di un lancio pubblico questi termini vanno formalizzati con un legale, specialmente in vista dell'attivazione dei pagamenti reali per gli abbonamenti.",
    terms_s1_title:"1. Accettazione",
    terms_s1_body:"Utilizzando Oncer accetti questi termini. Se non li condividi, ti chiediamo di non utilizzare l'app.",
    terms_s2_title:"2. Descrizione del servizio",
    terms_s2_body:"Oncer è un'applicazione di diario fotografico sociale: un contenuto al giorno, raccolto in un libro annuale, in micro-libri personali o in libri condivisi con altre persone, con funzionalità social, di personalizzazione e la possibilità di ordinare la stampa fisica dei propri libri.",
    terms_s3_title:"3. Account e contenuti",
    terms_s3_body:"Sei responsabile dei contenuti che pubblichi. Non è consentito caricare contenuti illegali, offensivi, o che violino diritti di terzi.",
    terms_s4_title:"4. Proprietà dei contenuti",
    terms_s4_body:"I contenuti che crei restano tuoi. Oncer non ne rivendica la proprietà.",
    terms_s5_title:"5. Firma Oncer sulle condivisioni",
    terms_s5_body:"Le immagini condivise al di fuori dell'app includono automaticamente e in modo non rimovibile un riferimento al brand Oncer.",
    terms_s6_title:"6. Abbonamenti",
    terms_s6_body:"I piani a pagamento (Oncer+, Oncer Pro) offrono funzionalità aggiuntive descritte nella sezione Abbonamento. Al momento nessun pagamento reale viene elaborato per gli abbonamenti — restano un'anteprima delle funzionalità in arrivo.",
    terms_s7_title:"7. Ordini di stampa",
    terms_s7_body:"Su Oncer puoi ordinare la stampa fisica dei tuoi libri (copertina rigida, morbida o layflat, a seconda dei formati disponibili). Il pagamento è elaborato da Stripe: non riceviamo né conserviamo i dati della tua carta. La produzione e la spedizione sono affidate a fornitori di stampa esterni (Prodigi, Gelato), che ricevono il file del libro e l'indirizzo di spedizione necessari a evadere l'ordine. I tempi di consegna variano in base al fornitore e al paese di destinazione. Una volta inviato in produzione, un ordine non può più essere modificato o annullato dall'app: per errori, ritardi o richieste relative a un ordine specifico scrivici a hello@oncer.app.",
    terms_s8_title:"8. Limitazione di responsabilità",
    terms_s8_body:"Oncer è fornito \"così com'è\", in fase beta. Non garantiamo l'assenza di errori o interruzioni del servizio.",
    terms_s9_title:"9. Modifiche",
    terms_s9_body:"Questi termini possono essere aggiornati; le modifiche rilevanti saranno comunicate in app.",
    terms_s10_title:"10. Contatti",
    terms_s10_body:"hello@oncer.app",
    // ── Batch 3: Library.jsx, SettingsShared.jsx ──────────────────────
    lib_header_title_mine:"📚 Libro {year}",
    lib_header_title_other:"📚 Libro {year} di @{username}",
    lib_recent_pages:"Ultime pagine",
    lib_page_label:"Pag. {day}",
    lib_today_badge:"oggi",
    lib_empty_title_mine:"Il libro {year} è vuoto",
    lib_empty_title_other:"@{username} non ha ancora pagine",
    lib_empty_subtitle:"Scatta la tua prima foto per iniziare.",
    lib_empty_cta:"✦ Scatta ora",
    lib_print_cta:"🖨 Stampa questo libro",
    lib_page_detail_title:"Pagina {day} · {year}",
    lib_share_external:"Condividi fuori da Oncer",
    lib_permanent_notice:"🔒 Questa pagina è permanente e non può essere modificata.",
    shared_claim_cta:"Ritira ✦",
    shared_claimed_label:"✓ Ottenuto",
    goal_unlocked_toast:"🎉 Hai sbloccato un obiettivo! Vai su Obiettivi per riscattare {name}.",
    // ── Batch 4: Print.jsx (schermate stampa, non ancora raggiungibili) ────
    print_preview_header:"Anteprima stampa",
    print_cover_stats:"{total} {pages} · {theme}",
    print_highlights_label:"Qualche momento saliente",
    print_highlights_empty:"Ancora poche pagine, ma già le tue.",
    print_stat_microbook:"Micro-libro",
    print_stat_book:"Libro {year}",
    print_continue_cta:"Continua alla stampa →",
    print_order_header:"Il tuo libro stampato",
    print_format_label:"Formato",
    print_format_cheaper:"più economico",
    print_selected:"✓ selezionato",
    print_empty_pages_label:"Includi anche le pagine vuote",
    print_empty_pages_sub:"Stampa un libro completo di 365 pagine, con quelle senza contenuto lasciate bianche — altrimenti stampiamo solo le pagine con davvero qualcosa dentro.",
    print_feature_cover:"Copertina rigida con il tuo tema in rilievo",
    print_feature_pages:"Ogni pagina è quella che hai scritto tu — anche i giorni vuoti",
    print_feature_delivery:"Consegna in {min}-{max} giorni lavorativi",
    print_feature_memory:"Il ricordo che un feed infinito non ti darà mai",
    print_price_label:"Il tuo {year}, per sempre",
    print_price_fallback:"libro",
    print_price_note:"Spedizione inclusa · Pagamento sicuro",
    print_price_breakdown:"Libro €{book} + spedizione €{shipping}",
    print_order_cta:"Ordina il tuo libro — €{price}",
    print_ordered_body:"Gli ordini veri non sono ancora attivi — stiamo preparando la stampa reale. Il tuo libro resta qui, pronto, per quando sarà il momento.",
    print_pdf_test_label:"Test admin — PDF di stampa reale",
    print_pdf_test_sub:"Genera il PDF pronto per la stampa (bordo di abbondanza e area di sicurezza inclusi) da caricare a mano su un partner di stampa esterno.",
    print_pdf_test_cta:"Genera PDF di stampa",
    print_pdf_test_busy:"Generazione in corso…",
    print_pdf_test_ready:"PDF pronto — tocca per aprirlo/scaricarlo",
    print_pdf_test_error:"Generazione del PDF fallita. Riprova tra poco.",
    print_format_test_badge:"Test Gelato",
    print_price_not_final_note:"Prezzo provvisorio, non ancora valutato per questo formato — solo per il test PDF.",
    print_hub_header:"Cosa vuoi stampare?",
    print_hub_annual_section:"Libro dell'anno",
    print_hub_micro_section:"Micro-libri",
    print_hub_empty_title:"Niente da stampare, per ora",
    print_hub_empty_sub:"Scrivi qualche pagina nel libro dell'anno o in un micro-libro, poi torna qui.",
    print_hub_status_done:"Completo",
    print_copies_label:"Numero di copie",
    print_copies_sub:"Utile se più persone vogliono la propria copia dello stesso libro.",
    print_copies_shared_hint:"Libro condiviso con {n} persone — magari ne vogliono una copia anche loro.",
    print_copies_multiplier_note:"€{unit} × {n} copie",
    // ── Batch 5: wizard formato libro + checkout reale (Gelato/Prodigi) ────
    print_wizard_header:"Scegli il tuo libro",
    print_wizard_step_layflat_q:"Vuoi che alcune foto si aprano su due pagine affiancate, senza interruzione al centro?",
    print_wizard_step_layflat_yes:"Sì, pagine piatte",
    print_wizard_step_layflat_no:"No, va bene normale",
    print_wizard_layflat_note:"Le pagine piatte (layflat) usano una rilegatura speciale — disponibili solo in formato quadrato o orizzontale.",
    print_wizard_step_cover_q:"Copertina",
    print_wizard_cover_soft:"Morbida",
    print_wizard_cover_hard:"Rigida",
    print_wizard_step_size_q:"Formato",
    print_wizard_step_orientation_q:"Orientamento",
    print_wizard_orientation_portrait:"Verticale",
    print_wizard_orientation_landscape:"Orizzontale",
    print_wizard_orientation_square:"Quadrato",
    print_wizard_pages_note:"Questo formato supporta da {min} a {max} pagine",
    print_wizard_pages_warning:"Il tuo libro ha {n} pagine, fuori dal range di questo formato ({min}–{max}). Verrà adattato in stampa.",
    print_wizard_continue:"Continua",
    print_wizard_missing_layflat:"Rispondi alla domanda qui sopra per continuare",
    print_wizard_missing_cover:"Scegli la copertina per continuare",
    print_wizard_missing_size:"Scegli la taglia per continuare",
    print_wizard_missing_orientation:"Scegli l'orientamento per continuare",
    print_photos_per_page_label:"Foto per pagina",
    print_photos_per_page_sub:"Con più foto per pagina il libro ha meno pagine e costa meno — resta comunque il tuo anno intero, solo più compatto.",
    print_photos_per_page_singular:"foto/pagina",
    print_photos_per_page_plural:"foto/pagina",
    print_wizard_vendor_note:"Confrontiamo automaticamente i migliori fornitori di stampa per te — non devi scegliere tu.",
    print_mockup_tap_hint:"Tocca il libro per aprirlo →",
    print_mockup_hard_open_hint:"Copertina rigida: dorso spesso, angoli netti, si apre con resistenza — dura negli anni.",
    print_mockup_soft_open_hint:"Copertina morbida: dorso sottile, angoli arrotondati, più leggera e flessibile in mano.",
    print_mockup_layflat_open_hint:"Pagine piatte: si apre a libro senza piega al centro — una foto può attraversare le due pagine senza interruzioni.",
    print_mockup_reading_hint:"Foto per pagina: la pagina in primo piano si volta per mostrarti il layout scelto.",
    print_mockup_reading_layflat_hint:"Così apparirebbe piatta: senza piega, una foto può attraversare la pagina accanto senza interruzioni.",
    print_wizard_back:"Indietro",
    print_quote_loading:"Calcolo il prezzo reale…",
    print_quote_error:"Non riesco a calcolare il prezzo in questo momento. Riprova tra poco.",
    print_quote_fallback_note:"Prezzo stimato — il preventivo live dal fornitore non è disponibile al momento.",
    print_quote_vendor_note:"Fornitore selezionato: {vendor} · consegna in {min}-{max} giorni",
    print_shipping_header:"Indirizzo di spedizione",
    print_shipping_name:"Nome e cognome",
    print_shipping_address1:"Indirizzo",
    print_shipping_address2:"Interno, scala, ecc. (opzionale)",
    print_shipping_city:"Città",
    print_shipping_postcode:"CAP",
    print_shipping_state:"Provincia (obbligatoria solo per alcuni paesi)",
    print_shipping_country:"Paese (codice a 2 lettere, es. IT)",
    print_shipping_phone:"Telefono",
    print_shipping_email:"Email per la conferma d'ordine",
    print_shipping_save_cta:"Continua al pagamento",
    print_shipping_incomplete:"Compila tutti i campi obbligatori prima di continuare.",
    print_checkout_cta:"Vai al pagamento — €{price}",
    print_checkout_busy:"Preparazione del pagamento…",
    print_checkout_error:"Non riesco ad avviare il pagamento. Riprova tra poco.",
    print_checkout_not_configured:"Il pagamento non è ancora attivo su questa build — serve collegare un account Stripe reale.",
    print_order_pending_title:"Ordine in preparazione",
    print_order_pending_body:"Ti abbiamo inviato un'email di conferma. Ti avviseremo appena l'ordine parte per la stampa.",
    print_order_confirmed_toast:"Pagamento andato a buon fine — controlla la tua email 🎉",
    print_order_cancelled_toast:"Pagamento annullato, nessun addebito effettuato.",
    auth_signup0_birthdate_label:"Data di nascita",
    auth_birthdate_day:"Giorno",
    auth_birthdate_month:"Mese",
    auth_birthdate_year:"Anno",
    auth_err_underage:"Devi avere almeno 18 anni per usare Oncer.",
  },
  en:{
    // ── Feed ──────────────────────────────────────────────────────────
    feed_page_singular:"page",
    feed_page_plural:"pages",
    feed_page_abbr:"p.",
    feed_from_followed_books:"📖 From the books you follow",
    feed_followed_empty_1:"Here you'll see pages from the open micro-books of people you follow.",
    feed_followed_empty_2:"Follow someone, or wait for them to post something.",
    feed_share_external_title:"Share outside Oncer",
    feed_share_watermark_note:"🔖 Every shared image carries the Oncer signature — it can't be removed.",
    feed_share_preparing:"Preparing...",
    feed_share_photo_btn:"↗ Share the photo",
    feed_share_no_native_note:"Your browser can't open the photo share panel — the options below only share a link, without the image.",
    feed_share_or_choose:"Or choose",
    feed_share_save_image:"⬇ Save image",
    feed_share_ig_tiktok_note:"For Instagram and TikTok: save the image, then upload it as a Story or Reel.",
    feed_share_message:"My page on Oncer 🔖 Be a Oncer. — oncer.app",
    feed_native_share_text:"Be a Oncer. 🔖 oncer.app",
    feed_send_page_to:"Send page to...",
    feed_sending:"Sending...",
    feed_follow_to_send:"Follow someone to be able to send pages.",
    feed_share_label:"Share",
    feed_send_label:"Send",
    feed_you:"you",
    feed_following:"following",
    feed_find_your_style:"🎞 Find your style",
    feed_discover_filters:"Discover trending filters or create your own →",
    feed_no_photo_today:"You haven't taken today's shot yet",
    feed_reminder_pre:"You picked",
    feed_reminder_post:"as your reminder time — tap to shoot now",
    feed_year_summary_cta:"Tap to see your year in review →",
    feed_special_moment:"A special moment to shoot today",
    feed_year_ago_today:"✨ One year ago, today",
    feed_toast_share_failed:"I couldn't open sharing, please try again.",
    feed_toast_reaction_remove_failed:"I couldn't remove the reaction, please try again.",
    feed_toast_reaction_save_failed:"The reaction wasn't saved, please try again.",
    feed_toast_send_failed:"I couldn't send it, please try again.",
    // ── Capture ───────────────────────────────────────────────────────
    capture_title:"Capture",
    capture_already_done_title:"You've already taken today's shot for the annual book!",
    capture_already_done_sub1:"Page {day} of {year} is written.",
    capture_already_done_sub2:"Come back tomorrow for the next page of the book.",
    capture_shoot_for_micro:"📎 Shoot anyway for a micro-book",
    capture_add_another_to_event:"📸 Add another photo to this book",
    capture_event_book_note:"Event book · unlimited photos today",
    capture_view_your_book:"View your book",
    capture_tap_to_shoot:"tap to shoot",
    capture_think_twice_1:"🔒 Think it through: this will be the shot chosen for your day.",
    capture_think_twice_2:"It can't be deleted. It stays forever.",
    capture_write_thought_only:"No photo today — just write a thought",
    capture_where_to_save:"where do you want to save this shot?",
    capture_words_only_suffix:"words only, today",
    capture_which_font:"Which font?",
    capture_drag_to_reframe:"drag to reframe",
    capture_filter_label:"🎞 filter",
    capture_browse_all:"Browse all →",
    capture_sticker_label:"🎨 sticker (optional)",
    capture_annual_book_label:"annual book",
    capture_already_done_today:"Already done for today",
    capture_only_micro_note:"this photo only goes into the micro-book chosen below",
    capture_also_diary_label:"Also add it to your personal diary?",
    capture_also_diary_hint:"If on, it will count as today's diary photo and appear in the Feed too.",
    capture_always_in_book:"will always be in your book",
    capture_annual_book_year:"{year} annual book",
    capture_permanent:"permanent",
    capture_choose_micro_required:"choose a micro-book (required)",
    capture_add_to_micro_optional:"also add it to a micro-book? (optional)",
    capture_micro_book_word:"micro-book",
    capture_create_new_micro:"Create new micro-book",
    capture_micro_title_placeholder:"micro-book name, e.g. Trip to Lisbon",
    capture_cancel:"Cancel",
    capture_creating:"Creating...",
    capture_create:"Create",
    capture_add_to_shared_optional:"also add it to a shared book? (optional)",
    capture_with_prefix:"with",
    capture_your_thought_today:"✏️ your thought for today",
    capture_add_a_caption:"add a caption",
    capture_thought_placeholder:"what's on your mind today...",
    capture_caption_placeholder:"what will remain of today...",
    capture_thought_not_removable:"✦ This thought can't be deleted.",
    capture_photo_not_removable:"✦ This photo can't be deleted.",
    capture_toast_micro_create_failed:"I couldn't create the micro-book, please try again.",
    capture_toast_choose_micro_first:"Choose a micro-book before saving.",
    capture_toast_already_today_reload:"You already have a page for today — reload to see it.",
    capture_toast_saved_local_not_uploaded:"Saved on your phone, not uploaded online — check your connection.",
    capture_toast_micro_not_synced:"I couldn't save it — the chosen micro-book isn't synced online yet. Try again shortly.",
    capture_toast_already_today_local_only:"You already have a page for today — this one is saved only on your phone.",
    capture_toast_saved_local_not_synced:"Saved on your phone, but not synced online. Check your connection.",
    capture_toast_shared_publish_failed:"I couldn't publish it to the shared book.",
    capture_toast_shared_already_today:"You already posted your photo for today on this book — try again tomorrow.",
    capture_toast_camera_failed:"I couldn't open the camera — check the app's permissions.",
    capture_saving:"Saving...",
    capture_write_something_first:"Write something before saving",
    capture_choose_micro_before_save:"Choose a micro-book before saving",
    capture_save_in_micro:"Save to the micro-book",
    capture_save_in_page:"Save to page {day}",
    capture_page_published:"Page {day} published!",
    capture_tell_world:"Let the world know you're a Oncer — share this moment outside the app too.",
    capture_share_now:"↗ Share now",
    capture_continue_in_app:"Continue in the app",
    capture_kind_photo:"photo",
    capture_blocked_title:"This {kind} can't be published",
    capture_blocked_desc:"Our automatic check flagged it as possibly violent or strong content. If you think this is a mistake, write to us — we'll review it in person.",
    capture_write_to_us:"Write to us, I think it's a mistake",
    capture_got_it:"Got it",
    capture_blocked_email_subject:"Photo blocked by mistake",
    // ── Navigation (bottom bar) ───────────────────────────────────────
    nav_feed:"feed",
    nav_capture:"capture",
    nav_micro:"micro",
    nav_notebook:"notebook",
    nav_discover:"discover",
    nav_profile:"profile",
    // ── Themes & categories ────────────────────────────────────────────
    theme_neon:"Neon Nights", theme_pastel:"Soft Hours", theme_retro:"90s", theme_clean:"Pure",
    theme_concrete:"Concrete", theme_graffiti:"Graffiti", theme_subway:"Subway",
    theme_vinyl:"Vinyl", theme_rave:"Rave", theme_jazzclub:"Jazz Club",
    theme_noir:"Noir", theme_coldcase:"Cold Case",
    theme_industrial:"Industrial", theme_darkroom:"Darkroom",
    theme_sakura:"Sakura", theme_zen:"Zen", theme_akiba:"Akihabara",
    theme_redgold:"Red & Gold", theme_ink:"Chinese Ink", theme_jade:"Jade Palace",
    theme_aurora:"Aurora", theme_ambra:"Amber",
    cat_originali:"Originals", cat_urban:"Urban", cat_musica:"Music",
    cat_poliziesco:"Detective", cat_underground:"Underground",
    cat_japan:"Japan", cat_chinese:"Chinese", cat_aggiuntivi:"Extra",
    // ── Micro-book presets ───────────────────────────────────────────
    mp_vacation:"Vacation", mp_trip:"Trip", mp_festival:"Festival", mp_wedding:"Wedding",
    mp_birthday:"Birthday", mp_road:"Road Trip", mp_concert:"Concert", mp_custom:"Custom",
    // ── Micro-books ──────────────────────────────────────────────────
    micro_page_singular:"page", micro_page_plural:"pages",
    micro_with_prefix:"with {list}",
    micro_loading_pages:"Loading pages...",
    micro_load_error_title:"Couldn't load the pages",
    micro_no_pages_title:"No pages yet",
    micro_no_pages_sub:"Take the first photo to get started.",
    micro_shoot_now:"✦ Shoot now",
    micro_all_pages:"All pages",
    micro_the_pages:"Pages",
    micro_shoot_today:"📷 Shoot today",
    micro_print_book:"🖨 Print micro-book",
    micro_share_outside:"Share outside Oncer",
    micro_page_locked_note:"🔒 This page is permanent and can't be edited.",
    micro_header_title:"📎 Micro-books",
    micro_new_btn:"+ New",
    micro_invite_msg:"invited you to a shared book",
    micro_accept:"Accept",
    micro_decline:"Decline",
    micro_accept_failed:"Couldn't accept, try again.",
    micro_decline_failed:"Couldn't decline, try again.",
    micro_what_are_title:"What are micro-books?",
    micro_what_are_desc:"Books for specific moments — vacations, trips, parties. No page limit, printable separately.",
    micro_in_progress:"Personal micro-books",
    micro_photos:"photos",
    micro_add_pill:"✦ Add",
    micro_planned_progress:"{pages}/{max} planned photos",
    micro_shared_books:"Shared books",
    micro_pag_short:"pg.",
    micro_archived:"Archived",
    micro_empty_title:"No micro-books yet",
    micro_empty_sub:"Create your first one for a special moment.",
    micro_create_btn:"+ Create micro-book",
    micro_customize_title:"✏️ Customize",
    micro_what_moment:"What's the occasion?",
    micro_title_label:"Title",
    micro_title_placeholder:"e.g. Sardinia summer, Tokyo March…",
    micro_who_writes:"Who's writing it?",
    micro_only_me:"Just me",
    micro_shared_kind:"👥 Shared",
    micro_kind_timed:"📎 Time-limited",
    micro_kind_family:"👨‍👩‍👧‍👦 Family, ongoing",
    micro_kind_evento:"💍 Event",
    micro_event_type_note:"Guests join via QR code/link, no need to already have the app or a Oncer account.",
    micro_event_limit_title:"Photo limit & price",
    micro_event_limit_sub:"Choose how many photos to include: the print price is shown right away, no surprises.",
    micro_event_limit_nolimit:"No limit",
    micro_event_limit_nolimit_sub:"calculated at the end of the event",
    micro_event_limit_price_loading:"Calculating…",
    micro_event_limit_price_unavailable:"Price not available right now",
    micro_event_progress_label:"photos collected",
    micro_event_fairuse_reminder:"The limit is almost reached: remind your guests to leave room for everyone else.",
    micro_closed_badge:"closed",
    micro_event_invite_title:"QR code and link for guests",
    micro_copy_link:"Copy link",
    micro_link_copied:"Copied ✓",
    micro_close_event_btn:"Close the event book",
    micro_closing:"Closing…",
    micro_delete_btn:"Delete micro-book",
    micro_delete_shared_btn:"Delete shared book",
    micro_delete_confirm:"Delete this micro-book? All photos inside it will be removed. This can't be undone.",
    micro_delete_shared_confirm:"Delete this shared book? It will be removed for every participant, who will get a notification. This can't be undone.",
    micro_delete_success:"Micro-book deleted",
    micro_delete_shared_success:"Shared book deleted",
    micro_delete_failed:"Delete failed, try again",
    micro_deleting:"Deleting…",
    micro_close_event_confirm:"Close this book? After this, no one will be able to add photos anymore, and every participant will get an email with the finished book.",
    micro_close_event_success:"Book closed — everyone has been notified by email.",
    micro_close_event_failed:"We couldn't close the book, please try again.",
    micro_who_else:"Who else is in it?",
    micro_search_username:"Search username...",
    micro_cover_theme:"Cover theme",
    micro_creating:"Creating...",
    micro_create_cta:"Create micro-book →",
    micro_toast_need_login:"You need to be signed in to create a shared book.",
    micro_toast_create_failed:"Couldn't create the shared book, try again.",
    micro_toast_local_only:"Created on your phone only, not synced online.",
    // ── Notebook handwriting styles ─────────────────────────────────
    nbs_cursive_name:"Cursive", nbs_cursive_desc:"Elegant and flowing",
    nbs_print_name:"Print", nbs_print_desc:"Clear and tidy",
    nbs_marker_name:"Marker", nbs_marker_desc:"Bold and decisive",
    nbs_romantic_name:"Romantic", nbs_romantic_desc:"Soft and poetic",
    nbs_retro_name:"Typewriter", nbs_retro_desc:"Typewriter style",
    nbs_minimal_name:"Minimal", nbs_minimal_desc:"Simple and personal",
    // ── Notebook paper types ─────────────────────────────────────────
    nbp_ivory_name:"Ivory", nbp_white_name:"White", nbp_kraft_name:"Kraft",
    nbp_night_name:"Night", nbp_sage_name:"Sage",
    // ── Notebook ─────────────────────────────────────────────────────
    nb_time_today:"today",
    nb_writes_in:"writes in {style}",
    nb_page_label:"📄 page {day}",
    nb_placeholder:"write here...",
    nb_your_writing:"✏️ Your handwriting",
    nb_paper_type:"Paper type",
    nb_sample_text:"sample text...",
    nb_sample_greeting:"Hi, how are you today?",
    nb_blocked_title:"This message can't be sent",
    nb_blocked_desc:"Our automatic check flagged it as possible hate speech. If you think this is a mistake, write to us — we'll review it in person.",
    nb_blocked_contact:"Contact us, I think it's a mistake",
    nb_blocked_dismiss:"Got it",
    nb_toast_send_failed:"Message not sent, try again.",
    nb_opening_chat:"Opening the conversation...",
    nb_empty_title:"No conversations yet",
    nb_empty_sub:"Search for a Oncer and start writing.",
    nb_new_conversation:"+ New conversation",
    nb_new_conversation_title:"New conversation",
    nb_no_messages_yet:"No messages yet — write the first one",
    nb_you_prefix:"you: ",
    nb_shared_page_preview:"📄 shared page — pg. {day}",
    nb_search_placeholder:"Search for a Oncer...",
    nb_already_chatting:"already chatting",
    nb_searching:"Searching...",
    nb_no_users_found:"No Oncer found.",
    nb_toast_profile_not_found:"Couldn't find this profile.",
    nb_toast_open_failed:"Couldn't open the conversation.",
    nb_delete_action:"Delete",
    nb_toast_delete_failed:"Couldn't delete the conversation, try again.",
    // ── "Oncer Experience" guide conversation ───────────────────────
    nb_exp_msg1:"Hi, welcome to Oncer 👋 I'm here to show you what you can do, no rush — read whenever you like, this conversation stays here forever.",
    nb_exp_msg2:"The basic idea is simple: one photo a day, forever. It can't be deleted or edited — it's today's page in your yearbook. If one day you'd rather write than photograph, you can post just a thought, choosing the font you like.",
    nb_exp_msg3:"📚 Your Library collects every page, day by day, from January 1st. Even the days you didn't shoot get their own little drawing — no page is ever really empty.",
    nb_exp_msg4:"📎 Micro-books are smaller collections within your world — a trip, a vacation, a project. They sit alongside your main book, not instead of it: you can put the same photo in both at once. You can also create one together with a friend or your whole family, so you all shoot into it together.",
    nb_exp_msg5:"🎯 In Goals you'll find small seasonal challenges — completing them unlocks rewards: badges for your profile, stickers for your photos, and more.",
    nb_exp_msg6:"⏳ Oncer Future is for messages you write today that arrive later — to your future self in a year, to a friend, to your family. They only unlock on the date you choose.",
    nb_exp_msg7:"✏️ The Notebook — this, where we are now — is where you write to friends, like a real notebook: you can choose the paper and handwriting for each conversation, to make it yours.",
    nb_exp_msg8:"🎞 In the Filters screen you'll find the trending ones, but you can also create your own and make it public for the whole community.",
    nb_exp_msg9:"One last thing: your library (the shelf with all your books) can be reordered by dragging the book spines — arrange it however you like.",
    nb_exp_msg10:"🖨 You'll also see a button to print your books — it's coming soon: soon you'll be able to turn a whole year into a real object you can hold.",
    nb_exp_msg11:"That's everything you need to know to get started. You'll discover the rest by shooting — one day at a time. Be a Oncer. 🔖",
    // ── Discover ─────────────────────────────────────────────────────
    disc_header_title:"🔭 Discover",
    disc_your_library:"Your library",
    disc_followers_needed:"{n} more followers to unlock",
    disc_follower_count:"{n} followers",
    disc_to_unlock:"{n} to unlock",
    disc_tab_people:"👤 People",
    disc_tab_libraries:"📖 Libraries",
    disc_tab_themes:"🎨 Themes",
    disc_tab_micro:"📎 Micro-books",
    disc_tab_filters:"🎞 Filters",
    disc_search_placeholder:"Search a Oncer by username...",
    disc_search_hint:"Search a username to find other Oncers and follow them.",
    disc_searching:"Searching...",
    disc_no_people_found:"No Oncer found with this username.",
    disc_loading:"Loading...",
    disc_no_libraries:"No other Oncers to show yet.",
    disc_verified_badge:"📖 Verified",
    disc_follow_btn:"+ Follow",
    disc_libraries_count:"{n} libraries",
    disc_exclusive_title:"✨ Unlockable only here",
    disc_unlocked_hint:"Unlocked — choose it from Settings",
    disc_tap_to_unlock:"Tap to unlock",
    disc_no_micro:"No completed micro-book to show yet.",
    disc_micro_by:"by @{u} · {pages} pages",
    disc_filters_hero_title:"🎞 Find your style",
    disc_filters_hero_desc:"Every filter tells who you are. Browse the gallery, create your own, get discovered by other Oncers.",
    disc_filters_open_gallery:"Open the gallery →",
    disc_filters_trending_title:"🔥 Trending this month",
    disc_filter_uses:"{n} uses",
    // ── Profile ──────────────────────────────────────────────────────
    prof_add_bio:"Add a bio →",
    prof_streak_badge_title:"{n}-day streak of completing all daily goals",
    prof_streak_badge_off_title:"Complete all of today's goals to light the flame",
    prof_loading_profile:"Loading profile...",
    prof_open_library_badge:"📖 Open Library",
    prof_stat_followers:"followers",
    prof_stat_following:"following",
    prof_stat_books:"books",
    prof_following_already:"✓ Already following",
    prof_request_sent:"Request sent",
    prof_goals_label:"Goals",
    prof_annual_books_label:"annual books",
    prof_private_library_title:"Private library",
    prof_private_desc1:"@{username} doesn't have a public library yet.",
    prof_private_desc2:"Follow to see more once they open it.",
    prof_toast_generic_fail:"Couldn't do that, try again shortly.",
    prof_book_meta:"{theme} · {pages} pages",
    prof_in_progress_badge:"IN PROGRESS",
    prof_micro_books_label:"micro-books",
    prof_micro_meta:"📎 micro-book · {pages} pages",
    prof_shared_books_label:"shared books",
    prof_pages_count:"{n} pages",
    // ── Filter names & categories ────────────────────────────────────
    filtname_none:"Original", filtname_golden:"Golden Hour", filtname_noir:"Cinema Noir",
    filtname_dream:"Dreamy", filtname_vivid:"Vivid Pop", filtname_vintage83:"'83 Vintage",
    filtname_moody:"Moody Blue", filtname_clean:"Clean White",
    filtname_c_sofia1:"Petal", filtname_c_luca1:"Afterhours", filtname_c_marco1:"Detective",
    filtname_ft_solstice:"Solstice", filtname_ft_frost:"Frost",
    filtname_os_portra:"Warm Portra", filtname_os_ektar:"Vivid Ektar",
    filtname_os_superia:"Green Superia", filtname_os_velvia:"Velvia Landscape",
    filtname_os_gold:"Gold Nostalgia", filtname_os_polaroid:"Faded Polaroid",
    filtname_os_ektachrome:"Dreamy Ektachrome", filtname_os_agfa:"Agfa Europe",
    filtname_os_trix:"Tri-X Contrast", filtname_os_pro160s:"Soft Pro160S",
    filtcat_setup:"Oncer Setup", filtcat_trend:"Trending", filtcat_vintage:"Vintage",
    filtcat_bn:"B&W", filtcat_vivid:"Vivid", filtcat_pastel:"Pastel",
    filtcat_cinema:"Cinema", filtcat_community:"Community",
    // ── Filters screen ───────────────────────────────────────────────
    filt_header_title:"🎞 Filters",
    filt_create_btn:"+ Create",
    filt_hero_title:"✨ Find your signature style",
    filt_hero_desc:"Every Oncer has a different way of telling their days. Create your own filter and get discovered by the whole community.",
    filt_pick_hint:"Choose a filter to apply to today's shot.",
    filt_search_placeholder:"Search a filter or a creator...",
    filt_cat_all:"All",
    filt_results_count:"Results ({n})",
    filt_all_filters:"All filters",
    filt_by_creator:"by @{creator}",
    filt_use_this:"Use this →",
    filt_seasonal_goal_fallback:"Seasonal goal",
    filt_none_found:"No filter found.",
    filt_create_title:"Create your filter",
    filt_name_label:"Filter name",
    filt_name_placeholder:"e.g. Nostalgia, Sunset Mood...",
    filt_base_label:"Base",
    filt_slider_saturation:"🎨 Saturation",
    filt_slider_contrast:"◐ Contrast",
    filt_slider_brightness:"☀️ Brightness",
    filt_slider_warmth:"🌡️ Warmth",
    filt_warmth_warm:"+{v} warm",
    filt_warmth_cold:"{v} cold",
    filt_warmth_neutral:"neutral",
    filt_advanced_label:"Advanced",
    filt_slider_hue:"🌈 Hue",
    filt_slider_sepia:"📜 Sepia",
    filt_slider_vignette:"🖤 Vignette",
    filt_slider_grain:"🎞 Grain",
    filt_absent:"none",
    filt_toast_local_only:"Created on your phone only, not shared with the community.",
    filt_publishing:"Publishing...",
    filt_publish_btn:"Publish filter",
    filt_published_title:"Filter published!",
    filt_published_desc:"\"{name}\" is now visible to the whole Oncer community. Now it's up to you to get it discovered.",
    filt_view_gallery_btn:"View in gallery",
    // ── Year Close ───────────────────────────────────────────────────
    yc_title:"Your {year} is about to close",
    yc_subtitle:"A year of shots, one day at a time. Here's what you built.",
    yc_stat_pages:"pages written",
    yc_stat_streak:"longest streak",
    yc_stat_top_month:"your most active month",
    yc_stat_filters:"different filters used",
    yc_print_btn:"🖨 Print your {year} book",
    yc_footer_note:"{nextYear} starts on its own, at your next page — no button to press. This year's book stays forever exactly as it is.",
    // ── Oncer Future ─────────────────────────────────────────────────
    of_tab_write:"Write",
    of_tab_received:"Received",
    of_write_intro:"Write something today. You won't read it (nor will whoever you choose) until the date you pick arrives — to your future self, to a friend, or to a family memory.",
    of_recipient_label:"Who is it for?",
    of_recipient_self:"Yourself",
    of_recipient_friend:"A friend",
    of_recipient_family:"Family",
    of_family_placeholder:"Who in the family? (optional, e.g. 'for mom')",
    of_unlock_when_label:"When does it unlock?",
    of_unlock_6m:"In 6 months",
    of_unlock_1y:"In 1 year",
    of_unlock_nextnewyear:"Next January 1st",
    of_unlock_custom:"Choose a date",
    of_content_placeholder:"Write here...",
    of_sending:"Sending...",
    of_send_btn:"Seal and send to the future",
    of_toast_send_failed:"Couldn't send it, try again.",
    of_unlocked_label:"Unlocked",
    of_written_by_you:"✍️ Written by you",
    of_from_user:"💌 From @{username}",
    of_in_transit_label:"On its way to the future",
    of_unlocks_on:"Unlocks on {date}",
    of_empty_state:"You haven't written anything to the future yet.",
    // ── Goals screen ─────────────────────────────────────────────────
    goals_header_title:"🎯 Goals",
    goals_rewards_link:"Rewards →",
    goals_season_banner:"{icon} Season: {name}",
    goals_event_banner:"✨ {icon} It's time for {event}! Special goals available.",
    goals_season_default_banner:"Complete the quarterly goals to unlock themed rewards.",
    goals_section_today:"Today",
    goals_daily_subtitle:"Change every day",
    goals_daily_all_done:"Day complete ✨",
    goals_daily_streak_toast:"🔥 Perfect day! {n}-day streak",
    goals_section_month:"This month",
    goals_section_season:"Seasonal goals",
    goals_footer_note:"New goals and rewards arrive with every season change — winter, spring, summer, autumn — with special editions for Easter, Thanksgiving and Christmas.",
    goals_claimed_title:"You unlocked: {name}",
    goals_claim_sticker:"Available now when you shoot a new page.",
    goals_claim_badge:"Now visible on your profile.",
    goals_claim_divider:"You can equip it on your shelf from the inventory.",
    goals_claim_filter:"New exclusive filter in the gallery.",
    goals_claim_cta:"Awesome!",
    // ── Rewards screen ───────────────────────────────────────────────
    rewards_header_title:"🎁 Your rewards",
    rewards_unlocked_count:"{n} of {total} unlocked",
    rewards_cat_all:"All",
    rewards_cat_sticker:"🎨 Stickers",
    rewards_cat_badge:"🎖️ Badges",
    rewards_cat_divider:"📚 Bookends",
    rewards_cat_filter:"🎞 Filters",
    reward_usage_sticker:"Used when you take a photo",
    reward_usage_badge:"Shown on your profile",
    reward_usage_divider:"Used on your library shelf",
    reward_usage_filter:"Used in the camera",
    rewards_equip_title:"Equip on your shelf (2 slots)",
    rewards_slot_label:"Slot {n}",
    rewards_none_option:"— none —",
    rewards_empty_state:"No rewards here yet — complete the goals to unlock them.",
    rewards_still_locked_label:"Still to unlock",
    // ── Daily/monthly/quarterly goals ─────────────────────────────────
    goal_d_post:"Shoot today's page",
    goal_d_caption:"Write a caption today",
    goal_d_react1:"React to at least 1 page",
    goal_d_react2:"React to 2 friends' pages",
    goal_d_react3:"React to 3 friends' pages",
    goal_d_filter:"Use a filter today",
    goal_d_sticker:"Add a sticker today",
    goal_d_caption_long:"Write a longer caption (20+ characters)",
    goal_d_combo:"Complete the page with a caption and a sticker",
    goal_d_early:"Post your page before noon",
    goal_m_streak7:"7-day streak",
    goal_m_pages15:"15 pages this month",
    goal_m_filters3:"Use 3 different filters",
    goal_q1_pages60:"60 pages this winter",
    goal_q1_easter:"Shoot during Easter week",
    goal_q1_streak14:"14-day streak",
    goal_q1_shared:"Be part of a shared book",
    goal_q1_sticker:"Use a sticker on a page",
    goal_q2_pages60:"60 pages this spring",
    goal_q2_micro:"Create a micro-book",
    goal_q2_streak14:"14-day streak",
    goal_q2_filters5:"Use 5 different filters",
    goal_q2_ownfilter:"Create and publish your own filter",
    goal_q3_pages60:"60 pages this summer",
    goal_q3_micro3:"3 micro-books total",
    goal_q3_streak21:"21-day streak",
    goal_q3_shared2:"Be part of 2 shared books",
    goal_q3_ownfilter2:"Create 2 of your own filters",
    goal_q4_pages60:"60 pages this autumn",
    goal_q4_thanksgiving:"Shoot on Thanksgiving day",
    goal_q4_christmas:"Shoot on Christmas day",
    goal_q4_streak30:"30-day streak",
    goal_q4_micro5:"5 micro-books total",
    // ── Reward names ─────────────────────────────────────────────────
    reward_sk_fire:"Flame", reward_sk_star:"Star", reward_sk_heart:"Golden Heart",
    reward_sk_sparkle:"Sparkle", reward_sk_snow:"Snowflake", reward_sk_egg:"Easter Egg",
    reward_sk_turkey:"Turkey", reward_sk_tree:"Christmas Tree", reward_sk_flower:"Flower",
    reward_bd_streak7:"Full Week", reward_bd_streak30:"Iron Month",
    reward_bd_pages100:"Hundred Pages", reward_bd_explorer:"Filter Explorer",
    reward_bd_yearfull:"Full Year",
    reward_dv_owl:"Wise Owl", reward_dv_plant:"Blooming Pot",
    reward_dv_lamp:"Warm Lamp", reward_dv_gold:"Golden Trophy",
    reward_sk_family:"Family", reward_sk_camera:"Lens",
    reward_bd_social:"Close Circle", reward_dv_book:"Open Book",
    // ── Seasons & events ─────────────────────────────────────────────
    season_winter:"Winter", season_spring:"Spring", season_summer:"Summer", season_autumn:"Autumn",
    event_easter:"Easter", event_thanksgiving_christmas:"Thanksgiving & Christmas",
    // ── Impostazioni ──────────────────────────────────────────────────
    set_header_title:"Settings",
    set_avatar_uploading:"Uploading...",
    set_avatar_hint:"Tap to change your profile photo",
    set_cover_change:"Change cover",
    set_cover_add:"Add a cover",
    set_sec_account:"Account",
    set_sec_notifications:"Notifications",
    set_sec_privacy:"Privacy",
    set_sec_data:"Your data",
    set_sec_customization:"Customization",
    set_sec_subscription:"Subscription",
    set_sec_style:"Style",
    set_sec_print:"Print",
    set_sec_admin:"Administration",
    set_sec_info:"Info",
    set_row_username:"Username",
    set_row_bio:"Bio",
    set_bio_empty:"No bio set",
    set_bio_cancel:"Cancel",
    set_bio_edit:"Edit",
    set_bio_placeholder:"Write something about yourself...",
    set_bio_save:"Save",
    set_row_email:"Email",
    set_row_change_password:"Change password",
    set_row_daily_notif:"Daily reminder",
    set_daily_notif_sub:"Every day at {time}",
    set_row_push_notif:"Push notifications",
    set_row_email_notif:"Email notifications",
    set_row_library_public:"Public library",
    set_library_public_sub:"Visible to everyone after 1,000 followers",
    set_row_who_message:"Who can message me",
    set_who_everyone:"Everyone",
    set_who_following:"Only people I follow",
    set_row_blocked_users:"Blocked users",
    set_blocked_none:"None",
    set_soon_badge:"Coming soon",
    set_row_export:"Export your data",
    set_export_preparing:"Preparing...",
    set_export_sub:"Download an up-to-date copy of everything you've created",
    set_row_data_location:"Where your data lives",
    set_data_location_sub:"On Supabase's servers, protected and never sold to third parties",
    set_row_theme:"Library theme",
    set_row_edit_library:"Edit your library",
    set_edit_library_sub:"Reorder books, place badges and dividers",
    set_row_nb_style:"Notebook writing style",
    set_row_nb_paper:"Notebook paper type",
    set_row_plan:"Your plan",
    set_plan_sub:"{plan} · Manage or upgrade",
    set_row_filters:"Filter gallery",
    set_filters_sub:"Discover, create, and get discovered with your filters",
    set_row_print:"Print your books",
    set_print_sub:"Turn a year into a physical object",
    set_row_moderation:"Blocked content",
    set_moderation_sub:"Log of photos and messages blocked by moderation",
    set_row_terms:"Terms and conditions",
    set_row_privacy_policy:"Privacy Policy",
    set_row_feedback:"Send feedback",
    set_row_version:"Version",
    set_row_logout:"Log out",
    set_row_delete_account:"Delete account",
    set_logout_confirm:"Log out of your Oncer account?",
    set_delete_confirm:"To permanently delete your account and all your data from the database, email hello@oncer.app from the address you signed up with — we'll take care of it within 48 hours, as required by the right to erasure.\n\nWould you like to log out and clear the data saved on this device in the meantime?",
    set_time_sheet_title:"When should we remind you?",
    set_theme_sheet_title:"🎨 Library theme",
    set_theme_locked_hint:"You haven't unlocked any extra themes yet — check the \"Themes\" section under Discover to find them.",
    set_style_sheet_title:"✏️ Notebook writing style",
    set_style_sample:"Hey, how are you?",
    set_paper_sheet_title:"📄 Notebook paper type",
    set_pw_sheet_title:"🔑 Change password",
    set_pw_new_label:"New password",
    set_pw_new_placeholder:"at least 6 characters",
    set_pw_repeat_label:"Repeat new password",
    set_pw_repeat_placeholder:"repeat",
    set_pw_err_short:"At least 6 characters.",
    set_pw_err_mismatch:"The two passwords don't match.",
    set_pw_save:"Save new password",
    set_pw_saving:"Saving...",
    set_toast_bio_offline:"Bio saved on your phone only, not online — check your connection.",
    set_toast_pw_updated:"Password updated ✓",
    set_toast_avatar_save_fail:"Couldn't save your photo, try again.",
    set_toast_avatar_upload_fail:"Couldn't upload your photo, try again.",
    set_toast_cover_save_fail:"Couldn't save your cover, try again.",
    set_toast_cover_upload_fail:"Couldn't upload your cover, try again.",
    // ── Batch 2: Editor scaffale, Legal, Abbonamento, Accesso, Notifiche, Moderazione, Tour, App ──
    shelf_header_title:"🛠 Edit your library",
    shelf_instructions:"Drag books to reorder them. Drag badges and dividers anywhere on the shelf, even to the edges. Nothing here opens by accident — take your time.",
    shelf_empty_decor:"You haven't unlocked any badges or dividers yet — they unlock by completing seasonal goals.",
    shelf_decor_section_title:"Your badges and dividers",
    shelf_decor_equipped:"On the shelf",
    shelf_decor_tap_to_add:"Tap to add",
    sub_header_title:"Subscription",
    sub_title:"Choose your Oncer",
    sub_subtitle:"More themes, more books, more ways to never forget.",
    sub_current_badge:"Current plan",
    sub_btn_active:"Active plan",
    sub_btn_switch_free:"Switch to Free",
    sub_btn_activate:"Activate {plan}",
    sub_footer_note:"You can cancel or change plan at any time.",
    sub_coming_title:"Coming soon",
    sub_coming_body:"We're setting up payments — {plan} will be available soon. We'll let you know as soon as it's ready.",
    sub_coming_ok:"Got it",
    plan_period_free:"forever",
    plan_period_month:"/month",
    plan_badge_recommended:"Recommended",
    plan_free_f1:"1 photo a day",
    plan_free_f2:"Unlimited annual book",
    plan_free_f3:"5 base themes",
    plan_free_f4:"1 active micro-book",
    plan_free_f5:"2 Notebook writing styles",
    plan_free_f6:"2 paper types",
    plan_plus_f1:"All 20 themes",
    plan_plus_f2:"Unlimited micro-books",
    plan_plus_f3:"All 6 writing styles",
    plan_plus_f4:"All 5 Notebook papers",
    plan_plus_f5:"Custom stickers",
    plan_plus_f6:"Annual stats",
    plan_plus_f7:"Exclusive profile badge",
    plan_pro_f1:"Everything in Oncer+",
    plan_pro_f2:"Unlimited high-quality PDF export",
    plan_pro_f3:"20% off every print",
    plan_pro_f4:"Early access to seasonal themes",
    plan_pro_f5:"Exclusive Pro badge",
    plan_pro_f6:"Priority support",
    notif_header_title:"Notifications",
    notif_empty_title:"No notifications",
    notif_empty_body:"When something happens, you'll find it here.",
    notif_accept:"Accept",
    notif_decline:"Decline",
    notif_action_failed:"Couldn't do that, try again.",
    notif_time_now:"just now",
    notif_time_min:"{n}m ago",
    notif_time_hour:"{n}h ago",
    notif_time_day:"{n}d ago",
    notif_desc_follow_request:"wants to follow you",
    notif_desc_follow_accepted:"accepted your request",
    notif_desc_reaction_plain:"reacted to one of your pages",
    notif_desc_reaction_with_emoji:"reacted with {emoji} to one of your pages",
    notif_desc_shared_invite:"invited you to the shared book",
    notif_desc_shared_deleted:"deleted the shared book",
    mod_locked_message:"This section isn't available.",
    mod_header_title:"🛡 Blocked content",
    mod_empty:"No blocks recorded so far.",
    mod_image_loading:"Loading image...",
    tour_step1_title:"The Feed",
    tour_step1_body:"Here you see photos from people you follow, your micro-books at the top, and how much is left to fill this year's book.",
    tour_step2_title:"Capture",
    tour_step2_body:"One photo a day, forever — it can't be deleted or edited. If you don't have a photo today, you can just write a thought, choosing your favorite font.",
    tour_step3_title:"Library",
    tour_step3_body:"Every day becomes a page. Here you see the whole year's book, day by day, from January 1st — even missing days get their own little sketch.",
    tour_step4_title:"Micro-books",
    tour_step4_body:"Smaller collections — a trip, a vacation — that sit alongside your main book without replacing it. You can even create one together with a friend or your whole family.",
    tour_step5_title:"Notebook",
    tour_step5_body:"Write to friends like in a real notebook — choose the paper and handwriting you like best for each conversation.",
    tour_step6_title:"Your profile",
    tour_step6_body:"This is where you customize your theme, photo, and cover — and where you'll find Goals, Rewards, and Oncer Future, the messages you write today for your future self.",
    tour_skip:"Skip the tour",
    tour_next:"Next →",
    tour_finish:"Got it, let's go! ✦",
    app_print_soon_title:"Coming soon",
    app_print_soon_body_1:"We're getting book printing ready.",
    app_print_soon_body_2:"Check back soon.",
    app_print_soon_cta:"Back to Feed",
    app_notif_reminder_body:"You haven't taken today's page yet 📖",
    app_swipehint_title:"A quick tip",
    app_swipehint_body1:"Swipe left or right with your finger to move between sections, like flipping through a book.",
    app_swipehint_body2:"Inside a chat or a menu, swipe right to go back.",
    app_swipehint_cta:"Got it",
    auth_pwrecovery_title:"New password",
    auth_pwrecovery_subtitle:"Choose a new one for your Oncer account.",
    auth_pw_repeat_label:"Repeat password",
    auth_pwrecovery_save:"Save new password ✦",
    auth_tagline_1:"Your story,",
    auth_tagline_2:"one page a day.",
    auth_welcome_cta:"Create your Oncer →",
    auth_welcome_have_account:"I already have an account",
    auth_welcome_terms_notice:"By continuing you accept the Terms and Privacy Policy, always available from Settings.",
    auth_checkemail_title:"Check your email",
    auth_checkemail_sent_to:"We sent a confirmation link to",
    auth_checkemail_instructions:"Open that link — it'll bring you straight back here to choose your username, theme, and a photo. One step, not two.",
    auth_checkemail_spam_hint:"Can't find it? Check your spam folder too.",
    auth_checkemail_confirmed_link:"I already confirmed, take me to login →",
    auth_login_eyebrow:"Log in",
    auth_login_title:"Welcome back.",
    auth_login_id_label:"Email or username",
    auth_login_id_placeholder:"you@example.com or username",
    auth_login_username_hint:"Can't remember your username? No problem — your email works too.",
    auth_password_label:"Password",
    auth_login_forgot:"Forgot password?",
    auth_login_submit:"Log in →",
    auth_login_submitting:"Logging in...",
    auth_err_wrong_creds:"Incorrect email/username or password.",
    auth_err_locked_out:"Too many attempts. Try again in {minutes} minutes.",
    auth_err_captcha_required:"Please complete the security check below first.",
    auth_err_email_not_confirmed:"You need to confirm your email first — check your inbox and open the link we sent you.",
    auth_err_no_account_username:"No account found with this username.",
    auth_forgot_eyebrow:"Password recovery",
    auth_forgot_title:"We'll send you a link.",
    auth_forgot_subtitle:"Enter the email you signed up with — you'll get a link to choose a new password.",
    auth_forgot_email_placeholder:"you@example.com",
    auth_forgot_success:"✓ Check your email — we sent you a link to choose a new password.",
    auth_forgot_cant_access:"Can't get in any other way?",
    auth_forgot_write_us:"Contact us",
    auth_forgot_submit:"Send the link →",
    auth_forgot_submitting:"Sending...",
    auth_signup0_eyebrow:"Create your login",
    auth_signup0_title:"Let's start here",
    auth_signup0_subtitle:"We'll send you a confirmation email right away — you'll choose your username, theme, and photo after opening it, in a single step.",
    auth_signup0_password_placeholder:"At least 6 characters",
    auth_err_validation:"A valid email and a password of at least 6 characters.",
    auth_continue:"Continue →",
    auth_creating:"Creating...",
    auth_signup1_eyebrow:"1 of 4 · Email confirmed!",
    auth_signup1_title:"What's your name?",
    auth_signup1_subtitle:"Your username will be visible to other Oncers.",
    auth_signup1_username_placeholder:"e.g. matte__",
    auth_signup1_min_chars:"At least 2 characters",
    auth_signup1_checking:"Checking availability...",
    auth_signup1_available:"✓ Username available",
    auth_signup1_taken:"This username is already taken, pick another one.",
    auth_signup2_eyebrow:"2 of 4",
    auth_signup2_title:"Choose your theme",
    auth_signup2_subtitle:"It defines your library. You can always change it later.",
    auth_signup2_selected:"✓ Selected",
    auth_signup3_eyebrow:"3 of 4",
    auth_signup3_title:"When do you usually take your photo?",
    auth_signup3_subtitle:"We'll send you one notification a day. Just one.",
    auth_signup3_submit:"Start capturing ✦",
    auth_signup4_eyebrow:"4 of 4",
    auth_signup4_title:"Add a photo?",
    auth_signup4_subtitle:"So the people who follow you recognize you right away. Optional — you can do it later too.",
    auth_signup4_saved:"Photo saved",
    auth_signup4_tap_hint:"Tap to choose a photo",
    auth_signup4_finish_with_photo:"Done, let's go ✦",
    auth_signup4_finish_no_photo:"Continue ✦",
    auth_signup4_skip:"or continue without →",
    auth_err_email_registered:"This email is already registered. Try logging in instead.",
    auth_err_username_taken:"Username already taken, pick another one.",
    auth_err_session_expired:"Session expired, try logging in again.",
    auth_err_unexpected:"Unexpected error, please retry — if it keeps happening, send us a screenshot.",
    priv_title:"Privacy Policy",
    priv_last_updated:"Last updated: September 2026 · Under Regulation (EU) 2016/679 (GDPR)",
    priv_beta_notice:"⚠️ This policy describes how Oncer currently works, while still in beta. It doesn't replace legal advice: it needs to be reviewed by a qualified professional before public launch, especially ahead of enabling real payments.",
    priv_s1_title:"1. Data controller",
    priv_s1_body:"The data controller is the Oncer team, reachable at the email address listed at the bottom of this page.",
    priv_s2_title:"2. How Oncer works",
    priv_s2_pre:"Oncer uses ",
    priv_s2_bold:"a real backend (Supabase)",
    priv_s2_post:" to store your content — photos, captions, themes, conversations, goals, shared books — so you can access your journal from more than one device and people you invite can see books shared with them. Data is stored on Supabase's servers, our database and authentication provider, protected by access rules that let each user see only their own data and whatever has been explicitly shared with them.",
    priv_s3_title:"3. Data processed",
    priv_s3_body:"Email and password (securely handled by Supabase Auth, never visible to us in plain text), username, bio, uploaded photos, captions, chosen theme, filters you've created, Notebook messages, goals and unlocked rewards, shared book content. If you order a printed book: name, shipping address, city, postal code, country, phone number and email used for delivery.",
    priv_s4_title:"4. Purpose and legal basis",
    priv_s4_body:"Data is processed to make the app work as intended (photo journal, customization, gamification, shared books) and, for anyone ordering a printed book, to fulfill the order (production, shipping, payment). The legal basis is the performance of a service requested by the user (GDPR art. 6.1.b) and, where applicable, compliance with legal obligations such as tax/accounting rules (GDPR art. 6.1.c).",
    priv_s5_title:"5. Third-party services",
    priv_s5_body:"To function, Oncer relies on: Supabase (database, authentication, and photo storage); Google Fonts (typefaces); api.qrserver.com (generates the QR code used to invite people to a shared book, receiving only the invite link). If you order a printed book, we also involve: Stripe (processes payment — we never receive your card details, which stay entirely with Stripe); Prodigi and Gelato (print and shipping providers, who receive your name, shipping address, and the book file needed to produce and ship the order). Each of these providers processes data according to its own privacy policy.",
    priv_s6_title:"6. Data retention",
    priv_s6_body:"Your profile and content data stay on our servers until you delete your account (Settings → Delete account), which permanently removes them. Data from a print order already sent to Prodigi, Gelato, or Stripe remains subject to their own retention periods (e.g. tax or warranty obligations) even after your account is deleted.",
    priv_s7_title:"7. Your rights",
    priv_s7_pre:"Under articles 15-22 of the GDPR you have the right to: access your data, correct it, delete it, restrict its processing, request its portability, and object to processing. You can exercise access and deletion directly from: ",
    priv_s7_bold1:"Settings → Export your data",
    priv_s7_mid:" (portability) and ",
    priv_s7_bold2:"Settings → Delete account",
    priv_s7_post:" (deletion). For correction, restriction, or objection requests, write to us at the address at the bottom of this page.",
    priv_s8_title:"8. Security",
    priv_s8_body:"Your data is stored on Supabase's servers and protected by authentication and access rules that keep each user's data isolated. Communication between the app and the server is encrypted (HTTPS/TLS). We still recommend protecting your phone/computer with a screen lock and never sharing your password.",
    priv_s9_title:"9. Minors",
    priv_s9_body:"Oncer isn't intended for people under 14. If you're between 14 and 18, using the app requires the consent of whoever holds parental responsibility for you.",
    priv_s10_title:"10. Complaints",
    priv_s10_body:"You have the right to lodge a complaint with the Italian Data Protection Authority (Garante per la Protezione dei Dati Personali, [www.garanteprivacy.it](https://www.garanteprivacy.it)), or with your own country's data protection authority, if you believe processing violates the GDPR.",
    priv_s11_title:"11. Changes",
    priv_s11_body:"This policy is updated whenever the way we process your data changes substantially — the latest revision reflects the move to a real backend (Supabase), the introduction of print orders, and the involvement of the related third-party providers (Stripe, Prodigi, Gelato).",
    priv_s12_title:"12. Contact",
    priv_s12_body:"For any request regarding your data: hello@oncer.app",
    terms_title:"Terms and Conditions",
    terms_last_updated:"Last updated: September 2026",
    terms_beta_notice:"⚠️ Beta version for demonstration purposes. Before any public launch these terms need to be formalized with a lawyer, especially ahead of enabling real payments for subscriptions.",
    terms_s1_title:"1. Acceptance",
    terms_s1_body:"By using Oncer you accept these terms. If you don't agree with them, please don't use the app.",
    terms_s2_title:"2. Service description",
    terms_s2_body:"Oncer is a social photo journal app: one piece of content a day, collected into an annual book, into personal micro-books, or into books shared with other people, with social and customization features and the option to order a physical printed copy of your books.",
    terms_s3_title:"3. Accounts and content",
    terms_s3_body:"You're responsible for the content you post. You may not upload illegal or offensive content, or content that infringes on third-party rights.",
    terms_s4_title:"4. Content ownership",
    terms_s4_body:"The content you create remains yours. Oncer doesn't claim ownership of it.",
    terms_s5_title:"5. Oncer watermark on shares",
    terms_s5_body:"Images shared outside the app automatically and permanently include a reference to the Oncer brand.",
    terms_s6_title:"6. Subscriptions",
    terms_s6_body:"Paid plans (Oncer+, Oncer Pro) offer additional features described in the Subscription section. No real payment is currently processed for subscriptions — they remain a preview of upcoming features.",
    terms_s7_title:"7. Print orders",
    terms_s7_body:"On Oncer you can order a physical printed copy of your books (hardcover, softcover, or layflat, depending on the formats available). Payment is processed by Stripe: we never receive or store your card details. Production and shipping are handled by external print partners (Prodigi, Gelato), who receive the book file and shipping address needed to fulfill the order. Delivery times vary by provider and destination country. Once an order has been sent to production, it can no longer be changed or cancelled from the app: for errors, delays, or requests about a specific order, write to us at hello@oncer.app.",
    terms_s8_title:"8. Limitation of liability",
    terms_s8_body:"Oncer is provided \"as is\", in beta. We don't guarantee it's free of errors or service interruptions.",
    terms_s9_title:"9. Changes",
    terms_s9_body:"These terms may be updated; significant changes will be communicated in the app.",
    terms_s10_title:"10. Contact",
    terms_s10_body:"hello@oncer.app",
    // ── Batch 3: Library.jsx, SettingsShared.jsx ──────────────────────
    lib_header_title_mine:"📚 {year} Book",
    lib_header_title_other:"📚 {year} Book by @{username}",
    lib_recent_pages:"Recent pages",
    lib_page_label:"Page {day}",
    lib_today_badge:"today",
    lib_empty_title_mine:"Your {year} book is empty",
    lib_empty_title_other:"@{username} doesn't have any pages yet",
    lib_empty_subtitle:"Take your first photo to get started.",
    lib_empty_cta:"✦ Capture now",
    lib_print_cta:"🖨 Print this book",
    lib_page_detail_title:"Page {day} · {year}",
    lib_share_external:"Share outside Oncer",
    lib_permanent_notice:"🔒 This page is permanent and can't be edited.",
    shared_claim_cta:"Claim ✦",
    shared_claimed_label:"✓ Claimed",
    goal_unlocked_toast:"🎉 You unlocked a goal! Go to Goals to claim {name}.",
    // ── Batch 4: Print.jsx (schermate stampa, non ancora raggiungibili) ────
    print_preview_header:"Print preview",
    print_cover_stats:"{total} {pages} · {theme}",
    print_highlights_label:"A few highlights",
    print_highlights_empty:"Still just a few pages, but already yours.",
    print_stat_microbook:"Micro-book",
    print_stat_book:"{year} Book",
    print_continue_cta:"Continue to print →",
    print_order_header:"Your printed book",
    print_format_label:"Format",
    print_format_cheaper:"cheaper",
    print_selected:"✓ selected",
    print_empty_pages_label:"Include empty pages too",
    print_empty_pages_sub:"Print a full 365-page book, with content-free days left blank — otherwise we only print pages that actually have something in them.",
    print_feature_cover:"Hardcover with your theme embossed",
    print_feature_pages:"Every page is the one you wrote — even the empty days",
    print_feature_delivery:"Delivery in {min}-{max} business days",
    print_feature_memory:"The memory an endless feed will never give you",
    print_price_label:"Your {year}, forever",
    print_price_fallback:"book",
    print_price_note:"Shipping included · Secure payment",
    print_price_breakdown:"Book €{book} + shipping €{shipping}",
    print_order_cta:"Order your book — €{price}",
    print_ordered_body:"Real orders aren't live yet — we're getting actual printing ready. Your book stays here, ready, for when the time comes.",
    print_pdf_test_label:"Admin test — real print PDF",
    print_pdf_test_sub:"Generates the print-ready PDF (bleed and safe area included) to upload manually to an outside print partner.",
    print_pdf_test_cta:"Generate print PDF",
    print_pdf_test_busy:"Generating…",
    print_pdf_test_ready:"PDF ready — tap to open/download",
    print_pdf_test_error:"PDF generation failed. Try again shortly.",
    print_format_test_badge:"Gelato test",
    print_price_not_final_note:"Provisional price, not yet evaluated for this format — PDF test only.",
    print_hub_header:"What do you want to print?",
    print_hub_annual_section:"Yearly book",
    print_hub_micro_section:"Micro-books",
    print_hub_empty_title:"Nothing to print yet",
    print_hub_empty_sub:"Write a few pages in your yearly book or a micro-book, then come back here.",
    print_hub_status_done:"Complete",
    print_copies_label:"Number of copies",
    print_copies_sub:"Useful if more than one person wants their own copy of the same book.",
    print_copies_shared_hint:"Shared with {n} people — they might want a copy too.",
    print_copies_multiplier_note:"€{unit} × {n} copies",
    print_wizard_header:"Choose your book",
    print_wizard_step_layflat_q:"Do you want some photos to open across two facing pages, with no gap in the middle?",
    print_wizard_step_layflat_yes:"Yes, flat pages",
    print_wizard_step_layflat_no:"No, regular is fine",
    print_wizard_layflat_note:"Flat (layflat) pages use a special binding — only available in square or landscape formats.",
    print_wizard_step_cover_q:"Cover",
    print_wizard_cover_soft:"Softcover",
    print_wizard_cover_hard:"Hardcover",
    print_wizard_step_size_q:"Size",
    print_wizard_step_orientation_q:"Orientation",
    print_wizard_orientation_portrait:"Portrait",
    print_wizard_orientation_landscape:"Landscape",
    print_wizard_orientation_square:"Square",
    print_wizard_pages_note:"This format supports {min} to {max} pages",
    print_wizard_pages_warning:"Your book has {n} pages, outside this format's range ({min}–{max}). It will be adjusted for print.",
    print_wizard_continue:"Continue",
    print_wizard_missing_layflat:"Answer the question above to continue",
    print_wizard_missing_cover:"Choose a cover to continue",
    print_wizard_missing_size:"Choose a size to continue",
    print_wizard_missing_orientation:"Choose an orientation to continue",
    print_photos_per_page_label:"Photos per page",
    print_photos_per_page_sub:"More photos per page means fewer pages and a lower price — it's still your whole year, just more compact.",
    print_photos_per_page_singular:"photo/page",
    print_photos_per_page_plural:"photos/page",
    print_wizard_vendor_note:"We automatically compare the best print partners for you — no need to choose one yourself.",
    print_mockup_tap_hint:"Tap the book to open it →",
    print_mockup_hard_open_hint:"Hardcover: thick spine, sharp edges, opens with a bit of resistance — built to last for years.",
    print_mockup_soft_open_hint:"Softcover: thin spine, rounded corners, lighter and more flexible in hand.",
    print_mockup_layflat_open_hint:"Flat pages: opens fully flat with no crease down the middle — a photo can flow across both pages seamlessly.",
    print_mockup_reading_hint:"Photos per page: the front page turns to show you the chosen layout.",
    print_mockup_reading_layflat_hint:"This is how it would look flat: no crease, a photo can flow onto the next page seamlessly.",
    print_wizard_back:"Back",
    print_quote_loading:"Calculating the real price…",
    print_quote_error:"Can't calculate the price right now. Try again shortly.",
    print_quote_fallback_note:"Estimated price — live quote from the print partner isn't available right now.",
    print_quote_vendor_note:"Selected partner: {vendor} · delivery in {min}-{max} days",
    print_shipping_header:"Shipping address",
    print_shipping_name:"Full name",
    print_shipping_address1:"Address",
    print_shipping_address2:"Apartment, floor, etc. (optional)",
    print_shipping_city:"City",
    print_shipping_postcode:"Postal code",
    print_shipping_state:"State/province (required for some countries)",
    print_shipping_country:"Country (2-letter code, e.g. US)",
    print_shipping_phone:"Phone",
    print_shipping_email:"Email for order confirmation",
    print_shipping_save_cta:"Continue to payment",
    print_shipping_incomplete:"Fill in all required fields before continuing.",
    print_checkout_cta:"Go to payment — €{price}",
    print_checkout_busy:"Preparing payment…",
    print_checkout_error:"Can't start payment right now. Try again shortly.",
    print_checkout_not_configured:"Payment isn't active on this build yet — a real Stripe account needs to be connected.",
    print_order_pending_title:"Order in progress",
    print_order_pending_body:"We've sent you a confirmation email. We'll let you know as soon as your order goes to print.",
    print_order_confirmed_toast:"Payment successful — check your email 🎉",
    print_order_cancelled_toast:"Payment cancelled, nothing was charged.",
    auth_signup0_birthdate_label:"Date of birth",
    auth_birthdate_day:"Day",
    auth_birthdate_month:"Month",
    auth_birthdate_year:"Year",
    auth_err_underage:"You must be at least 18 years old to use Oncer.",
  },
  es:{
    // ── Feed ──────────────────────────────────────────────────────────
    feed_page_singular:"página",
    feed_page_plural:"páginas",
    feed_page_abbr:"pág.",
    feed_from_followed_books:"📖 De los libros que sigues",
    feed_followed_empty_1:"Aquí verás las páginas de los micro-libros abiertos de las personas que sigues.",
    feed_followed_empty_2:"Sigue a alguien, o espera a que publique algo.",
    feed_share_external_title:"Compartir fuera de Oncer",
    feed_share_watermark_note:"🔖 Cada imagen compartida lleva la firma de Oncer — no se puede quitar.",
    feed_share_preparing:"Preparando...",
    feed_share_photo_btn:"↗ Compartir la foto",
    feed_share_no_native_note:"Tu navegador no puede abrir el panel para compartir la foto — con las opciones de abajo solo se comparte un enlace, sin imagen.",
    feed_share_or_choose:"O elige",
    feed_share_save_image:"⬇ Guardar imagen",
    feed_share_ig_tiktok_note:"Para Instagram y TikTok: guarda la imagen y luego súbela como Historia o Reel.",
    feed_share_message:"Mi página en Oncer 🔖 Be a Oncer. — oncer.app",
    feed_native_share_text:"Be a Oncer. 🔖 oncer.app",
    feed_send_page_to:"Enviar página a...",
    feed_sending:"Enviando...",
    feed_follow_to_send:"Sigue a alguien para poder enviar páginas.",
    feed_share_label:"Compartir",
    feed_send_label:"Enviar",
    feed_you:"tú",
    feed_following:"siguiendo",
    feed_find_your_style:"🎞 Encuentra tu estilo",
    feed_discover_filters:"Descubre los filtros de tendencia o crea el tuyo →",
    feed_no_photo_today:"Todavía no has hecho la foto de hoy",
    feed_reminder_pre:"Elegiste las",
    feed_reminder_post:"como recordatorio — toca para disparar ahora",
    feed_year_summary_cta:"Toca para ver el resumen de tu año →",
    feed_special_moment:"Un momento especial para fotografiar hoy",
    feed_year_ago_today:"✨ Hace un año, hoy",
    feed_toast_share_failed:"No pude abrir el panel para compartir, inténtalo de nuevo.",
    feed_toast_reaction_remove_failed:"No pude quitar la reacción, inténtalo de nuevo.",
    feed_toast_reaction_save_failed:"La reacción no se guardó, inténtalo de nuevo.",
    feed_toast_send_failed:"No pude enviarla, inténtalo de nuevo.",
    // ── Capture ───────────────────────────────────────────────────────
    capture_title:"Captura",
    capture_already_done_title:"¡Ya has hecho la foto de hoy para el libro anual!",
    capture_already_done_sub1:"La página {day} del {year} está escrita.",
    capture_already_done_sub2:"Vuelve mañana para la próxima página del libro.",
    capture_shoot_for_micro:"📎 Haz la foto igualmente para un micro-libro",
    capture_add_another_to_event:"📸 Añade otra foto a este libro",
    capture_event_book_note:"Libro de evento · fotos ilimitadas hoy",
    capture_view_your_book:"Ver tu libro",
    capture_tap_to_shoot:"toca para disparar",
    capture_think_twice_1:"🔒 Piénsalo bien: será la foto elegida para tu día.",
    capture_think_twice_2:"No se puede borrar. Se queda para siempre.",
    capture_write_thought_only:"Hoy sin foto — escribe solo un pensamiento",
    capture_where_to_save:"¿dónde guardas esta foto?",
    capture_words_only_suffix:"solo palabras, hoy",
    capture_which_font:"¿Con qué tipografía?",
    capture_drag_to_reframe:"arrastra para mover el encuadre",
    capture_filter_label:"🎞 filtro",
    capture_browse_all:"Ver todos →",
    capture_sticker_label:"🎨 pegatina (opcional)",
    capture_annual_book_label:"libro anual",
    capture_already_done_today:"Ya hecho por hoy",
    capture_only_micro_note:"esta foto solo va al micro-libro elegido abajo",
    capture_also_diary_label:"¿Añadirla también a tu diario personal?",
    capture_also_diary_hint:"Si está activo, contará como la foto del día y también aparecerá en el Feed.",
    capture_always_in_book:"siempre estará en tu libro",
    capture_annual_book_year:"Libro anual {year}",
    capture_permanent:"permanente",
    capture_choose_micro_required:"elige un micro-libro (obligatorio)",
    capture_add_to_micro_optional:"¿añadirla también a un micro-libro? (opcional)",
    capture_micro_book_word:"micro-libro",
    capture_create_new_micro:"Crear nuevo micro-libro",
    capture_micro_title_placeholder:"nombre del micro-libro, ej. Viaje a Lisboa",
    capture_cancel:"Cancelar",
    capture_creating:"Creando...",
    capture_create:"Crear",
    capture_add_to_shared_optional:"¿añadirla también a un libro compartido? (opcional)",
    capture_with_prefix:"con",
    capture_your_thought_today:"✏️ tu pensamiento de hoy",
    capture_add_a_caption:"añade una frase",
    capture_thought_placeholder:"qué tienes en mente hoy...",
    capture_caption_placeholder:"qué quedará de hoy...",
    capture_thought_not_removable:"✦ Este pensamiento no se podrá eliminar.",
    capture_photo_not_removable:"✦ Esta foto no se podrá eliminar.",
    capture_toast_micro_create_failed:"No pude crear el micro-libro, inténtalo de nuevo.",
    capture_toast_choose_micro_first:"Elige un micro-libro antes de guardar.",
    capture_toast_already_today_reload:"Ya tienes una página para hoy — recarga para verla.",
    capture_toast_saved_local_not_uploaded:"Guardada en el teléfono, no subida online — comprueba tu conexión.",
    capture_toast_micro_not_synced:"No pude guardarla — el micro-libro elegido todavía no está sincronizado online. Inténtalo de nuevo en un momento.",
    capture_toast_already_today_local_only:"Ya tienes una página para hoy — esta se guardó solo en el teléfono.",
    capture_toast_saved_local_not_synced:"Guardada en el teléfono, pero no sincronizada online. Comprueba tu conexión.",
    capture_toast_shared_publish_failed:"No pude publicarla en el libro compartido.",
    capture_toast_camera_failed:"No pude abrir la cámara — comprueba los permisos de la app.",
    capture_saving:"Guardando...",
    capture_write_something_first:"Escribe algo antes de guardar",
    capture_choose_micro_before_save:"Elige un micro-libro antes de guardar",
    capture_save_in_micro:"Guardar en el micro-libro",
    capture_save_in_page:"Guardar en la página {day}",
    capture_page_published:"¡Página {day} publicada!",
    capture_tell_world:"Que el mundo sepa que eres un Oncer — comparte este momento también fuera de la app.",
    capture_share_now:"↗ Compartir ahora",
    capture_continue_in_app:"Continuar en la app",
    capture_kind_photo:"foto",
    capture_blocked_title:"Esta {kind} no se puede publicar",
    capture_blocked_desc:"Nuestro control automático la marcó como posible contenido violento o fuerte. Si crees que es un error, escríbenos — la revisaremos en persona.",
    capture_write_to_us:"Escríbenos, creo que es un error",
    capture_got_it:"Entendido",
    capture_blocked_email_subject:"Foto bloqueada por error",
    // ── Navigation (barra inferior) ───────────────────────────────────
    nav_feed:"feed",
    nav_capture:"captura",
    nav_micro:"micro",
    nav_notebook:"notebook",
    nav_discover:"descubre",
    nav_profile:"perfil",
    // ── Temas y categorías ────────────────────────────────────────────
    theme_neon:"Neon Nights", theme_pastel:"Soft Hours", theme_retro:"Años 90", theme_clean:"Pure",
    theme_concrete:"Concrete", theme_graffiti:"Graffiti", theme_subway:"Subway",
    theme_vinyl:"Vinyl", theme_rave:"Rave", theme_jazzclub:"Jazz Club",
    theme_noir:"Noir", theme_coldcase:"Cold Case",
    theme_industrial:"Industrial", theme_darkroom:"Darkroom",
    theme_sakura:"Sakura", theme_zen:"Zen", theme_akiba:"Akihabara",
    theme_redgold:"Red & Gold", theme_ink:"Chinese Ink", theme_jade:"Jade Palace",
    theme_aurora:"Aurora", theme_ambra:"Ámbar",
    cat_originali:"Originales", cat_urban:"Urban", cat_musica:"Música",
    cat_poliziesco:"Policíaco", cat_underground:"Underground",
    cat_japan:"Japón", cat_chinese:"Chino", cat_aggiuntivi:"Extra",
    // ── Preajustes de micro-libro ────────────────────────────────────
    mp_vacation:"Vacaciones", mp_trip:"Viaje", mp_festival:"Festival", mp_wedding:"Boda",
    mp_birthday:"Cumpleaños", mp_road:"Road Trip", mp_concert:"Concierto", mp_custom:"Personalizado",
    // ── Micro-libros ─────────────────────────────────────────────────
    micro_page_singular:"página", micro_page_plural:"páginas",
    micro_with_prefix:"con {list}",
    micro_loading_pages:"Cargando páginas...",
    micro_load_error_title:"No se pudieron cargar las páginas",
    micro_no_pages_title:"Todavía no hay páginas",
    micro_no_pages_sub:"Toma la primera foto para empezar.",
    micro_shoot_now:"✦ Capturar ahora",
    micro_all_pages:"Todas las páginas",
    micro_the_pages:"Páginas",
    micro_shoot_today:"📷 Capturar hoy",
    micro_print_book:"🖨 Imprimir micro-libro",
    micro_share_outside:"Compartir fuera de Oncer",
    micro_page_locked_note:"🔒 Esta página es permanente y no se puede editar.",
    micro_header_title:"📎 Micro-libros",
    micro_new_btn:"+ Nuevo",
    micro_invite_msg:"te invitó a un libro compartido",
    micro_accept:"Aceptar",
    micro_decline:"Rechazar",
    micro_accept_failed:"No se pudo aceptar, inténtalo de nuevo.",
    micro_decline_failed:"No se pudo rechazar, inténtalo de nuevo.",
    micro_what_are_title:"¿Qué son los micro-libros?",
    micro_what_are_desc:"Libros para momentos específicos: vacaciones, viajes, fiestas. Sin límite de páginas, imprimibles por separado.",
    micro_in_progress:"Micro-libros personales",
    micro_photos:"fotos",
    micro_add_pill:"✦ Añadir",
    micro_planned_progress:"{pages}/{max} fotos planificadas",
    micro_shared_books:"Libros compartidos",
    micro_pag_short:"pág.",
    micro_archived:"Archivados",
    micro_empty_title:"Ningún micro-libro",
    micro_empty_sub:"Crea el primero para un momento especial.",
    micro_create_btn:"+ Crear micro-libro",
    micro_customize_title:"✏️ Personalizar",
    micro_what_moment:"¿Qué momento es?",
    micro_title_label:"Título",
    micro_title_placeholder:"ej. Cerdeña verano, Tokio marzo…",
    micro_who_writes:"¿Quién lo escribe?",
    micro_only_me:"Solo yo",
    micro_shared_kind:"👥 Compartido",
    micro_kind_timed:"📎 Con límite",
    micro_kind_family:"👨‍👩‍👧‍👦 Familia, sin fin",
    micro_kind_evento:"💍 Evento",
    micro_event_type_note:"Los invitados entran mediante código QR/enlace, sin necesidad de tener ya la app ni una cuenta de Oncer.",
    micro_event_limit_title:"Límite de fotos y precio",
    micro_event_limit_sub:"Elige cuántas fotos incluir: el precio de impresión se muestra al instante, sin sorpresas.",
    micro_event_limit_nolimit:"Sin límite",
    micro_event_limit_nolimit_sub:"calculado al final del evento",
    micro_event_limit_price_loading:"Calculando…",
    micro_event_limit_price_unavailable:"Precio no disponible ahora",
    micro_event_progress_label:"fotos recogidas",
    micro_event_fairuse_reminder:"El límite está por agotarse: recuerda a los invitados dejar espacio para los demás.",
    micro_closed_badge:"cerrado",
    micro_event_invite_title:"Código QR y enlace para los invitados",
    micro_copy_link:"Copiar enlace",
    micro_link_copied:"Copiado ✓",
    micro_close_event_btn:"Cerrar el libro del evento",
    micro_closing:"Cerrando…",
    micro_delete_btn:"Eliminar micro-libro",
    micro_delete_shared_btn:"Eliminar libro compartido",
    micro_delete_confirm:"¿Eliminar este micro-libro? Se borrarán todas las fotos que contiene. No se puede deshacer.",
    micro_delete_shared_confirm:"¿Eliminar este libro compartido? Se eliminará para todos los participantes, que recibirán una notificación. No se puede deshacer.",
    micro_delete_success:"Micro-libro eliminado",
    micro_delete_shared_success:"Libro compartido eliminado",
    micro_delete_failed:"No se pudo eliminar, inténtalo de nuevo",
    micro_deleting:"Eliminando…",
    micro_close_event_confirm:"¿Cerrar el libro? Después ya no se podrán añadir fotos, y cada participante recibirá un correo con el libro terminado.",
    micro_close_event_success:"Libro cerrado — avisamos a todos por correo.",
    micro_close_event_failed:"No hemos podido cerrar el libro, inténtalo de nuevo.",
    micro_who_else:"¿Quién más participa?",
    micro_search_username:"Buscar nombre de usuario...",
    micro_cover_theme:"Tema de portada",
    micro_creating:"Creando...",
    micro_create_cta:"Crear micro-libro →",
    micro_toast_need_login:"Debes iniciar sesión para crear un libro compartido.",
    micro_toast_create_failed:"No se pudo crear el libro compartido, inténtalo de nuevo.",
    micro_toast_local_only:"Creado solo en tu teléfono, no sincronizado en línea.",
    // ── Estilos de escritura Notebook ─────────────────────────────────
    nbs_cursive_name:"Cursiva", nbs_cursive_desc:"Elegante y fluido",
    nbs_print_name:"Imprenta", nbs_print_desc:"Claro y ordenado",
    nbs_marker_name:"Rotulador", nbs_marker_desc:"Decidido y audaz",
    nbs_romantic_name:"Romántico", nbs_romantic_desc:"Suave y poético",
    nbs_retro_name:"Máquina", nbs_retro_desc:"Estilo máquina de escribir",
    nbs_minimal_name:"Minimal", nbs_minimal_desc:"Simple y personal",
    // ── Tipos de papel Notebook ──────────────────────────────────────
    nbp_ivory_name:"Marfil", nbp_white_name:"Blanco", nbp_kraft_name:"Kraft",
    nbp_night_name:"Noche", nbp_sage_name:"Salvia",
    // ── Notebook ─────────────────────────────────────────────────────
    nb_time_today:"hoy",
    nb_writes_in:"escribe en {style}",
    nb_page_label:"📄 página {day}",
    nb_placeholder:"escribe aquí...",
    nb_your_writing:"✏️ Tu escritura",
    nb_paper_type:"Tipo de papel",
    nb_sample_text:"texto de ejemplo...",
    nb_sample_greeting:"Hola, ¿cómo estás hoy?",
    nb_blocked_title:"Este mensaje no se puede enviar",
    nb_blocked_desc:"Nuestro control automático lo marcó como posible discurso de odio. Si crees que es un error, escríbenos — lo revisamos en persona.",
    nb_blocked_contact:"Escríbenos, creo que es un error",
    nb_blocked_dismiss:"Entendido",
    nb_toast_send_failed:"Mensaje no enviado, inténtalo de nuevo.",
    nb_opening_chat:"Abriendo la conversación...",
    nb_empty_title:"Ninguna conversación",
    nb_empty_sub:"Busca a un Oncer y empieza a escribir.",
    nb_new_conversation:"+ Nueva conversación",
    nb_new_conversation_title:"Nueva conversación",
    nb_no_messages_yet:"Todavía no hay mensajes — escribe tú el primero",
    nb_you_prefix:"tú: ",
    nb_shared_page_preview:"📄 página compartida — pág. {day}",
    nb_search_placeholder:"Buscar un Oncer...",
    nb_already_chatting:"ya en chat",
    nb_searching:"Buscando...",
    nb_no_users_found:"Ningún Oncer encontrado.",
    nb_toast_profile_not_found:"No se pudo encontrar este perfil.",
    nb_toast_open_failed:"No se pudo abrir la conversación.",
    nb_delete_action:"Eliminar",
    nb_toast_delete_failed:"No se pudo eliminar la conversación, inténtalo de nuevo.",
    // ── Conversación guía "Oncer Experience" ─────────────────────────
    nb_exp_msg1:"Hola, bienvenido a Oncer 👋 Estoy aquí para mostrarte qué puedes hacer, sin prisa — lee cuando quieras, esta conversación se queda aquí para siempre.",
    nb_exp_msg2:"La idea básica es sencilla: una foto al día, para siempre. No se borra, no se modifica — es la página de hoy de tu libro del año. Si algún día prefieres las palabras a una foto, puedes escribir solo un pensamiento, eligiendo también la tipografía que prefieras.",
    nb_exp_msg3:"📚 Tu Biblioteca reúne todas las páginas, día a día, desde el 1 de enero. Incluso los días en que no capturaste una foto tienen su propio pequeño dibujo — ninguna página queda realmente vacía.",
    nb_exp_msg4:"📎 Los micro-libros son colecciones más pequeñas dentro de tu mundo — un viaje, unas vacaciones, un proyecto. Se suman al libro principal, no lo sustituyen: puedes poner la misma foto en ambos a la vez. También puedes crear uno junto a un amigo o con toda tu familia, para capturar todos juntos dentro de él.",
    nb_exp_msg5:"🎯 En Objetivos encontrarás pequeños retos de temporada — al completarlos desbloqueas recompensas: insignias para el perfil, stickers para las fotos, y más.",
    nb_exp_msg6:"⏳ Oncer Future es para los mensajes que escribes hoy y llegan más adelante — a ti mismo dentro de un año, a un amigo, a tu familia. Se desbloquean solo en la fecha que tú elijas.",
    nb_exp_msg7:"✏️ El Notebook — este, donde estamos ahora — es donde escribes a tus amigos, como un verdadero cuaderno: puedes elegir el papel y la caligrafía para cada conversación, para hacerla tuya.",
    nb_exp_msg8:"🎞 En la pantalla de Filtros encontrarás los de tendencia, pero también puedes crear el tuyo y hacerlo público para toda la comunidad.",
    nb_exp_msg9:"Una última cosa: tu biblioteca (la estantería con todos tus libros) se puede reordenar arrastrando los lomos de los libros — organízala como prefieras.",
    nb_exp_msg10:"🖨 También verás un botón para imprimir tus libros — está en camino: pronto podrás convertir un año entero en un objeto real, para tener en tus manos.",
    nb_exp_msg11:"Esto es todo lo que necesitas saber para empezar. El resto lo descubrirás capturando — un día a la vez. Be a Oncer. 🔖",
    // ── Descubre ─────────────────────────────────────────────────────
    disc_header_title:"🔭 Descubre",
    disc_your_library:"Tu biblioteca",
    disc_followers_needed:"faltan {n} seguidores para desbloquear",
    disc_follower_count:"{n} seguidores",
    disc_to_unlock:"{n} para desbloquear",
    disc_tab_people:"👤 Personas",
    disc_tab_libraries:"📖 Bibliotecas",
    disc_tab_themes:"🎨 Temas",
    disc_tab_micro:"📎 Micro-libros",
    disc_tab_filters:"🎞 Filtros",
    disc_search_placeholder:"Busca un Oncer por nombre de usuario...",
    disc_search_hint:"Busca un nombre de usuario para encontrar otros Oncers y seguirlos.",
    disc_searching:"Buscando...",
    disc_no_people_found:"Ningún Oncer encontrado con este nombre de usuario.",
    disc_loading:"Cargando...",
    disc_no_libraries:"Todavía no hay otros Oncers que mostrar.",
    disc_verified_badge:"📖 Verificado",
    disc_follow_btn:"+ Seguir",
    disc_libraries_count:"{n} bibliotecas",
    disc_exclusive_title:"✨ Solo se desbloquean aquí",
    disc_unlocked_hint:"Desbloqueado — elígelo en Ajustes",
    disc_tap_to_unlock:"Toca para desbloquear",
    disc_no_micro:"Todavía no hay ningún micro-libro completado que mostrar.",
    disc_micro_by:"de @{u} · {pages} páginas",
    disc_filters_hero_title:"🎞 Encuentra tu estilo",
    disc_filters_hero_desc:"Cada filtro cuenta quién eres. Explora la galería, crea el tuyo, deja que otros Oncers te descubran.",
    disc_filters_open_gallery:"Abrir la galería →",
    disc_filters_trending_title:"🔥 Tendencia este mes",
    disc_filter_uses:"{n} usos",
    // ── Perfil ───────────────────────────────────────────────────────
    prof_add_bio:"Añadir una bio →",
    prof_streak_badge_title:"{n} días seguidos completando todos los objetivos diarios",
    prof_streak_badge_off_title:"Completa todos los objetivos de hoy para encender la llama",
    prof_loading_profile:"Cargando perfil...",
    prof_open_library_badge:"📖 Biblioteca Abierta",
    prof_stat_followers:"seguidores",
    prof_stat_following:"siguiendo",
    prof_stat_books:"libros",
    prof_following_already:"✓ Ya sigues",
    prof_request_sent:"Solicitud enviada",
    prof_goals_label:"Objetivos",
    prof_annual_books_label:"libros anuales",
    prof_private_library_title:"Biblioteca privada",
    prof_private_desc1:"@{username} todavía no tiene una biblioteca pública.",
    prof_private_desc2:"Síguelo para ver más cuando la abra.",
    prof_toast_generic_fail:"No se pudo completar, inténtalo de nuevo en un momento.",
    prof_book_meta:"{theme} · {pages} páginas",
    prof_in_progress_badge:"EN CURSO",
    prof_micro_books_label:"micro-libros",
    prof_micro_meta:"📎 micro-libro · {pages} páginas",
    prof_shared_books_label:"libros compartidos",
    prof_pages_count:"{n} páginas",
    // ── Nombres y categorías de filtros ──────────────────────────────
    filtname_none:"Original", filtname_golden:"Golden Hour", filtname_noir:"Cinema Noir",
    filtname_dream:"Dreamy", filtname_vivid:"Vivid Pop", filtname_vintage83:"'83 Vintage",
    filtname_moody:"Moody Blue", filtname_clean:"Clean White",
    filtname_c_sofia1:"Pétalo", filtname_c_luca1:"Afterhours", filtname_c_marco1:"Detective",
    filtname_ft_solstice:"Solsticio", filtname_ft_frost:"Escarcha",
    filtname_os_portra:"Portra Cálida", filtname_os_ektar:"Ektar Vívido",
    filtname_os_superia:"Superia Verde", filtname_os_velvia:"Velvia Paisaje",
    filtname_os_gold:"Gold Nostalgia", filtname_os_polaroid:"Polaroid Desvaída",
    filtname_os_ektachrome:"Ektachrome Onírico", filtname_os_agfa:"Agfa Europa",
    filtname_os_trix:"Tri-X Contraste", filtname_os_pro160s:"Pro160S Suave",
    filtcat_setup:"Oncer Setup", filtcat_trend:"Tendencia", filtcat_vintage:"Vintage",
    filtcat_bn:"B&N", filtcat_vivid:"Vívido", filtcat_pastel:"Pastel",
    filtcat_cinema:"Cinema", filtcat_community:"Community",
    // ── Pantalla de Filtros ──────────────────────────────────────────
    filt_header_title:"🎞 Filtros",
    filt_create_btn:"+ Crear",
    filt_hero_title:"✨ Encuentra tu estilo distintivo",
    filt_hero_desc:"Cada Oncer tiene una forma distinta de contar sus días. Crea tu propio filtro y deja que toda la comunidad te descubra.",
    filt_pick_hint:"Elige un filtro para aplicar a tu foto de hoy.",
    filt_search_placeholder:"Busca un filtro o un creador...",
    filt_cat_all:"Todos",
    filt_results_count:"Resultados ({n})",
    filt_all_filters:"Todos los filtros",
    filt_by_creator:"de @{creator}",
    filt_use_this:"Usar este →",
    filt_seasonal_goal_fallback:"Objetivo de temporada",
    filt_none_found:"Ningún filtro encontrado.",
    filt_create_title:"Crea tu filtro",
    filt_name_label:"Nombre del filtro",
    filt_name_placeholder:"ej. Nostalgia, Sunset Mood...",
    filt_base_label:"Base",
    filt_slider_saturation:"🎨 Saturación",
    filt_slider_contrast:"◐ Contraste",
    filt_slider_brightness:"☀️ Brillo",
    filt_slider_warmth:"🌡️ Calidez",
    filt_warmth_warm:"+{v} cálido",
    filt_warmth_cold:"{v} frío",
    filt_warmth_neutral:"neutro",
    filt_advanced_label:"Avanzado",
    filt_slider_hue:"🌈 Tono",
    filt_slider_sepia:"📜 Sepia",
    filt_slider_vignette:"🖤 Viñeta",
    filt_slider_grain:"🎞 Grano",
    filt_absent:"ninguno",
    filt_toast_local_only:"Creado solo en tu teléfono, no compartido con la comunidad.",
    filt_publishing:"Publicando...",
    filt_publish_btn:"Publicar filtro",
    filt_published_title:"¡Filtro publicado!",
    filt_published_desc:"\"{name}\" ahora es visible para toda la comunidad Oncer. Ahora depende de ti hacer que lo descubran.",
    filt_view_gallery_btn:"Ver en la galería",
    // ── Cierre de Año ────────────────────────────────────────────────
    yc_title:"Tu {year} está a punto de cerrarse",
    yc_subtitle:"Un año de fotos, un día a la vez. Esto es lo que has construido.",
    yc_stat_pages:"páginas escritas",
    yc_stat_streak:"racha más larga",
    yc_stat_top_month:"tu mes más activo",
    yc_stat_filters:"filtros distintos usados",
    yc_print_btn:"🖨 Imprimir tu libro de {year}",
    yc_footer_note:"El {nextYear} empieza solo, en tu próxima página — sin botones que pulsar. El libro de este año permanece para siempre tal como está.",
    // ── Oncer Future ─────────────────────────────────────────────────
    of_tab_write:"Escribir",
    of_tab_received:"Recibidos",
    of_write_intro:"Escribe algo hoy. No lo leerás (ni lo leerá quien elijas) hasta que llegue la fecha que tú elijas — a tu yo futuro, a un amigo, o a un recuerdo familiar.",
    of_recipient_label:"¿Para quién es?",
    of_recipient_self:"Tú mismo",
    of_recipient_friend:"Un amigo",
    of_recipient_family:"Familia",
    of_family_placeholder:"¿Para quién de la familia? (opcional, ej. 'para mamá')",
    of_unlock_when_label:"¿Cuándo se desbloquea?",
    of_unlock_6m:"En 6 meses",
    of_unlock_1y:"En 1 año",
    of_unlock_nextnewyear:"El próximo 1 de enero",
    of_unlock_custom:"Elegir fecha",
    of_content_placeholder:"Escribe aquí...",
    of_sending:"Enviando...",
    of_send_btn:"Sellar y enviar al futuro",
    of_toast_send_failed:"No se pudo enviar, inténtalo de nuevo.",
    of_unlocked_label:"Desbloqueados",
    of_written_by_you:"✍️ Escrito por ti",
    of_from_user:"💌 De @{username}",
    of_in_transit_label:"En camino hacia el futuro",
    of_unlocks_on:"Se desbloquea el {date}",
    of_empty_state:"Todavía no has escrito nada al futuro.",
    // ── Pantalla de Objetivos ─────────────────────────────────────────
    goals_header_title:"🎯 Objetivos",
    goals_rewards_link:"Recompensas →",
    goals_season_banner:"{icon} Temporada: {name}",
    goals_event_banner:"✨ {icon} ¡Es hora de {event}! Objetivos especiales disponibles.",
    goals_season_default_banner:"Completa los objetivos trimestrales para desbloquear recompensas temáticas.",
    goals_section_today:"Hoy",
    goals_daily_subtitle:"Cambian cada día",
    goals_daily_all_done:"Día completo ✨",
    goals_daily_streak_toast:"🔥 ¡Día perfecto! Racha de {n} días",
    goals_section_month:"Este mes",
    goals_section_season:"Objetivos de temporada",
    goals_footer_note:"Llegan nuevos objetivos y recompensas con cada cambio de estación — invierno, primavera, verano, otoño — con ediciones especiales para Pascua, Acción de Gracias y Navidad.",
    goals_claimed_title:"Has desbloqueado: {name}",
    goals_claim_sticker:"Disponible ahora al capturar una nueva página.",
    goals_claim_badge:"Visible ahora en tu perfil.",
    goals_claim_divider:"Puedes equiparlo en tu estantería desde el inventario.",
    goals_claim_filter:"Nuevo filtro exclusivo en la galería.",
    goals_claim_cta:"¡Genial!",
    // ── Pantalla de Recompensas ────────────────────────────────────────
    rewards_header_title:"🎁 Tus recompensas",
    rewards_unlocked_count:"{n} de {total} desbloqueadas",
    rewards_cat_all:"Todos",
    rewards_cat_sticker:"🎨 Stickers",
    rewards_cat_badge:"🎖️ Insignias",
    rewards_cat_divider:"📚 Sujetalibros",
    rewards_cat_filter:"🎞 Filtros",
    reward_usage_sticker:"Se usa cuando haces una foto",
    reward_usage_badge:"Aparece en tu perfil",
    reward_usage_divider:"Se usa en la estantería de tu biblioteca",
    reward_usage_filter:"Se usa en la cámara",
    rewards_equip_title:"Equipar en la estantería (2 espacios)",
    rewards_slot_label:"Espacio {n}",
    rewards_none_option:"— ninguno —",
    rewards_empty_state:"Todavía no hay recompensas aquí — completa los objetivos para desbloquearlas.",
    rewards_still_locked_label:"Aún por desbloquear",
    // ── Objetivos diarios/mensuales/trimestrales ──────────────────────
    goal_d_post:"Captura la página de hoy",
    goal_d_caption:"Escribe una didascalia hoy",
    goal_d_react1:"Reacciona a al menos 1 página",
    goal_d_react2:"Reacciona a 2 páginas de amigos",
    goal_d_react3:"Reacciona a 3 páginas de amigos",
    goal_d_filter:"Usa un filtro hoy",
    goal_d_sticker:"Añade un adhesivo hoy",
    goal_d_caption_long:"Escribe una descripción más larga (20+ caracteres)",
    goal_d_combo:"Completa la página con descripción y adhesivo",
    goal_d_early:"Publica tu página antes del mediodía",
    goal_m_streak7:"Racha de 7 días consecutivos",
    goal_m_pages15:"15 páginas este mes",
    goal_m_filters3:"Usa 3 filtros diferentes",
    goal_q1_pages60:"60 páginas este invierno",
    goal_q1_easter:"Captura durante la semana de Pascua",
    goal_q1_streak14:"Racha de 14 días consecutivos",
    goal_q1_shared:"Forma parte de un libro compartido",
    goal_q1_sticker:"Usa un sticker en una página",
    goal_q2_pages60:"60 páginas esta primavera",
    goal_q2_micro:"Crea un micro-libro",
    goal_q2_streak14:"Racha de 14 días consecutivos",
    goal_q2_filters5:"Usa 5 filtros diferentes",
    goal_q2_ownfilter:"Crea y publica tu propio filtro",
    goal_q3_pages60:"60 páginas este verano",
    goal_q3_micro3:"3 micro-libros en total",
    goal_q3_streak21:"Racha de 21 días consecutivos",
    goal_q3_shared2:"Forma parte de 2 libros compartidos",
    goal_q3_ownfilter2:"Crea 2 filtros propios",
    goal_q4_pages60:"60 páginas este otoño",
    goal_q4_thanksgiving:"Captura el día de Acción de Gracias",
    goal_q4_christmas:"Captura el día de Navidad",
    goal_q4_streak30:"Racha de 30 días consecutivos",
    goal_q4_micro5:"5 micro-libros en total",
    // ── Nombres de recompensas ─────────────────────────────────────────
    reward_sk_fire:"Llama", reward_sk_star:"Estrella", reward_sk_heart:"Corazón de oro",
    reward_sk_sparkle:"Destello", reward_sk_snow:"Copo de nieve", reward_sk_egg:"Huevo de Pascua",
    reward_sk_turkey:"Pavo", reward_sk_tree:"Árbol de Navidad", reward_sk_flower:"Flor",
    reward_bd_streak7:"Semana Completa", reward_bd_streak30:"Mes de Hierro",
    reward_bd_pages100:"Cien Páginas", reward_bd_explorer:"Explorador de Filtros",
    reward_bd_yearfull:"Año Completo",
    reward_dv_owl:"Búho Sabio", reward_dv_plant:"Maceta Florecida",
    reward_dv_lamp:"Lámpara Cálida", reward_dv_gold:"Trofeo Dorado",
    reward_sk_family:"Familia", reward_sk_camera:"Objetivo",
    reward_bd_social:"Círculo Cercano", reward_dv_book:"Libro Abierto",
    // ── Estaciones y eventos ───────────────────────────────────────────
    season_winter:"Invierno", season_spring:"Primavera", season_summer:"Verano", season_autumn:"Otoño",
    event_easter:"Pascua", event_thanksgiving_christmas:"Acción de Gracias y Navidad",
    // ── Impostazioni ──────────────────────────────────────────────────
    set_header_title:"Ajustes",
    set_avatar_uploading:"Subiendo...",
    set_avatar_hint:"Toca para cambiar tu foto de perfil",
    set_cover_change:"Cambiar portada",
    set_cover_add:"Añadir una portada",
    set_sec_account:"Cuenta",
    set_sec_notifications:"Notificaciones",
    set_sec_privacy:"Privacidad",
    set_sec_data:"Tus datos",
    set_sec_customization:"Personalización",
    set_sec_subscription:"Suscripción",
    set_sec_style:"Estilo",
    set_sec_print:"Impresión",
    set_sec_admin:"Administración",
    set_sec_info:"Información",
    set_row_username:"Nombre de usuario",
    set_row_bio:"Biografía",
    set_bio_empty:"Sin biografía",
    set_bio_cancel:"Cancelar",
    set_bio_edit:"Editar",
    set_bio_placeholder:"Escribe algo sobre ti...",
    set_bio_save:"Guardar",
    set_row_email:"Correo electrónico",
    set_row_change_password:"Cambiar contraseña",
    set_row_daily_notif:"Recordatorio diario",
    set_daily_notif_sub:"Todos los días a las {time}",
    set_row_push_notif:"Notificaciones push",
    set_row_email_notif:"Notificaciones por correo",
    set_row_library_public:"Biblioteca pública",
    set_library_public_sub:"Visible para todos a partir de 1.000 seguidores",
    set_row_who_message:"Quién puede escribirme",
    set_who_everyone:"Todos",
    set_who_following:"Solo a quienes sigo",
    set_row_blocked_users:"Usuarios bloqueados",
    set_blocked_none:"Ninguno",
    set_soon_badge:"Próximamente",
    set_row_export:"Exporta tus datos",
    set_export_preparing:"Preparando...",
    set_export_sub:"Descarga una copia actualizada de todo lo que has creado",
    set_row_data_location:"Dónde viven tus datos",
    set_data_location_sub:"En los servidores de Supabase, protegidos y nunca vendidos a terceros",
    set_row_theme:"Tema de la biblioteca",
    set_row_edit_library:"Edita tu biblioteca",
    set_edit_library_sub:"Reordena los libros, coloca insignias y separadores",
    set_row_nb_style:"Estilo de escritura del Notebook",
    set_row_nb_paper:"Tipo de papel del Notebook",
    set_row_plan:"Tu plan",
    set_plan_sub:"{plan} · Gestiona o mejora tu plan",
    set_row_filters:"Galería de filtros",
    set_filters_sub:"Descubre, crea y deja que te descubran con tus filtros",
    set_row_print:"Imprime tus libros",
    set_print_sub:"Convierte un año en un objeto físico",
    set_row_moderation:"Contenido bloqueado",
    set_moderation_sub:"Registro de fotos y mensajes bloqueados por moderación",
    set_row_terms:"Términos y condiciones",
    set_row_privacy_policy:"Política de privacidad",
    set_row_feedback:"Enviar comentarios",
    set_row_version:"Versión",
    set_row_logout:"Cerrar sesión",
    set_row_delete_account:"Eliminar cuenta",
    set_logout_confirm:"¿Cerrar sesión de tu cuenta de Oncer?",
    set_delete_confirm:"Para eliminar definitivamente tu cuenta y todos tus datos de la base de datos, escribe a hello@oncer.app desde el correo con el que te registraste — lo haremos en un plazo de 48 horas, según el derecho de supresión.\n\n¿Quieres mientras tanto cerrar sesión y borrar los datos guardados en este dispositivo?",
    set_time_sheet_title:"¿Cuándo quieres que te avisemos?",
    set_theme_sheet_title:"🎨 Tema de la biblioteca",
    set_theme_locked_hint:"Todavía no has desbloqueado ningún tema adicional — ve a Descubrir, sección \"Temas\", para encontrarlos.",
    set_style_sheet_title:"✏️ Estilo de escritura del Notebook",
    set_style_sample:"Hola, ¿cómo estás?",
    set_paper_sheet_title:"📄 Tipo de papel del Notebook",
    set_pw_sheet_title:"🔑 Cambiar contraseña",
    set_pw_new_label:"Nueva contraseña",
    set_pw_new_placeholder:"al menos 6 caracteres",
    set_pw_repeat_label:"Repite la nueva contraseña",
    set_pw_repeat_placeholder:"repite",
    set_pw_err_short:"Al menos 6 caracteres.",
    set_pw_err_mismatch:"Las dos contraseñas no coinciden.",
    set_pw_save:"Guardar nueva contraseña",
    set_pw_saving:"Guardando...",
    set_toast_bio_offline:"Biografía guardada solo en tu teléfono, no en línea — revisa tu conexión.",
    set_toast_pw_updated:"Contraseña actualizada ✓",
    set_toast_avatar_save_fail:"No se pudo guardar tu foto, inténtalo de nuevo.",
    set_toast_avatar_upload_fail:"No se pudo subir tu foto, inténtalo de nuevo.",
    set_toast_cover_save_fail:"No se pudo guardar tu portada, inténtalo de nuevo.",
    set_toast_cover_upload_fail:"No se pudo subir tu portada, inténtalo de nuevo.",
    // ── Batch 2: Editor scaffale, Legal, Abbonamento, Accesso, Notifiche, Moderazione, Tour, App ──
    shelf_header_title:"🛠 Edita tu biblioteca",
    shelf_instructions:"Arrastra los libros para reordenarlos. Arrastra las insignias y los separadores a cualquier parte de la estantería, incluso a los bordes. Aquí nada se abre por error — puedes editar con calma.",
    shelf_empty_decor:"Todavía no has desbloqueado insignias ni separadores — se desbloquean completando objetivos estacionales.",
    shelf_decor_section_title:"Tus insignias y separadores",
    shelf_decor_equipped:"En la estantería",
    shelf_decor_tap_to_add:"Toca para añadir",
    sub_header_title:"Suscripción",
    sub_title:"Elige tu Oncer",
    sub_subtitle:"Más temas, más libros, más formas de no olvidar.",
    sub_current_badge:"Plan actual",
    sub_btn_active:"Plan activo",
    sub_btn_switch_free:"Pasar a Free",
    sub_btn_activate:"Activar {plan}",
    sub_footer_note:"Puedes cancelar o cambiar de plan en cualquier momento.",
    sub_coming_title:"Próximamente",
    sub_coming_body:"Estamos preparando los pagos — {plan} estará disponible pronto. Te avisaremos en cuanto esté listo.",
    sub_coming_ok:"Entendido",
    plan_period_free:"para siempre",
    plan_period_month:"/mes",
    plan_badge_recommended:"Recomendado",
    plan_free_f1:"1 foto al día",
    plan_free_f2:"Libro anual ilimitado",
    plan_free_f3:"5 temas base",
    plan_free_f4:"1 micro-libro activo",
    plan_free_f5:"2 estilos de escritura Notebook",
    plan_free_f6:"2 tipos de papel",
    plan_plus_f1:"Los 20 temas",
    plan_plus_f2:"Micro-libros ilimitados",
    plan_plus_f3:"Los 6 estilos de escritura",
    plan_plus_f4:"Los 5 papeles del Notebook",
    plan_plus_f5:"Stickers personalizados",
    plan_plus_f6:"Estadísticas anuales",
    plan_plus_f7:"Insignia exclusiva en el perfil",
    plan_pro_f1:"Todo lo de Oncer+",
    plan_pro_f2:"Exportación PDF de alta calidad ilimitada",
    plan_pro_f3:"20% de descuento en cada impresión",
    plan_pro_f4:"Acceso anticipado a los temas de temporada",
    plan_pro_f5:"Insignia Pro exclusiva",
    plan_pro_f6:"Soporte prioritario",
    notif_header_title:"Notificaciones",
    notif_empty_title:"Sin notificaciones",
    notif_empty_body:"Cuando pase algo, lo encontrarás aquí.",
    notif_accept:"Aceptar",
    notif_decline:"Rechazar",
    notif_action_failed:"No lo he conseguido, inténtalo de nuevo.",
    notif_time_now:"ahora",
    notif_time_min:"hace {n}m",
    notif_time_hour:"hace {n}h",
    notif_time_day:"hace {n}d",
    notif_desc_follow_request:"quiere seguirte",
    notif_desc_follow_accepted:"ha aceptado tu solicitud",
    notif_desc_reaction_plain:"ha reaccionado a una de tus páginas",
    notif_desc_reaction_with_emoji:"ha reaccionado con {emoji} a una de tus páginas",
    notif_desc_shared_invite:"te ha invitado al libro compartido",
    notif_desc_shared_deleted:"eliminó el libro compartido",
    mod_locked_message:"Esta sección no está disponible.",
    mod_header_title:"🛡 Contenido bloqueado",
    mod_empty:"Ningún bloqueo registrado por ahora.",
    mod_image_loading:"Cargando imagen...",
    tour_step1_title:"El Feed",
    tour_step1_body:"Aquí ves las fotos de quienes sigues, tus micro-libros arriba del todo, y cuánto falta para completar el libro de este año.",
    tour_step2_title:"Captura",
    tour_step2_body:"Una foto al día, para siempre — no se borra ni se edita. Si hoy no tienes una foto, puedes escribir solo un pensamiento, eligiendo también la tipografía.",
    tour_step3_title:"Biblioteca",
    tour_step3_body:"Cada día se convierte en una página. Aquí ves el libro completo del año, día a día, desde el 1 de enero — hasta los días que faltan tienen su pequeño dibujo.",
    tour_step4_title:"Micro-libros",
    tour_step4_body:"Colecciones más pequeñas — un viaje, unas vacaciones — que se suman al libro principal, sin sustituirlo. Puedes crear uno también junto a un amigo o con toda la familia.",
    tour_step5_title:"Notebook",
    tour_step5_body:"Escribe a tus amigos como en un cuaderno de verdad — elige el papel y la caligrafía que prefieras para cada conversación.",
    tour_step6_title:"Tu perfil",
    tour_step6_body:"Aquí personalizas tema, foto y portada — y es donde encuentras Objetivos, Recompensas y Oncer Future, los mensajes que escribes hoy para tu futuro.",
    tour_skip:"Saltar el recorrido",
    tour_next:"Siguiente →",
    tour_finish:"Entendido, ¡vamos! ✦",
    app_print_soon_title:"Próximamente",
    app_print_soon_body_1:"Estamos preparando la impresión de tus libros.",
    app_print_soon_body_2:"Vuelve pronto.",
    app_print_soon_cta:"Volver al Feed",
    app_notif_reminder_body:"Todavía no has hecho la página de hoy 📖",
    app_swipehint_title:"Un consejo rápido",
    app_swipehint_body1:"Desliza el dedo a la izquierda o a la derecha para pasar de una sección a otra, como al hojear un libro.",
    app_swipehint_body2:"Dentro de un chat o un menú, desliza hacia la derecha para volver atrás.",
    app_swipehint_cta:"Entendido",
    auth_pwrecovery_title:"Nueva contraseña",
    auth_pwrecovery_subtitle:"Elige una nueva para tu cuenta de Oncer.",
    auth_pw_repeat_label:"Repite la contraseña",
    auth_pwrecovery_save:"Guardar nueva contraseña ✦",
    auth_tagline_1:"Tu historia,",
    auth_tagline_2:"una página al día.",
    auth_welcome_cta:"Crea tu Oncer →",
    auth_welcome_have_account:"Ya tengo una cuenta",
    auth_welcome_terms_notice:"Al continuar aceptas los Términos y la Política de Privacidad, disponibles en cualquier momento desde Ajustes.",
    auth_checkemail_title:"Revisa tu correo",
    auth_checkemail_sent_to:"Te hemos enviado un enlace de confirmación a",
    auth_checkemail_instructions:"Abre ese enlace — te traerá directamente aquí para elegir tu nombre de usuario, tema y una foto. Un solo paso, no dos.",
    auth_checkemail_spam_hint:"¿No lo encuentras? Revisa también la carpeta de spam.",
    auth_checkemail_confirmed_link:"Ya he confirmado, llévame al acceso →",
    auth_login_eyebrow:"Acceder",
    auth_login_title:"Bienvenido de nuevo.",
    auth_login_id_label:"Correo o nombre de usuario",
    auth_login_id_placeholder:"tu@ejemplo.com o nombre de usuario",
    auth_login_username_hint:"¿No recuerdas tu nombre de usuario? No hay problema — con el correo basta.",
    auth_password_label:"Contraseña",
    auth_login_forgot:"¿Olvidaste tu contraseña?",
    auth_login_submit:"Acceder →",
    auth_login_submitting:"Accediendo...",
    auth_err_wrong_creds:"Correo/usuario o contraseña incorrectos.",
    auth_err_locked_out:"Demasiados intentos. Vuelve a intentarlo en {minutes} minutos.",
    auth_err_captcha_required:"Completa primero la verificación de seguridad de abajo.",
    auth_err_email_not_confirmed:"Primero debes confirmar tu correo — revisa tu bandeja y abre el enlace que te enviamos.",
    auth_err_no_account_username:"No existe ninguna cuenta con este nombre de usuario.",
    auth_forgot_eyebrow:"Recuperar contraseña",
    auth_forgot_title:"Te enviamos un enlace.",
    auth_forgot_subtitle:"Escribe el correo con el que te registraste — recibirás un enlace para elegir una nueva contraseña.",
    auth_forgot_email_placeholder:"tu@ejemplo.com",
    auth_forgot_success:"✓ Revisa tu correo — te hemos enviado un enlace para elegir una nueva contraseña.",
    auth_forgot_cant_access:"¿No consigues acceder de ninguna manera?",
    auth_forgot_write_us:"Escríbenos",
    auth_forgot_submit:"Enviar enlace →",
    auth_forgot_submitting:"Enviando...",
    auth_signup0_eyebrow:"Crea tu acceso",
    auth_signup0_title:"Empecemos por aquí",
    auth_signup0_subtitle:"Te enviamos enseguida un correo de confirmación — el nombre de usuario, el tema y la foto los eliges después de abrirlo, en un solo paso.",
    auth_signup0_password_placeholder:"Al menos 6 caracteres",
    auth_err_validation:"Un correo válido y una contraseña de al menos 6 caracteres.",
    auth_continue:"Continuar →",
    auth_creating:"Creando...",
    auth_signup1_eyebrow:"1 de 4 · ¡Correo confirmado!",
    auth_signup1_title:"¿Cómo te llamas?",
    auth_signup1_subtitle:"Tu nombre de usuario será visible para otros Oncers.",
    auth_signup1_username_placeholder:"ej. matte__",
    auth_signup1_min_chars:"Al menos 2 caracteres",
    auth_signup1_checking:"Comprobando disponibilidad...",
    auth_signup1_available:"✓ Nombre de usuario disponible",
    auth_signup1_taken:"Este nombre de usuario ya está en uso, elige otro.",
    auth_signup2_eyebrow:"2 de 4",
    auth_signup2_title:"Elige tu tema",
    auth_signup2_subtitle:"Define tu biblioteca. Siempre puedes cambiarlo.",
    auth_signup2_selected:"✓ Seleccionado",
    auth_signup3_eyebrow:"3 de 4",
    auth_signup3_title:"¿Cuándo sueles hacer tu foto?",
    auth_signup3_subtitle:"Te enviamos una notificación al día. Solo una.",
    auth_signup3_submit:"Empezar a capturar ✦",
    auth_signup4_eyebrow:"4 de 4",
    auth_signup4_title:"¿Pones una foto?",
    auth_signup4_subtitle:"Así quienes te siguen te reconocen enseguida. Opcional — puedes hacerlo más adelante.",
    auth_signup4_saved:"Foto guardada",
    auth_signup4_tap_hint:"Toca para elegir una foto",
    auth_signup4_finish_with_photo:"Hecho, ¡vamos! ✦",
    auth_signup4_finish_no_photo:"Continuar ✦",
    auth_signup4_skip:"o continúa sin foto →",
    auth_err_email_registered:"Este correo ya está registrado. Prueba a iniciar sesión.",
    auth_err_username_taken:"Nombre de usuario ya en uso, elige otro.",
    auth_err_session_expired:"Sesión caducada, intenta acceder de nuevo.",
    priv_title:"Política de Privacidad",
    priv_last_updated:"Última actualización: septiembre de 2026 · De acuerdo con el Reglamento (UE) 2016/679 (RGPD)",
    priv_beta_notice:"⚠️ Esta política describe el funcionamiento actual de Oncer, todavía en fase beta. No sustituye el asesoramiento legal: antes del lanzamiento público debe ser revisada por un profesional habilitado, especialmente antes de activar los pagos reales.",
    priv_s1_title:"1. Responsable del tratamiento",
    priv_s1_body:"El responsable del tratamiento de los datos es el equipo de Oncer, al que puedes contactar en la dirección de correo indicada al final de esta página.",
    priv_s2_title:"2. Cómo funciona Oncer",
    priv_s2_pre:"Oncer utiliza ",
    priv_s2_bold:"un servidor real (Supabase)",
    priv_s2_post:" para guardar tus contenidos — fotos, pies de foto, temas, conversaciones, objetivos, libros compartidos — para que puedas acceder a tu diario desde más de un dispositivo y las personas que invites puedan ver los libros compartidos con ellas. Los datos se guardan en los servidores de Supabase, nuestro proveedor de base de datos y autenticación, protegidos por reglas de acceso que permiten que cada usuario vea solo sus propios datos y los que se han compartido explícitamente con él.",
    priv_s3_title:"3. Datos tratados",
    priv_s3_body:"Email y contraseña (gestionada de forma segura por Supabase Auth, nunca visible para nosotros en texto plano), nombre de usuario, biografía, fotos subidas, pies de foto, tema gráfico elegido, filtros creados, mensajes del Notebook, objetivos y recompensas desbloqueadas, contenido de los libros compartidos. Si pides un libro impreso: nombre, dirección de envío, ciudad, código postal, país, teléfono y email usados para la entrega.",
    priv_s4_title:"4. Finalidad y base jurídica",
    priv_s4_body:"Los datos se tratan para que la aplicación funcione según sus características (diario fotográfico, personalización, gamificación, libros compartidos) y, para quien pide un libro impreso, para ejecutar el pedido (producción, envío, pago). La base jurídica es la ejecución de un servicio solicitado por el usuario (art. 6.1.b RGPD) y, cuando corresponda, el cumplimiento de obligaciones legales fiscales/contables (art. 6.1.c RGPD).",
    priv_s5_title:"5. Servicios de terceros",
    priv_s5_body:"Para funcionar, Oncer se apoya en: Supabase (base de datos, autenticación y almacenamiento de fotos); Google Fonts (tipografías); api.qrserver.com (genera el código QR para invitar a otras personas a un libro compartido, recibe solo el enlace de invitación). Si pides un libro impreso, también intervienen: Stripe (procesa el pago — nunca recibimos los datos de tu tarjeta, quedan enteramente en Stripe); Prodigi y Gelato (proveedores de impresión y envío, reciben tu nombre, dirección de envío y el archivo del libro para producirlo y enviarlo). Cada uno de estos proveedores trata los datos según su propia política de privacidad.",
    priv_s6_title:"6. Conservación de los datos",
    priv_s6_body:"Los datos del perfil y de los contenidos permanecen en nuestros servidores hasta que elimines tu cuenta (Ajustes → Eliminar cuenta), lo que los elimina de forma permanente. Los datos de un pedido de impresión ya enviado a Prodigi, Gelato o Stripe siguen sujetos a sus propios plazos de conservación (p. ej. obligaciones fiscales o de garantía) incluso después de eliminar la cuenta.",
    priv_s7_title:"7. Tus derechos",
    priv_s7_pre:"Según los artículos 15-22 del RGPD tienes derecho a: acceder a tus datos, corregirlos, eliminarlos, limitar su tratamiento, solicitar su portabilidad y oponerte al tratamiento. Puedes ejercer el acceso y la eliminación directamente desde: ",
    priv_s7_bold1:"Ajustes → Exporta tus datos",
    priv_s7_mid:" (portabilidad) y ",
    priv_s7_bold2:"Ajustes → Eliminar cuenta",
    priv_s7_post:" (eliminación). Para solicitudes de rectificación, limitación u oposición, escríbenos a la dirección indicada al final de esta página.",
    priv_s8_title:"8. Seguridad",
    priv_s8_body:"Tus datos se guardan en los servidores de Supabase y están protegidos por autenticación y reglas de acceso que mantienen aislados los datos de cada usuario. La comunicación entre la app y el servidor está cifrada (HTTPS/TLS). Aun así, te recomendamos proteger tu teléfono/ordenador con un bloqueo de pantalla y no compartir nunca tu contraseña.",
    priv_s9_title:"9. Menores",
    priv_s9_body:"Oncer no está destinado a personas menores de 14 años. Si tienes entre 14 y 18 años, el uso de la aplicación requiere el consentimiento de quien ejerza tu responsabilidad parental.",
    priv_s10_title:"10. Reclamaciones",
    priv_s10_body:"Tienes derecho a presentar una reclamación ante la autoridad italiana de protección de datos (Garante per la Protezione dei Dati Personali, [www.garanteprivacy.it](https://www.garanteprivacy.it)), o ante la autoridad de protección de datos de tu propio país, si consideras que el tratamiento vulnera el RGPD.",
    priv_s11_title:"11. Modificaciones",
    priv_s11_body:"Esta política se actualiza cuando cambia de forma sustancial cómo tratamos tus datos — la última revisión refleja el paso a un servidor real (Supabase), la introducción de los pedidos de impresión y la intervención de los proveedores terceros relacionados (Stripe, Prodigi, Gelato).",
    priv_s12_title:"12. Contacto",
    priv_s12_body:"Para cualquier solicitud relacionada con tus datos: hello@oncer.app",
    terms_title:"Términos y Condiciones",
    terms_last_updated:"Última actualización: septiembre de 2026",
    terms_beta_notice:"⚠️ Versión beta con fines demostrativos. Antes de un lanzamiento público, estos términos deben formalizarse con un abogado, especialmente antes de activar los pagos reales para las suscripciones.",
    terms_s1_title:"1. Aceptación",
    terms_s1_body:"Al usar Oncer aceptas estos términos. Si no estás de acuerdo con ellos, te pedimos que no uses la aplicación.",
    terms_s2_title:"2. Descripción del servicio",
    terms_s2_body:"Oncer es una aplicación de diario fotográfico social: un contenido al día, recopilado en un libro anual, en micro-libros personales o en libros compartidos con otras personas, con funciones sociales, de personalización y la posibilidad de pedir la impresión física de tus libros.",
    terms_s3_title:"3. Cuentas y contenido",
    terms_s3_body:"Eres responsable del contenido que publicas. No está permitido subir contenido ilegal, ofensivo, o que vulnere derechos de terceros.",
    terms_s4_title:"4. Propiedad del contenido",
    terms_s4_body:"El contenido que creas sigue siendo tuyo. Oncer no reclama su propiedad.",
    terms_s5_title:"5. Firma de Oncer en las publicaciones compartidas",
    terms_s5_body:"Las imágenes compartidas fuera de la aplicación incluyen automáticamente y de forma permanente una referencia a la marca Oncer.",
    terms_s6_title:"6. Suscripciones",
    terms_s6_body:"Los planes de pago (Oncer+, Oncer Pro) ofrecen funciones adicionales descritas en la sección Suscripción. Actualmente no se procesa ningún pago real para las suscripciones — siguen siendo un adelanto de funciones futuras.",
    terms_s7_title:"7. Pedidos de impresión",
    terms_s7_body:"En Oncer puedes pedir la impresión física de tus libros (tapa dura, blanda o layflat, según los formatos disponibles). El pago lo procesa Stripe: nunca recibimos ni guardamos los datos de tu tarjeta. La producción y el envío corren a cargo de proveedores de impresión externos (Prodigi, Gelato), que reciben el archivo del libro y la dirección de envío necesarios para completar el pedido. Los tiempos de entrega varían según el proveedor y el país de destino. Una vez que un pedido se envía a producción, ya no se puede modificar ni cancelar desde la app: para errores, retrasos o consultas sobre un pedido concreto, escríbenos a hello@oncer.app.",
    terms_s8_title:"8. Limitación de responsabilidad",
    terms_s8_body:"Oncer se ofrece \"tal cual\", en fase beta. No garantizamos la ausencia de errores o interrupciones del servicio.",
    terms_s9_title:"9. Modificaciones",
    terms_s9_body:"Estos términos pueden actualizarse; los cambios relevantes se comunicarán en la aplicación.",
    terms_s10_title:"10. Contacto",
    terms_s10_body:"hello@oncer.app",
    // ── Batch 3: Library.jsx, SettingsShared.jsx ──────────────────────
    lib_header_title_mine:"📚 Libro {year}",
    lib_header_title_other:"📚 Libro {year} de @{username}",
    lib_recent_pages:"Últimas páginas",
    lib_page_label:"Pág. {day}",
    lib_today_badge:"hoy",
    lib_empty_title_mine:"El libro {year} está vacío",
    lib_empty_title_other:"@{username} todavía no tiene páginas",
    lib_empty_subtitle:"Haz tu primera foto para empezar.",
    lib_empty_cta:"✦ Capturar ahora",
    lib_print_cta:"🖨 Imprimir este libro",
    lib_page_detail_title:"Página {day} · {year}",
    lib_share_external:"Compartir fuera de Oncer",
    lib_permanent_notice:"🔒 Esta página es permanente y no se puede editar.",
    shared_claim_cta:"Reclamar ✦",
    shared_claimed_label:"✓ Conseguido",
    goal_unlocked_toast:"🎉 ¡Has desbloqueado un objetivo! Ve a Objetivos para reclamar {name}.",
    // ── Batch 4: Print.jsx (schermate stampa, non ancora raggiungibili) ────
    print_preview_header:"Vista previa de impresión",
    print_cover_stats:"{total} {pages} · {theme}",
    print_highlights_label:"Algunos momentos destacados",
    print_highlights_empty:"Todavía pocas páginas, pero ya son tuyas.",
    print_stat_microbook:"Micro-libro",
    print_stat_book:"Libro {year}",
    print_continue_cta:"Continuar a la impresión →",
    print_order_header:"Tu libro impreso",
    print_format_label:"Formato",
    print_format_cheaper:"más económico",
    print_selected:"✓ seleccionado",
    print_empty_pages_label:"Incluir también las páginas vacías",
    print_empty_pages_sub:"Imprime un libro completo de 365 páginas, con los días sin contenido en blanco — si no, imprimimos solo las páginas que realmente tienen algo dentro.",
    print_feature_cover:"Tapa dura con tu tema en relieve",
    print_feature_pages:"Cada página es la que escribiste tú — incluso los días vacíos",
    print_feature_delivery:"Entrega en {min}-{max} días laborables",
    print_feature_memory:"El recuerdo que un feed infinito nunca te dará",
    print_price_label:"Tu {year}, para siempre",
    print_price_fallback:"libro",
    print_price_note:"Envío incluido · Pago seguro",
    print_price_breakdown:"Libro €{book} + envío €{shipping}",
    print_order_cta:"Pide tu libro — €{price}",
    print_ordered_body:"Los pedidos reales todavía no están activos — estamos preparando la impresión de verdad. Tu libro se queda aquí, listo, para cuando llegue el momento.",
    print_pdf_test_label:"Prueba admin — PDF de impresión real",
    print_pdf_test_sub:"Genera el PDF listo para imprimir (con sangrado y área de seguridad) para subirlo a mano a un proveedor de impresión externo.",
    print_pdf_test_cta:"Generar PDF de impresión",
    print_pdf_test_busy:"Generando…",
    print_pdf_test_ready:"PDF listo — toca para abrirlo/descargarlo",
    print_pdf_test_error:"No se pudo generar el PDF. Inténtalo de nuevo en un momento.",
    print_format_test_badge:"Prueba Gelato",
    print_price_not_final_note:"Precio provisional, aún no evaluado para este formato — solo para la prueba de PDF.",
    print_hub_header:"¿Qué quieres imprimir?",
    print_hub_annual_section:"Libro del año",
    print_hub_micro_section:"Micro-libros",
    print_hub_empty_title:"Nada que imprimir todavía",
    print_hub_empty_sub:"Escribe algunas páginas en tu libro del año o en un micro-libro, luego vuelve aquí.",
    print_hub_status_done:"Completo",
    print_copies_label:"Número de copias",
    print_copies_sub:"Útil si más de una persona quiere su propia copia del mismo libro.",
    print_copies_shared_hint:"Compartido con {n} personas — quizás también quieran una copia.",
    print_copies_multiplier_note:"€{unit} × {n} copias",
    auth_signup0_birthdate_label:"Fecha de nacimiento",
    auth_birthdate_day:"Día",
    auth_birthdate_month:"Mes",
    auth_birthdate_year:"Año",
    auth_err_underage:"Debes tener al menos 18 años para usar Oncer.",
  },
  fr:{
    // ── Feed ──────────────────────────────────────────────────────────
    feed_page_singular:"page",
    feed_page_plural:"pages",
    feed_page_abbr:"p.",
    feed_from_followed_books:"📖 Des livres que tu suis",
    feed_followed_empty_1:"Tu verras ici les pages des micro-livres ouverts des personnes que tu suis.",
    feed_followed_empty_2:"Suis quelqu'un, ou attends qu'il publie quelque chose.",
    feed_share_external_title:"Partager en dehors d'Oncer",
    feed_share_watermark_note:"🔖 Chaque image partagée porte la signature Oncer — elle ne peut pas être retirée.",
    feed_share_preparing:"Préparation...",
    feed_share_photo_btn:"↗ Partager la photo",
    feed_share_no_native_note:"Ton navigateur ne peut pas ouvrir le panneau de partage avec la photo — les options ci-dessous partagent seulement un lien, sans image.",
    feed_share_or_choose:"Ou choisis",
    feed_share_save_image:"⬇ Enregistrer l'image",
    feed_share_ig_tiktok_note:"Pour Instagram et TikTok : enregistre l'image, puis publie-la comme Story ou Reel.",
    feed_share_message:"Ma page sur Oncer 🔖 Be a Oncer. — oncer.app",
    feed_native_share_text:"Be a Oncer. 🔖 oncer.app",
    feed_send_page_to:"Envoyer la page à...",
    feed_sending:"Envoi...",
    feed_follow_to_send:"Suis quelqu'un pour pouvoir envoyer des pages.",
    feed_share_label:"Partager",
    feed_send_label:"Envoyer",
    feed_you:"toi",
    feed_following:"abonnements",
    feed_find_your_style:"🎞 Trouve ton style",
    feed_discover_filters:"Découvre les filtres tendance ou crée le tien →",
    feed_no_photo_today:"Tu n'as pas encore pris ta photo aujourd'hui",
    feed_reminder_pre:"Tu avais choisi",
    feed_reminder_post:"comme rappel — appuie pour prendre la photo maintenant",
    feed_year_summary_cta:"Appuie pour voir le résumé de ton année →",
    feed_special_moment:"Un moment spécial pour prendre une photo aujourd'hui",
    feed_year_ago_today:"✨ Il y a un an, aujourd'hui",
    feed_toast_share_failed:"Je n'ai pas réussi à ouvrir le partage, réessaie.",
    feed_toast_reaction_remove_failed:"Je n'ai pas réussi à retirer la réaction, réessaie.",
    feed_toast_reaction_save_failed:"La réaction n'a pas été enregistrée, réessaie.",
    feed_toast_send_failed:"Je n'ai pas réussi à l'envoyer, réessaie.",
    // ── Capture ───────────────────────────────────────────────────────
    capture_title:"Capture",
    capture_already_done_title:"Tu as déjà pris ta photo du jour pour le livre annuel !",
    capture_already_done_sub1:"La page {day} de {year} est écrite.",
    capture_already_done_sub2:"Reviens demain pour la prochaine page du livre.",
    capture_shoot_for_micro:"📎 Prends quand même une photo pour un micro-livre",
    capture_add_another_to_event:"📸 Ajoute une autre photo à ce livre",
    capture_event_book_note:"Livre d'événement · photos illimitées aujourd'hui",
    capture_view_your_book:"Voir ton livre",
    capture_tap_to_shoot:"appuie pour prendre la photo",
    capture_think_twice_1:"🔒 Réfléchis bien : ce sera la photo choisie pour ta journée.",
    capture_think_twice_2:"Elle ne peut pas être supprimée. Elle reste pour toujours.",
    capture_write_thought_only:"Pas de photo aujourd'hui — écris juste une pensée",
    capture_where_to_save:"où veux-tu enregistrer cette photo ?",
    capture_words_only_suffix:"juste des mots, aujourd'hui",
    capture_which_font:"Quelle police ?",
    capture_drag_to_reframe:"fais glisser pour recadrer",
    capture_filter_label:"🎞 filtre",
    capture_browse_all:"Voir tous →",
    capture_sticker_label:"🎨 autocollant (facultatif)",
    capture_annual_book_label:"livre annuel",
    capture_already_done_today:"Déjà fait pour aujourd'hui",
    capture_only_micro_note:"cette photo ira uniquement dans le micro-livre choisi ci-dessous",
    capture_also_diary_label:"L'ajouter aussi à ton journal personnel ?",
    capture_also_diary_hint:"Si activé, elle comptera comme photo du jour et apparaîtra aussi dans le Feed.",
    capture_always_in_book:"sera toujours dans ton livre",
    capture_annual_book_year:"Livre annuel {year}",
    capture_permanent:"permanent",
    capture_choose_micro_required:"choisis un micro-livre (obligatoire)",
    capture_add_to_micro_optional:"l'ajouter aussi à un micro-livre ? (facultatif)",
    capture_micro_book_word:"micro-livre",
    capture_create_new_micro:"Créer un nouveau micro-livre",
    capture_micro_title_placeholder:"nom du micro-livre, ex. Voyage à Lisbonne",
    capture_cancel:"Annuler",
    capture_creating:"Création...",
    capture_create:"Créer",
    capture_add_to_shared_optional:"l'ajouter aussi à un livre partagé ? (facultatif)",
    capture_with_prefix:"avec",
    capture_your_thought_today:"✏️ ta pensée du jour",
    capture_add_a_caption:"ajoute une phrase",
    capture_thought_placeholder:"à quoi penses-tu aujourd'hui...",
    capture_caption_placeholder:"que restera-t-il d'aujourd'hui...",
    capture_thought_not_removable:"✦ Cette pensée ne pourra pas être supprimée.",
    capture_photo_not_removable:"✦ Cette photo ne pourra pas être supprimée.",
    capture_toast_micro_create_failed:"Je n'ai pas réussi à créer le micro-livre, réessaie.",
    capture_toast_choose_micro_first:"Choisis un micro-livre avant d'enregistrer.",
    capture_toast_already_today_reload:"Tu as déjà une page pour aujourd'hui — recharge pour la voir.",
    capture_toast_saved_local_not_uploaded:"Enregistrée sur le téléphone, pas mise en ligne — vérifie ta connexion.",
    capture_toast_micro_not_synced:"Je n'ai pas réussi à l'enregistrer — le micro-livre choisi n'est pas encore synchronisé en ligne. Réessaie bientôt.",
    capture_toast_already_today_local_only:"Tu as déjà une page pour aujourd'hui — celle-ci est enregistrée seulement sur le téléphone.",
    capture_toast_saved_local_not_synced:"Enregistrée sur le téléphone, mais pas synchronisée en ligne. Vérifie ta connexion.",
    capture_toast_shared_publish_failed:"Je n'ai pas réussi à la publier dans le livre partagé.",
    capture_toast_camera_failed:"Je n'ai pas réussi à ouvrir l'appareil photo — vérifie les autorisations de l'application.",
    capture_saving:"Enregistrement...",
    capture_write_something_first:"Écris quelque chose avant d'enregistrer",
    capture_choose_micro_before_save:"Choisis un micro-livre avant d'enregistrer",
    capture_save_in_micro:"Enregistrer dans le micro-livre",
    capture_save_in_page:"Enregistrer dans la page {day}",
    capture_page_published:"Page {day} publiée !",
    capture_tell_world:"Fais savoir au monde que tu es un Oncer — partage aussi ce moment en dehors de l'application.",
    capture_share_now:"↗ Partager maintenant",
    capture_continue_in_app:"Continuer dans l'application",
    capture_kind_photo:"photo",
    capture_blocked_title:"Cette {kind} ne peut pas être publiée",
    capture_blocked_desc:"Notre contrôle automatique l'a signalée comme contenu potentiellement violent ou fort. Si tu penses que c'est une erreur, écris-nous — nous la vérifierons en personne.",
    capture_write_to_us:"Écris-nous, je pense que c'est une erreur",
    capture_got_it:"Compris",
    capture_blocked_email_subject:"Photo bloquée par erreur",
    // ── Navigation (barre du bas) ─────────────────────────────────────
    nav_feed:"feed",
    nav_capture:"capture",
    nav_micro:"micro",
    nav_notebook:"notebook",
    nav_discover:"découvre",
    nav_profile:"profil",
    // ── Thèmes et catégories ──────────────────────────────────────────
    theme_neon:"Neon Nights", theme_pastel:"Soft Hours", theme_retro:"Années 90", theme_clean:"Pure",
    theme_concrete:"Concrete", theme_graffiti:"Graffiti", theme_subway:"Subway",
    theme_vinyl:"Vinyl", theme_rave:"Rave", theme_jazzclub:"Jazz Club",
    theme_noir:"Noir", theme_coldcase:"Cold Case",
    theme_industrial:"Industrial", theme_darkroom:"Darkroom",
    theme_sakura:"Sakura", theme_zen:"Zen", theme_akiba:"Akihabara",
    theme_redgold:"Red & Gold", theme_ink:"Chinese Ink", theme_jade:"Jade Palace",
    theme_aurora:"Aurora", theme_ambra:"Ambre",
    cat_originali:"Originaux", cat_urban:"Urban", cat_musica:"Musique",
    cat_poliziesco:"Polar", cat_underground:"Underground",
    cat_japan:"Japon", cat_chinese:"Chinois", cat_aggiuntivi:"Bonus",
    // ── Préréglages micro-livre ──────────────────────────────────────
    mp_vacation:"Vacances", mp_trip:"Voyage", mp_festival:"Festival", mp_wedding:"Mariage",
    mp_birthday:"Anniversaire", mp_road:"Road Trip", mp_concert:"Concert", mp_custom:"Personnalisé",
    // ── Micro-livres ─────────────────────────────────────────────────
    micro_page_singular:"page", micro_page_plural:"pages",
    micro_with_prefix:"avec {list}",
    micro_loading_pages:"Chargement des pages...",
    micro_load_error_title:"Impossible de charger les pages",
    micro_no_pages_title:"Encore aucune page",
    micro_no_pages_sub:"Prends la première photo pour commencer.",
    micro_shoot_now:"✦ Prendre une photo",
    micro_all_pages:"Toutes les pages",
    micro_the_pages:"Pages",
    micro_shoot_today:"📷 Prendre une photo aujourd'hui",
    micro_print_book:"🖨 Imprimer le micro-livre",
    micro_share_outside:"Partager en dehors d'Oncer",
    micro_page_locked_note:"🔒 Cette page est permanente et ne peut pas être modifiée.",
    micro_header_title:"📎 Micro-livres",
    micro_new_btn:"+ Nouveau",
    micro_invite_msg:"t'a invité à un livre partagé",
    micro_accept:"Accepter",
    micro_decline:"Refuser",
    micro_accept_failed:"Impossible d'accepter, réessaie.",
    micro_decline_failed:"Impossible de refuser, réessaie.",
    micro_what_are_title:"Que sont les micro-livres ?",
    micro_what_are_desc:"Des livres pour des moments précis — vacances, voyages, fêtes. Aucune limite de pages, imprimables séparément.",
    micro_in_progress:"Micro-livres personnels",
    micro_photos:"photos",
    micro_add_pill:"✦ Ajouter",
    micro_planned_progress:"{pages}/{max} photos prévues",
    micro_shared_books:"Livres partagés",
    micro_pag_short:"p.",
    micro_archived:"Archivés",
    micro_empty_title:"Aucun micro-livre",
    micro_empty_sub:"Crée le premier pour un moment spécial.",
    micro_create_btn:"+ Créer un micro-livre",
    micro_customize_title:"✏️ Personnaliser",
    micro_what_moment:"Quel est le moment ?",
    micro_title_label:"Titre",
    micro_title_placeholder:"ex. Sardaigne été, Tokyo mars…",
    micro_who_writes:"Qui l'écrit ?",
    micro_only_me:"Moi seul(e)",
    micro_shared_kind:"👥 Partagé",
    micro_kind_timed:"📎 Limité dans le temps",
    micro_kind_family:"👨‍👩‍👧‍👦 Famille, sans fin",
    micro_kind_evento:"💍 Événement",
    micro_event_type_note:"Les invités rejoignent via QR code/lien, sans avoir besoin d'avoir déjà l'app ou un compte Oncer.",
    micro_event_limit_title:"Limite de photos et prix",
    micro_event_limit_sub:"Choisissez combien de photos inclure : le prix d'impression s'affiche immédiatement, sans surprise.",
    micro_event_limit_nolimit:"Sans limite",
    micro_event_limit_nolimit_sub:"calculé à la fin de l'événement",
    micro_event_limit_price_loading:"Calcul…",
    micro_event_limit_price_unavailable:"Prix indisponible pour le moment",
    micro_event_progress_label:"photos collectées",
    micro_event_fairuse_reminder:"La limite est presque atteinte : rappelez aux invités de laisser de la place aux autres.",
    micro_closed_badge:"clôturé",
    micro_event_invite_title:"QR code et lien pour les invités",
    micro_copy_link:"Copier le lien",
    micro_link_copied:"Copié ✓",
    micro_close_event_btn:"Clôturer le livre de l'événement",
    micro_closing:"Clôture en cours…",
    micro_delete_btn:"Supprimer le micro-livre",
    micro_delete_shared_btn:"Supprimer le livre partagé",
    micro_delete_confirm:"Supprimer ce micro-livre ? Toutes les photos qu'il contient seront supprimées. Action irréversible.",
    micro_delete_shared_confirm:"Supprimer ce livre partagé ? Il sera supprimé pour tous les participants, qui recevront une notification. Action irréversible.",
    micro_delete_success:"Micro-livre supprimé",
    micro_delete_shared_success:"Livre partagé supprimé",
    micro_delete_failed:"Suppression échouée, réessaie",
    micro_deleting:"Suppression en cours…",
    micro_close_event_confirm:"Clôturer ce livre ? Ensuite, plus personne ne pourra ajouter de photos, et chaque participant recevra un e-mail avec le livre terminé.",
    micro_close_event_success:"Livre clôturé — tout le monde a été prévenu par e-mail.",
    micro_close_event_failed:"Nous n'avons pas réussi à clôturer le livre, réessaie.",
    micro_who_else:"Qui d'autre en fait partie ?",
    micro_search_username:"Rechercher un nom d'utilisateur...",
    micro_cover_theme:"Thème de couverture",
    micro_creating:"Création...",
    micro_create_cta:"Créer le micro-livre →",
    micro_toast_need_login:"Tu dois être connecté pour créer un livre partagé.",
    micro_toast_create_failed:"Impossible de créer le livre partagé, réessaie.",
    micro_toast_local_only:"Créé uniquement sur ton téléphone, non synchronisé en ligne.",
    // ── Styles d'écriture Notebook ────────────────────────────────────
    nbs_cursive_name:"Cursive", nbs_cursive_desc:"Élégant et fluide",
    nbs_print_name:"Script", nbs_print_desc:"Clair et ordonné",
    nbs_marker_name:"Feutre", nbs_marker_desc:"Franc et affirmé",
    nbs_romantic_name:"Romantique", nbs_romantic_desc:"Doux et poétique",
    nbs_retro_name:"Machine", nbs_retro_desc:"Style machine à écrire",
    nbs_minimal_name:"Minimal", nbs_minimal_desc:"Simple et personnel",
    // ── Types de papier Notebook ─────────────────────────────────────
    nbp_ivory_name:"Ivoire", nbp_white_name:"Blanc", nbp_kraft_name:"Kraft",
    nbp_night_name:"Nuit", nbp_sage_name:"Sauge",
    // ── Notebook ─────────────────────────────────────────────────────
    nb_time_today:"aujourd'hui",
    nb_writes_in:"écrit en {style}",
    nb_page_label:"📄 page {day}",
    nb_placeholder:"écris ici...",
    nb_your_writing:"✏️ Ton écriture",
    nb_paper_type:"Type de papier",
    nb_sample_text:"texte d'exemple...",
    nb_sample_greeting:"Salut, comment vas-tu aujourd'hui ?",
    nb_blocked_title:"Ce message ne peut pas être envoyé",
    nb_blocked_desc:"Notre contrôle automatique l'a signalé comme discours de haine potentiel. Si tu penses que c'est une erreur, écris-nous — on le vérifie en personne.",
    nb_blocked_contact:"Écris-nous, je pense que c'est une erreur",
    nb_blocked_dismiss:"J'ai compris",
    nb_toast_send_failed:"Message non envoyé, réessaie.",
    nb_opening_chat:"Ouverture de la conversation...",
    nb_empty_title:"Aucune conversation",
    nb_empty_sub:"Cherche un Oncer et commence à écrire.",
    nb_new_conversation:"+ Nouvelle conversation",
    nb_new_conversation_title:"Nouvelle conversation",
    nb_no_messages_yet:"Aucun message pour l'instant — écris le premier",
    nb_you_prefix:"toi : ",
    nb_shared_page_preview:"📄 page partagée — p. {day}",
    nb_search_placeholder:"Chercher un Oncer...",
    nb_already_chatting:"déjà en discussion",
    nb_searching:"Recherche...",
    nb_no_users_found:"Aucun Oncer trouvé.",
    nb_toast_profile_not_found:"Impossible de trouver ce profil.",
    nb_toast_open_failed:"Impossible d'ouvrir la conversation.",
    nb_delete_action:"Supprimer",
    nb_toast_delete_failed:"Impossible de supprimer la conversation, réessaie.",
    // ── Conversation guide "Oncer Experience" ────────────────────────
    nb_exp_msg1:"Salut, bienvenue sur Oncer 👋 Je suis là pour te montrer ce que tu peux faire, sans te presser — lis quand tu veux, cette conversation reste ici pour toujours.",
    nb_exp_msg2:"L'idée de base est simple : une photo par jour, pour toujours. Elle ne se supprime pas, ne se modifie pas — c'est la page du jour de ton livre de l'année. Si un jour tu préfères les mots à une photo, tu peux écrire juste une pensée, en choisissant aussi la police que tu préfères.",
    nb_exp_msg3:"📚 Ta Bibliothèque rassemble toutes les pages, jour après jour, depuis le 1er janvier. Même les jours où tu n'as rien pris ont leur propre petit dessin — aucune page ne reste jamais vraiment vide.",
    nb_exp_msg4:"📎 Les micro-livres sont des collections plus petites au sein de ton univers — un voyage, des vacances, un projet. Ils viennent s'ajouter au livre principal, pas le remplacer : tu peux mettre la même photo dans les deux à la fois. Tu peux aussi en créer un avec un ami ou toute la famille, pour y prendre des photos tous ensemble.",
    nb_exp_msg5:"🎯 Dans Objectifs, tu trouveras de petits défis saisonniers — en les complétant, tu débloques des récompenses : badges pour le profil, autocollants pour les photos, et bien plus.",
    nb_exp_msg6:"⏳ Oncer Future, c'est pour les messages que tu écris aujourd'hui et qui arrivent plus tard — à toi-même dans un an, à un ami, à ta famille. Ils ne se débloquent qu'à la date que tu choisis.",
    nb_exp_msg7:"✏️ Le Notebook — celui-ci, où nous sommes maintenant — c'est là que tu écris à tes amis, comme un vrai carnet : tu peux choisir le papier et l'écriture pour chaque conversation, pour la rendre tienne.",
    nb_exp_msg8:"🎞 Dans l'écran Filtres, tu trouveras les plus populaires, mais tu peux aussi créer le tien et le rendre public pour toute la communauté.",
    nb_exp_msg9:"Une dernière chose : ta bibliothèque (l'étagère avec tous tes livres) peut être réorganisée en faisant glisser les dos des livres — arrange-la comme tu préfères.",
    nb_exp_msg10:"🖨 Tu verras aussi un bouton pour imprimer tes livres — il arrive bientôt : tu pourras bientôt transformer une année entière en un vrai objet, à tenir dans tes mains.",
    nb_exp_msg11:"Voilà tout ce qu'il faut savoir pour commencer. Le reste, tu le découvriras en photographiant — un jour à la fois. Be a Oncer. 🔖",
    // ── Découvre ─────────────────────────────────────────────────────
    disc_header_title:"🔭 Découvre",
    disc_your_library:"Ta bibliothèque",
    disc_followers_needed:"encore {n} abonnés pour débloquer",
    disc_follower_count:"{n} abonnés",
    disc_to_unlock:"{n} pour débloquer",
    disc_tab_people:"👤 Personnes",
    disc_tab_libraries:"📖 Bibliothèques",
    disc_tab_themes:"🎨 Thèmes",
    disc_tab_micro:"📎 Micro-livres",
    disc_tab_filters:"🎞 Filtres",
    disc_search_placeholder:"Cherche un Oncer par nom d'utilisateur...",
    disc_search_hint:"Cherche un nom d'utilisateur pour trouver d'autres Oncers et les suivre.",
    disc_searching:"Recherche...",
    disc_no_people_found:"Aucun Oncer trouvé avec ce nom d'utilisateur.",
    disc_loading:"Chargement...",
    disc_no_libraries:"Aucun autre Oncer à montrer pour l'instant.",
    disc_verified_badge:"📖 Vérifié",
    disc_follow_btn:"+ Suivre",
    disc_libraries_count:"{n} bibliothèques",
    disc_exclusive_title:"✨ Déblocables uniquement ici",
    disc_unlocked_hint:"Débloqué — choisis-le dans Réglages",
    disc_tap_to_unlock:"Touche pour débloquer",
    disc_no_micro:"Aucun micro-livre terminé à montrer pour l'instant.",
    disc_micro_by:"par @{u} · {pages} pages",
    disc_filters_hero_title:"🎞 Trouve ton style",
    disc_filters_hero_desc:"Chaque filtre raconte qui tu es. Parcours la galerie, crée le tien, fais-toi découvrir par d'autres Oncers.",
    disc_filters_open_gallery:"Ouvrir la galerie →",
    disc_filters_trending_title:"🔥 Tendance ce mois-ci",
    disc_filter_uses:"{n} utilisations",
    // ── Profil ───────────────────────────────────────────────────────
    prof_add_bio:"Ajouter une bio →",
    prof_streak_badge_title:"Série de {n} jours avec tous les objectifs quotidiens complétés",
    prof_streak_badge_off_title:"Termine tous les objectifs du jour pour allumer la flamme",
    prof_loading_profile:"Chargement du profil...",
    prof_open_library_badge:"📖 Bibliothèque Ouverte",
    prof_stat_followers:"abonnés",
    prof_stat_following:"abonnements",
    prof_stat_books:"livres",
    prof_following_already:"✓ Déjà suivi",
    prof_request_sent:"Demande envoyée",
    prof_goals_label:"Objectifs",
    prof_annual_books_label:"livres annuels",
    prof_private_library_title:"Bibliothèque privée",
    prof_private_desc1:"@{username} n'a pas encore de bibliothèque publique.",
    prof_private_desc2:"Suis-le pour en voir plus quand il l'ouvrira.",
    prof_toast_generic_fail:"Impossible d'y arriver, réessaie dans un instant.",
    prof_book_meta:"{theme} · {pages} pages",
    prof_in_progress_badge:"EN COURS",
    prof_micro_books_label:"micro-livres",
    prof_micro_meta:"📎 micro-livre · {pages} pages",
    prof_shared_books_label:"livres partagés",
    prof_pages_count:"{n} pages",
    // ── Noms et catégories de filtres ────────────────────────────────
    filtname_none:"Original", filtname_golden:"Golden Hour", filtname_noir:"Cinema Noir",
    filtname_dream:"Dreamy", filtname_vivid:"Vivid Pop", filtname_vintage83:"'83 Vintage",
    filtname_moody:"Moody Blue", filtname_clean:"Clean White",
    filtname_c_sofia1:"Pétale", filtname_c_luca1:"Afterhours", filtname_c_marco1:"Détective",
    filtname_ft_solstice:"Solstice", filtname_ft_frost:"Givre",
    filtname_os_portra:"Portra Chaude", filtname_os_ektar:"Ektar Vif",
    filtname_os_superia:"Superia Vert", filtname_os_velvia:"Velvia Paysage",
    filtname_os_gold:"Gold Nostalgia", filtname_os_polaroid:"Polaroid Délavé",
    filtname_os_ektachrome:"Ektachrome Rêve", filtname_os_agfa:"Agfa Europe",
    filtname_os_trix:"Tri-X Contraste", filtname_os_pro160s:"Pro160S Doux",
    filtcat_setup:"Oncer Setup", filtcat_trend:"Tendance", filtcat_vintage:"Vintage",
    filtcat_bn:"N&B", filtcat_vivid:"Vif", filtcat_pastel:"Pastel",
    filtcat_cinema:"Cinema", filtcat_community:"Community",
    // ── Écran Filtres ────────────────────────────────────────────────
    filt_header_title:"🎞 Filtres",
    filt_create_btn:"+ Créer",
    filt_hero_title:"✨ Trouve ton style distinctif",
    filt_hero_desc:"Chaque Oncer a une façon différente de raconter ses journées. Crée ton propre filtre et fais-toi découvrir par toute la communauté.",
    filt_pick_hint:"Choisis un filtre à appliquer à ta photo du jour.",
    filt_search_placeholder:"Cherche un filtre ou un créateur...",
    filt_cat_all:"Tous",
    filt_results_count:"Résultats ({n})",
    filt_all_filters:"Tous les filtres",
    filt_by_creator:"par @{creator}",
    filt_use_this:"Utiliser celui-ci →",
    filt_seasonal_goal_fallback:"Objectif saisonnier",
    filt_none_found:"Aucun filtre trouvé.",
    filt_create_title:"Crée ton filtre",
    filt_name_label:"Nom du filtre",
    filt_name_placeholder:"ex. Nostalgie, Sunset Mood...",
    filt_base_label:"Base",
    filt_slider_saturation:"🎨 Saturation",
    filt_slider_contrast:"◐ Contraste",
    filt_slider_brightness:"☀️ Luminosité",
    filt_slider_warmth:"🌡️ Chaleur",
    filt_warmth_warm:"+{v} chaud",
    filt_warmth_cold:"{v} froid",
    filt_warmth_neutral:"neutre",
    filt_advanced_label:"Avancé",
    filt_slider_hue:"🌈 Teinte",
    filt_slider_sepia:"📜 Sépia",
    filt_slider_vignette:"🖤 Vignette",
    filt_slider_grain:"🎞 Grain",
    filt_absent:"aucun",
    filt_toast_local_only:"Créé uniquement sur ton téléphone, non partagé avec la communauté.",
    filt_publishing:"Publication...",
    filt_publish_btn:"Publier le filtre",
    filt_published_title:"Filtre publié !",
    filt_published_desc:"\"{name}\" est désormais visible par toute la communauté Oncer. À toi de le faire découvrir.",
    filt_view_gallery_btn:"Voir dans la galerie",
    // ── Clôture de l'année ───────────────────────────────────────────
    yc_title:"Ton {year} est sur le point de se clore",
    yc_subtitle:"Une année de photos, un jour à la fois. Voici ce que tu as construit.",
    yc_stat_pages:"pages écrites",
    yc_stat_streak:"plus longue série",
    yc_stat_top_month:"ton mois le plus actif",
    yc_stat_filters:"filtres différents utilisés",
    yc_print_btn:"🖨 Imprimer ton livre {year}",
    yc_footer_note:"{nextYear} commence tout seul, à ta prochaine page — aucun bouton à presser. Le livre de cette année reste pour toujours tel qu'il est.",
    // ── Oncer Future ─────────────────────────────────────────────────
    of_tab_write:"Écrire",
    of_tab_received:"Reçus",
    of_write_intro:"Écris quelque chose aujourd'hui. Tu ne le liras pas (ni la personne que tu choisis) tant que la date choisie n'est pas arrivée — à toi-même dans le futur, à un ami, ou à un souvenir de famille.",
    of_recipient_label:"C'est pour qui ?",
    of_recipient_self:"Toi-même",
    of_recipient_friend:"Un ami",
    of_recipient_family:"Famille",
    of_family_placeholder:"Pour qui dans la famille ? (facultatif, ex. 'pour maman')",
    of_unlock_when_label:"Quand se débloque-t-il ?",
    of_unlock_6m:"Dans 6 mois",
    of_unlock_1y:"Dans 1 an",
    of_unlock_nextnewyear:"Au prochain 1er janvier",
    of_unlock_custom:"Choisir une date",
    of_content_placeholder:"Écris ici...",
    of_sending:"Envoi...",
    of_send_btn:"Sceller et envoyer vers le futur",
    of_toast_send_failed:"Impossible de l'envoyer, réessaie.",
    of_unlocked_label:"Débloqués",
    of_written_by_you:"✍️ Écrit par toi",
    of_from_user:"💌 De @{username}",
    of_in_transit_label:"En route vers le futur",
    of_unlocks_on:"Se débloque le {date}",
    of_empty_state:"Tu n'as encore rien écrit pour le futur.",
    // ── Écran Objectifs ──────────────────────────────────────────────
    goals_header_title:"🎯 Objectifs",
    goals_rewards_link:"Récompenses →",
    goals_season_banner:"{icon} Saison : {name}",
    goals_event_banner:"✨ {icon} C'est le moment de {event} ! Objectifs spéciaux disponibles.",
    goals_season_default_banner:"Complète les objectifs trimestriels pour débloquer des récompenses à thème.",
    goals_section_today:"Aujourd'hui",
    goals_daily_subtitle:"Changent chaque jour",
    goals_daily_all_done:"Journée complète ✨",
    goals_daily_streak_toast:"🔥 Journée parfaite ! Série de {n} jours",
    goals_section_month:"Ce mois-ci",
    goals_section_season:"Objectifs saisonniers",
    goals_footer_note:"De nouveaux objectifs et récompenses arrivent à chaque changement de saison — hiver, printemps, été, automne — avec des éditions spéciales pour Pâques, Thanksgiving et Noël.",
    goals_claimed_title:"Tu as débloqué : {name}",
    goals_claim_sticker:"Disponible dès que tu prends une nouvelle page.",
    goals_claim_badge:"Désormais visible sur ton profil.",
    goals_claim_divider:"Tu peux l'équiper sur ton étagère depuis l'inventaire.",
    goals_claim_filter:"Nouveau filtre exclusif dans la galerie.",
    goals_claim_cta:"Génial !",
    // ── Écran Récompenses ────────────────────────────────────────────
    rewards_header_title:"🎁 Tes récompenses",
    rewards_unlocked_count:"{n} sur {total} débloquées",
    rewards_cat_all:"Tous",
    rewards_cat_sticker:"🎨 Autocollants",
    rewards_cat_badge:"🎖️ Badges",
    rewards_cat_divider:"📚 Serre-livres",
    rewards_cat_filter:"🎞 Filtres",
    reward_usage_sticker:"S'utilise quand tu prends une photo",
    reward_usage_badge:"Apparaît sur ton profil",
    reward_usage_divider:"S'utilise sur l'étagère de ta bibliothèque",
    reward_usage_filter:"S'utilise dans l'appareil photo",
    rewards_equip_title:"Équiper sur l'étagère (2 emplacements)",
    rewards_slot_label:"Emplacement {n}",
    rewards_none_option:"— aucun —",
    rewards_empty_state:"Aucune récompense ici pour l'instant — complète les objectifs pour les débloquer.",
    rewards_still_locked_label:"Encore à débloquer",
    // ── Objectifs quotidiens/mensuels/trimestriels ────────────────────
    goal_d_post:"Prends la photo du jour",
    goal_d_caption:"Écris une légende aujourd'hui",
    goal_d_react1:"Réagis à au moins 1 page",
    goal_d_react2:"Réagis à 2 pages d'amis",
    goal_d_react3:"Réagis à 3 pages d'amis",
    goal_d_filter:"Utilise un filtre aujourd'hui",
    goal_d_sticker:"Ajoute un autocollant aujourd'hui",
    goal_d_caption_long:"Écris une légende plus longue (20+ caractères)",
    goal_d_combo:"Complète la page avec une légende et un autocollant",
    goal_d_early:"Publie ta page avant midi",
    goal_m_streak7:"Série de 7 jours consécutifs",
    goal_m_pages15:"15 pages ce mois-ci",
    goal_m_filters3:"Utilise 3 filtres différents",
    goal_q1_pages60:"60 pages cet hiver",
    goal_q1_easter:"Prends une photo pendant la semaine de Pâques",
    goal_q1_streak14:"Série de 14 jours consécutifs",
    goal_q1_shared:"Fais partie d'un livre partagé",
    goal_q1_sticker:"Utilise un autocollant sur une page",
    goal_q2_pages60:"60 pages ce printemps",
    goal_q2_micro:"Crée un micro-livre",
    goal_q2_streak14:"Série de 14 jours consécutifs",
    goal_q2_filters5:"Utilise 5 filtres différents",
    goal_q2_ownfilter:"Crée et publie ton propre filtre",
    goal_q3_pages60:"60 pages cet été",
    goal_q3_micro3:"3 micro-livres au total",
    goal_q3_streak21:"Série de 21 jours consécutifs",
    goal_q3_shared2:"Fais partie de 2 livres partagés",
    goal_q3_ownfilter2:"Crée 2 filtres à toi",
    goal_q4_pages60:"60 pages cet automne",
    goal_q4_thanksgiving:"Prends une photo le jour de Thanksgiving",
    goal_q4_christmas:"Prends une photo le jour de Noël",
    goal_q4_streak30:"Série de 30 jours consécutifs",
    goal_q4_micro5:"5 micro-livres au total",
    // ── Noms des récompenses ───────────────────────────────────────────
    reward_sk_fire:"Flamme", reward_sk_star:"Étoile", reward_sk_heart:"Cœur d'or",
    reward_sk_sparkle:"Étincelle", reward_sk_snow:"Flocon de neige", reward_sk_egg:"Œuf de Pâques",
    reward_sk_turkey:"Dinde", reward_sk_tree:"Sapin de Noël", reward_sk_flower:"Fleur",
    reward_bd_streak7:"Semaine Pleine", reward_bd_streak30:"Mois de Fer",
    reward_bd_pages100:"Cent Pages", reward_bd_explorer:"Explorateur de Filtres",
    reward_bd_yearfull:"Année Pleine",
    reward_dv_owl:"Hibou Sage", reward_dv_plant:"Pot Fleuri",
    reward_dv_lamp:"Lampe Chaude", reward_dv_gold:"Trophée Doré",
    reward_sk_family:"Famille", reward_sk_camera:"Objectif",
    reward_bd_social:"Cercle Proche", reward_dv_book:"Livre Ouvert",
    // ── Saisons et événements ──────────────────────────────────────────
    season_winter:"Hiver", season_spring:"Printemps", season_summer:"Été", season_autumn:"Automne",
    event_easter:"Pâques", event_thanksgiving_christmas:"Thanksgiving et Noël",
    // ── Impostazioni ──────────────────────────────────────────────────
    set_header_title:"Paramètres",
    set_avatar_uploading:"Téléversement...",
    set_avatar_hint:"Touche pour changer ta photo de profil",
    set_cover_change:"Changer la couverture",
    set_cover_add:"Ajouter une couverture",
    set_sec_account:"Compte",
    set_sec_notifications:"Notifications",
    set_sec_privacy:"Confidentialité",
    set_sec_data:"Tes données",
    set_sec_customization:"Personnalisation",
    set_sec_subscription:"Abonnement",
    set_sec_style:"Style",
    set_sec_print:"Impression",
    set_sec_admin:"Administration",
    set_sec_info:"Infos",
    set_row_username:"Nom d'utilisateur",
    set_row_bio:"Bio",
    set_bio_empty:"Aucune bio définie",
    set_bio_cancel:"Annuler",
    set_bio_edit:"Modifier",
    set_bio_placeholder:"Écris quelque chose sur toi...",
    set_bio_save:"Enregistrer",
    set_row_email:"E-mail",
    set_row_change_password:"Changer le mot de passe",
    set_row_daily_notif:"Rappel quotidien",
    set_daily_notif_sub:"Chaque jour à {time}",
    set_row_push_notif:"Notifications push",
    set_row_email_notif:"Notifications par e-mail",
    set_row_library_public:"Bibliothèque publique",
    set_library_public_sub:"Visible par tous à partir de 1 000 abonnés",
    set_row_who_message:"Qui peut m'écrire",
    set_who_everyone:"Tout le monde",
    set_who_following:"Seulement les personnes que je suis",
    set_row_blocked_users:"Utilisateurs bloqués",
    set_blocked_none:"Aucun",
    set_soon_badge:"Bientôt",
    set_row_export:"Exporter tes données",
    set_export_preparing:"Préparation en cours...",
    set_export_sub:"Télécharge une copie à jour de tout ce que tu as créé",
    set_row_data_location:"Où vivent tes données",
    set_data_location_sub:"Sur les serveurs de Supabase, protégées et jamais vendues à des tiers",
    set_row_theme:"Thème de la bibliothèque",
    set_row_edit_library:"Modifier ta bibliothèque",
    set_edit_library_sub:"Réorganise les livres, place des badges et des séparateurs",
    set_row_nb_style:"Style d'écriture du Notebook",
    set_row_nb_paper:"Type de papier du Notebook",
    set_row_plan:"Ton abonnement",
    set_plan_sub:"{plan} · Gérer ou passer à un niveau supérieur",
    set_row_filters:"Galerie de filtres",
    set_filters_sub:"Découvre, crée et fais-toi découvrir avec tes filtres",
    set_row_print:"Imprime tes livres",
    set_print_sub:"Transforme une année en objet physique",
    set_row_moderation:"Contenu bloqué",
    set_moderation_sub:"Journal des photos et messages bloqués par la modération",
    set_row_terms:"Conditions générales",
    set_row_privacy_policy:"Politique de confidentialité",
    set_row_feedback:"Envoyer un commentaire",
    set_row_version:"Version",
    set_row_logout:"Se déconnecter",
    set_row_delete_account:"Supprimer le compte",
    set_logout_confirm:"Se déconnecter de ton compte Oncer ?",
    set_delete_confirm:"Pour supprimer définitivement ton compte et toutes tes données de la base de données, écris à hello@oncer.app depuis l'adresse avec laquelle tu t'es inscrit(e) — nous nous en occupons sous 48 heures, comme le prévoit le droit à l'effacement.\n\nVeux-tu en attendant te déconnecter et effacer les données enregistrées sur cet appareil ?",
    set_time_sheet_title:"Quand veux-tu qu'on te le rappelle ?",
    set_theme_sheet_title:"🎨 Thème de la bibliothèque",
    set_theme_locked_hint:"Tu n'as encore débloqué aucun thème supplémentaire — va dans Découvrir, section « Thèmes », pour les trouver.",
    set_style_sheet_title:"✏️ Style d'écriture du Notebook",
    set_style_sample:"Salut, comment ça va ?",
    set_paper_sheet_title:"📄 Type de papier du Notebook",
    set_pw_sheet_title:"🔑 Changer le mot de passe",
    set_pw_new_label:"Nouveau mot de passe",
    set_pw_new_placeholder:"au moins 6 caractères",
    set_pw_repeat_label:"Répète le nouveau mot de passe",
    set_pw_repeat_placeholder:"répète",
    set_pw_err_short:"Au moins 6 caractères.",
    set_pw_err_mismatch:"Les deux mots de passe ne correspondent pas.",
    set_pw_save:"Enregistrer le nouveau mot de passe",
    set_pw_saving:"Enregistrement...",
    set_toast_bio_offline:"Bio enregistrée uniquement sur ton téléphone, pas en ligne — vérifie ta connexion.",
    set_toast_pw_updated:"Mot de passe mis à jour ✓",
    set_toast_avatar_save_fail:"Impossible d'enregistrer ta photo, réessaie.",
    set_toast_avatar_upload_fail:"Impossible de téléverser ta photo, réessaie.",
    set_toast_cover_save_fail:"Impossible d'enregistrer ta couverture, réessaie.",
    set_toast_cover_upload_fail:"Impossible de téléverser ta couverture, réessaie.",
    // ── Batch 2: Editor scaffale, Legal, Abbonamento, Accesso, Notifiche, Moderazione, Tour, App ──
    shelf_header_title:"🛠 Modifier ta bibliothèque",
    shelf_instructions:"Fais glisser les livres pour les réorganiser. Fais glisser les badges et les séparateurs n'importe où sur l'étagère, même sur les bords. Ici rien ne s'ouvre par erreur — tu peux modifier tranquillement.",
    shelf_empty_decor:"Tu n'as encore débloqué aucun badge ni séparateur — ils se débloquent en complétant des objectifs saisonniers.",
    shelf_decor_section_title:"Tes badges et séparateurs",
    shelf_decor_equipped:"Sur l'étagère",
    shelf_decor_tap_to_add:"Touche pour ajouter",
    sub_header_title:"Abonnement",
    sub_title:"Choisis ton Oncer",
    sub_subtitle:"Plus de thèmes, plus de livres, plus de façons de ne jamais oublier.",
    sub_current_badge:"Abonnement actuel",
    sub_btn_active:"Abonnement actif",
    sub_btn_switch_free:"Passer à Free",
    sub_btn_activate:"Activer {plan}",
    sub_footer_note:"Tu peux annuler ou changer d'abonnement à tout moment.",
    sub_coming_title:"Bientôt disponible",
    sub_coming_body:"Nous préparons les paiements — {plan} sera bientôt activable. Nous te préviendrons dès que ce sera prêt.",
    sub_coming_ok:"D'accord",
    plan_period_free:"pour toujours",
    plan_period_month:"/mois",
    plan_badge_recommended:"Recommandé",
    plan_free_f1:"1 photo par jour",
    plan_free_f2:"Livre annuel illimité",
    plan_free_f3:"5 thèmes de base",
    plan_free_f4:"1 micro-livre actif",
    plan_free_f5:"2 styles d'écriture Notebook",
    plan_free_f6:"2 types de papier",
    plan_plus_f1:"Les 20 thèmes",
    plan_plus_f2:"Micro-livres illimités",
    plan_plus_f3:"Les 6 styles d'écriture",
    plan_plus_f4:"Les 5 papiers du Notebook",
    plan_plus_f5:"Stickers personnalisés",
    plan_plus_f6:"Statistiques annuelles",
    plan_plus_f7:"Badge exclusif sur le profil",
    plan_pro_f1:"Tout Oncer+",
    plan_pro_f2:"Export PDF haute qualité illimité",
    plan_pro_f3:"20% de réduction sur chaque impression",
    plan_pro_f4:"Accès anticipé aux thèmes saisonniers",
    plan_pro_f5:"Badge Pro exclusif",
    plan_pro_f6:"Support prioritaire",
    notif_header_title:"Notifications",
    notif_empty_title:"Aucune notification",
    notif_empty_body:"Quand quelque chose se passe, tu le trouves ici.",
    notif_accept:"Accepter",
    notif_decline:"Refuser",
    notif_action_failed:"Je n'y suis pas arrivé, réessaie.",
    notif_time_now:"à l'instant",
    notif_time_min:"il y a {n}m",
    notif_time_hour:"il y a {n}h",
    notif_time_day:"il y a {n}j",
    notif_desc_follow_request:"veut te suivre",
    notif_desc_follow_accepted:"a accepté ta demande",
    notif_desc_reaction_plain:"a réagi à une de tes pages",
    notif_desc_reaction_with_emoji:"a réagi avec {emoji} à une de tes pages",
    notif_desc_shared_invite:"t'a invité(e) au livre partagé",
    notif_desc_shared_deleted:"a supprimé le livre partagé",
    mod_locked_message:"Cette section n'est pas disponible.",
    mod_header_title:"🛡 Contenu bloqué",
    mod_empty:"Aucun blocage enregistré pour l'instant.",
    mod_image_loading:"Chargement de l'image...",
    tour_step1_title:"Le Fil",
    tour_step1_body:"Ici tu vois les photos des personnes que tu suis, tes micro-livres en haut, et ce qu'il reste à remplir dans le livre de cette année.",
    tour_step2_title:"Capture",
    tour_step2_body:"Une photo par jour, pour toujours — elle ne s'efface pas et ne se modifie pas. Si tu n'as pas de photo aujourd'hui, tu peux écrire juste une pensée, en choisissant aussi la police.",
    tour_step3_title:"Bibliothèque",
    tour_step3_body:"Chaque jour devient une page. Ici tu vois le livre entier de l'année, jour par jour, depuis le 1er janvier — même les jours manquants ont leur petit dessin.",
    tour_step4_title:"Micro-livres",
    tour_step4_body:"Des collections plus petites — un voyage, des vacances — qui accompagnent le livre principal sans le remplacer. Tu peux même en créer un avec un ami ou toute la famille.",
    tour_step5_title:"Notebook",
    tour_step5_body:"Écris à tes amis comme dans un vrai carnet — choisis le papier et l'écriture que tu préfères pour chaque conversation.",
    tour_step6_title:"Ton profil",
    tour_step6_body:"C'est ici que tu personnalises ton thème, ta photo et ta couverture — et que tu trouves Objectifs, Récompenses et Oncer Future, les messages que tu écris aujourd'hui pour ton futur.",
    tour_skip:"Passer la visite",
    tour_next:"Suivant →",
    tour_finish:"Compris, on y va ! ✦",
    app_print_soon_title:"Bientôt disponible",
    app_print_soon_body_1:"Nous préparons l'impression de tes livres.",
    app_print_soon_body_2:"Reviens bientôt.",
    app_print_soon_cta:"Retour au Fil",
    app_notif_reminder_body:"Tu n'as pas encore fait la page d'aujourd'hui 📖",
    app_swipehint_title:"Un conseil rapide",
    app_swipehint_body1:"Fais glisser ton doigt à gauche ou à droite pour passer d'une section à l'autre, comme si tu feuilletais un livre.",
    app_swipehint_body2:"Dans une discussion ou un menu, glisse vers la droite pour revenir en arrière.",
    app_swipehint_cta:"Compris",
    auth_pwrecovery_title:"Nouveau mot de passe",
    auth_pwrecovery_subtitle:"Choisis-en un nouveau pour ton compte Oncer.",
    auth_pw_repeat_label:"Répète le mot de passe",
    auth_pwrecovery_save:"Enregistrer le nouveau mot de passe ✦",
    auth_tagline_1:"Ton histoire,",
    auth_tagline_2:"une page par jour.",
    auth_welcome_cta:"Crée ton Oncer →",
    auth_welcome_have_account:"J'ai déjà un compte",
    auth_welcome_terms_notice:"En continuant, tu acceptes les Conditions et la Politique de confidentialité, consultables à tout moment depuis les Paramètres.",
    auth_checkemail_title:"Vérifie ton e-mail",
    auth_checkemail_sent_to:"Nous t'avons envoyé un lien de confirmation à",
    auth_checkemail_instructions:"Ouvre ce lien — il te ramènera directement ici pour choisir ton nom d'utilisateur, ton thème et une photo. Une seule étape, pas deux.",
    auth_checkemail_spam_hint:"Tu ne le trouves pas ? Vérifie aussi tes spams.",
    auth_checkemail_confirmed_link:"J'ai déjà confirmé, emmène-moi à la connexion →",
    auth_login_eyebrow:"Se connecter",
    auth_login_title:"Bon retour.",
    auth_login_id_label:"E-mail ou nom d'utilisateur",
    auth_login_id_placeholder:"toi@exemple.fr ou nom d'utilisateur",
    auth_login_username_hint:"Tu ne te souviens plus de ton nom d'utilisateur ? Pas de souci — l'e-mail suffit.",
    auth_password_label:"Mot de passe",
    auth_login_forgot:"Mot de passe oublié ?",
    auth_login_submit:"Se connecter →",
    auth_login_submitting:"Connexion...",
    auth_err_wrong_creds:"E-mail/nom d'utilisateur ou mot de passe incorrect.",
    auth_err_locked_out:"Trop de tentatives. Réessayez dans {minutes} minutes.",
    auth_err_captcha_required:"Complétez d'abord la vérification de sécurité ci-dessous.",
    auth_err_email_not_confirmed:"Tu dois d'abord confirmer ton e-mail — vérifie ta boîte de réception et ouvre le lien que nous t'avons envoyé.",
    auth_err_no_account_username:"Aucun compte avec ce nom d'utilisateur.",
    auth_forgot_eyebrow:"Récupération du mot de passe",
    auth_forgot_title:"Nous t'envoyons un lien.",
    auth_forgot_subtitle:"Indique l'e-mail avec lequel tu t'es inscrit(e) — tu recevras un lien pour choisir un nouveau mot de passe.",
    auth_forgot_email_placeholder:"toi@exemple.fr",
    auth_forgot_success:"✓ Vérifie ton e-mail — nous t'avons envoyé un lien pour choisir un nouveau mot de passe.",
    auth_forgot_cant_access:"Tu n'arrives à te connecter d'aucune façon ?",
    auth_forgot_write_us:"Écris-nous",
    auth_forgot_submit:"Envoyer le lien →",
    auth_forgot_submitting:"Envoi...",
    auth_signup0_eyebrow:"Crée ton accès",
    auth_signup0_title:"Commençons par ici",
    auth_signup0_subtitle:"Nous t'envoyons tout de suite un e-mail de confirmation — tu choisiras ton nom d'utilisateur, ton thème et une photo après l'avoir ouvert, en une seule étape.",
    auth_signup0_password_placeholder:"Au moins 6 caractères",
    auth_err_validation:"Un e-mail valide et un mot de passe d'au moins 6 caractères.",
    auth_continue:"Continuer →",
    auth_creating:"Création...",
    auth_signup1_eyebrow:"1 sur 4 · E-mail confirmé !",
    auth_signup1_title:"Comment tu t'appelles ?",
    auth_signup1_subtitle:"Ton nom d'utilisateur sera visible par les autres Oncers.",
    auth_signup1_username_placeholder:"ex. matte__",
    auth_signup1_min_chars:"Au moins 2 caractères",
    auth_signup1_checking:"Vérification de la disponibilité...",
    auth_signup1_available:"✓ Nom d'utilisateur disponible",
    auth_signup1_taken:"Ce nom d'utilisateur est déjà pris, choisis-en un autre.",
    auth_signup2_eyebrow:"2 sur 4",
    auth_signup2_title:"Choisis ton thème",
    auth_signup2_subtitle:"Il définit ta bibliothèque. Tu peux toujours le changer.",
    auth_signup2_selected:"✓ Sélectionné",
    auth_signup3_eyebrow:"3 sur 4",
    auth_signup3_title:"Quand prends-tu généralement ta photo ?",
    auth_signup3_subtitle:"Nous t'envoyons une notification par jour. Une seule.",
    auth_signup3_submit:"Commencer à capturer ✦",
    auth_signup4_eyebrow:"4 sur 4",
    auth_signup4_title:"Tu ajoutes une photo ?",
    auth_signup4_subtitle:"Comme ça, les personnes qui te suivent te reconnaissent tout de suite. Facultatif — tu peux aussi le faire plus tard.",
    auth_signup4_saved:"Photo enregistrée",
    auth_signup4_tap_hint:"Touche pour choisir une photo",
    auth_signup4_finish_with_photo:"C'est fait, on y va ✦",
    auth_signup4_finish_no_photo:"Continuer ✦",
    auth_signup4_skip:"ou continue sans →",
    auth_err_email_registered:"Cet e-mail est déjà enregistré. Essaie de te connecter.",
    auth_err_username_taken:"Nom d'utilisateur déjà pris, choisis-en un autre.",
    auth_err_session_expired:"Session expirée, réessaie de te connecter.",
    priv_title:"Politique de confidentialité",
    priv_last_updated:"Dernière mise à jour : septembre 2026 · Conformément au Règlement (UE) 2016/679 (RGPD)",
    priv_beta_notice:"⚠️ Cette politique décrit le fonctionnement actuel d'Oncer, encore en version bêta. Elle ne remplace pas un conseil juridique : avant tout lancement public, elle doit être révisée par un professionnel qualifié, surtout avant l'activation des paiements réels.",
    priv_s1_title:"1. Responsable du traitement",
    priv_s1_body:"Le responsable du traitement des données est l'équipe Oncer, joignable à l'adresse e-mail indiquée en bas de cette page.",
    priv_s2_title:"2. Comment fonctionne Oncer",
    priv_s2_pre:"Oncer utilise ",
    priv_s2_bold:"un vrai serveur (Supabase)",
    priv_s2_post:" pour enregistrer tes contenus — photos, légendes, thèmes, conversations, objectifs, livres partagés — afin que tu puisses accéder à ton journal depuis plusieurs appareils et que les personnes que tu invites puissent voir les livres partagés avec elles. Les données sont conservées sur les serveurs de Supabase, notre fournisseur de base de données et d'authentification, protégées par des règles d'accès qui permettent à chaque utilisateur de ne voir que ses propres données et celles explicitement partagées avec lui.",
    priv_s3_title:"3. Données traitées",
    priv_s3_body:"E-mail et mot de passe (géré de façon sécurisée par Supabase Auth, jamais visible pour nous en clair), nom d'utilisateur, bio, photos importées, légendes, thème graphique choisi, filtres créés, messages du Notebook, objectifs et récompenses débloquées, contenu des livres partagés. Si tu commandes un livre imprimé : nom, adresse de livraison, ville, code postal, pays, téléphone et e-mail utilisés pour la livraison.",
    priv_s4_title:"4. Finalité et base juridique",
    priv_s4_body:"Les données sont traitées pour faire fonctionner l'application selon ses fonctionnalités (journal photo, personnalisation, gamification, livres partagés) et, pour qui commande un livre imprimé, pour exécuter la commande (production, livraison, paiement). La base juridique est l'exécution d'un service demandé par l'utilisateur (art. 6.1.b du RGPD) et, le cas échéant, le respect d'obligations légales fiscales/comptables (art. 6.1.c du RGPD).",
    priv_s5_title:"5. Services tiers",
    priv_s5_body:"Pour fonctionner, Oncer s'appuie sur : Supabase (base de données, authentification et stockage des photos) ; Google Fonts (polices) ; api.qrserver.com (génère le QR code pour inviter d'autres personnes à un livre partagé, ne reçoit que le lien d'invitation). Si tu commandes un livre imprimé, sont aussi impliqués : Stripe (traite le paiement — nous ne recevons jamais les données de ta carte, qui restent entièrement chez Stripe) ; Prodigi et Gelato (fournisseurs d'impression et de livraison, qui reçoivent ton nom, ton adresse de livraison et le fichier du livre pour le produire et l'expédier). Chacun de ces fournisseurs traite les données selon sa propre politique de confidentialité.",
    priv_s6_title:"6. Conservation des données",
    priv_s6_body:"Les données de profil et de contenu restent sur nos serveurs jusqu'à la suppression de ton compte (Paramètres → Supprimer le compte), qui les supprime définitivement. Les données d'une commande d'impression déjà envoyée à Prodigi, Gelato ou Stripe restent soumises à leurs propres durées de conservation (obligations fiscales ou de garantie, par exemple) même après la suppression du compte.",
    priv_s7_title:"7. Tes droits",
    priv_s7_pre:"Conformément aux articles 15 à 22 du RGPD, tu as le droit de : accéder à tes données, les corriger, les supprimer, en limiter le traitement, en demander la portabilité et t'opposer au traitement. Tu peux exercer l'accès et la suppression directement depuis : ",
    priv_s7_bold1:"Paramètres → Exporter tes données",
    priv_s7_mid:" (portabilité) et ",
    priv_s7_bold2:"Paramètres → Supprimer le compte",
    priv_s7_post:" (suppression). Pour une demande de rectification, de limitation ou d'opposition, écris-nous à l'adresse indiquée en bas de cette page.",
    priv_s8_title:"8. Sécurité",
    priv_s8_body:"Tes données sont conservées sur les serveurs de Supabase et protégées par une authentification et des règles d'accès qui isolent les données de chaque utilisateur. Les communications entre l'application et le serveur sont chiffrées (HTTPS/TLS). Nous te recommandons quand même de protéger ton téléphone/ordinateur avec un verrouillage d'écran et de ne jamais partager ton mot de passe.",
    priv_s9_title:"9. Mineurs",
    priv_s9_body:"Oncer n'est pas destiné aux personnes de moins de 14 ans. Si tu as entre 14 et 18 ans, l'utilisation de l'application nécessite le consentement de la personne exerçant l'autorité parentale.",
    priv_s10_title:"10. Réclamations",
    priv_s10_body:"Tu as le droit de déposer une réclamation auprès de l'autorité italienne de protection des données (Garante per la Protezione dei Dati Personali, [www.garanteprivacy.it](https://www.garanteprivacy.it)), ou auprès de l'autorité de protection des données de ton propre pays, si tu estimes que le traitement viole le RGPD.",
    priv_s11_title:"11. Modifications",
    priv_s11_body:"Cette politique est mise à jour lorsque la façon dont nous traitons tes données change de manière substantielle — la dernière révision reflète le passage à un vrai serveur (Supabase), l'introduction des commandes d'impression et l'implication des fournisseurs tiers associés (Stripe, Prodigi, Gelato).",
    priv_s12_title:"12. Contact",
    priv_s12_body:"Pour toute demande concernant tes données : hello@oncer.app",
    terms_title:"Conditions générales",
    terms_last_updated:"Dernière mise à jour : septembre 2026",
    terms_beta_notice:"⚠️ Version bêta à des fins de démonstration. Avant tout lancement public, ces conditions doivent être formalisées avec un avocat, surtout avant l'activation des paiements réels pour les abonnements.",
    terms_s1_title:"1. Acceptation",
    terms_s1_body:"En utilisant Oncer, tu acceptes ces conditions. Si tu n'es pas d'accord, nous te demandons de ne pas utiliser l'application.",
    terms_s2_title:"2. Description du service",
    terms_s2_body:"Oncer est une application de journal photo social : un contenu par jour, réuni dans un livre annuel, dans des micro-livres personnels ou dans des livres partagés avec d'autres personnes, avec des fonctionnalités sociales, de personnalisation et la possibilité de commander l'impression physique de tes livres.",
    terms_s3_title:"3. Comptes et contenus",
    terms_s3_body:"Tu es responsable des contenus que tu publies. Il est interdit de publier des contenus illégaux, offensants, ou qui violent les droits de tiers.",
    terms_s4_title:"4. Propriété des contenus",
    terms_s4_body:"Les contenus que tu crées restent les tiens. Oncer n'en revendique pas la propriété.",
    terms_s5_title:"5. Signature Oncer sur les partages",
    terms_s5_body:"Les images partagées en dehors de l'application incluent automatiquement et de façon permanente une référence à la marque Oncer.",
    terms_s6_title:"6. Abonnements",
    terms_s6_body:"Les offres payantes (Oncer+, Oncer Pro) proposent des fonctionnalités supplémentaires décrites dans la section Abonnement. Aucun paiement réel n'est actuellement traité pour les abonnements — ils restent un aperçu des fonctionnalités à venir.",
    terms_s7_title:"7. Commandes d'impression",
    terms_s7_body:"Sur Oncer, tu peux commander l'impression physique de tes livres (couverture rigide, souple ou layflat, selon les formats disponibles). Le paiement est traité par Stripe : nous ne recevons ni ne conservons jamais les données de ta carte. La production et l'expédition sont confiées à des prestataires d'impression externes (Prodigi, Gelato), qui reçoivent le fichier du livre et l'adresse de livraison nécessaires pour traiter la commande. Les délais de livraison varient selon le prestataire et le pays de destination. Une fois envoyée en production, une commande ne peut plus être modifiée ni annulée depuis l'application : pour toute erreur, tout retard ou toute question sur une commande précise, écris-nous à hello@oncer.app.",
    terms_s8_title:"8. Limitation de responsabilité",
    terms_s8_body:"Oncer est fourni « tel quel », en version bêta. Nous ne garantissons pas l'absence d'erreurs ou d'interruptions de service.",
    terms_s9_title:"9. Modifications",
    terms_s9_body:"Ces conditions peuvent être mises à jour ; les changements importants seront communiqués dans l'application.",
    terms_s10_title:"10. Contact",
    terms_s10_body:"hello@oncer.app",
    // ── Batch 3: Library.jsx, SettingsShared.jsx ──────────────────────
    lib_header_title_mine:"📚 Livre {year}",
    lib_header_title_other:"📚 Livre {year} de @{username}",
    lib_recent_pages:"Dernières pages",
    lib_page_label:"Page {day}",
    lib_today_badge:"aujourd'hui",
    lib_empty_title_mine:"Le livre {year} est vide",
    lib_empty_title_other:"@{username} n'a pas encore de pages",
    lib_empty_subtitle:"Prends ta première photo pour commencer.",
    lib_empty_cta:"✦ Capturer maintenant",
    lib_print_cta:"🖨 Imprimer ce livre",
    lib_page_detail_title:"Page {day} · {year}",
    lib_share_external:"Partager en dehors d'Oncer",
    lib_permanent_notice:"🔒 Cette page est permanente et ne peut pas être modifiée.",
    shared_claim_cta:"Récupérer ✦",
    shared_claimed_label:"✓ Obtenu",
    goal_unlocked_toast:"🎉 Tu as débloqué un objectif ! Va dans Objectifs pour récupérer {name}.",
    // ── Batch 4: Print.jsx (schermate stampa, non ancora raggiungibili) ────
    print_preview_header:"Aperçu de l'impression",
    print_cover_stats:"{total} {pages} · {theme}",
    print_highlights_label:"Quelques moments forts",
    print_highlights_empty:"Encore peu de pages, mais déjà les tiennes.",
    print_stat_microbook:"Micro-livre",
    print_stat_book:"Livre {year}",
    print_continue_cta:"Continuer vers l'impression →",
    print_order_header:"Ton livre imprimé",
    print_format_label:"Format",
    print_format_cheaper:"plus économique",
    print_selected:"✓ sélectionné",
    print_empty_pages_label:"Inclure aussi les pages vides",
    print_empty_pages_sub:"Imprime un livre complet de 365 pages, avec les jours sans contenu laissés blancs — sinon on n'imprime que les pages qui ont vraiment quelque chose dedans.",
    print_feature_cover:"Couverture rigide avec ton thème en relief",
    print_feature_pages:"Chaque page est celle que tu as écrite — même les jours vides",
    print_feature_delivery:"Livraison en {min}-{max} jours ouvrés",
    print_feature_memory:"Le souvenir qu'un fil infini ne te donnera jamais",
    print_price_label:"Ton {year}, pour toujours",
    print_price_fallback:"livre",
    print_price_note:"Livraison incluse · Paiement sécurisé",
    print_price_breakdown:"Livre {book}€ + livraison {shipping}€",
    print_order_cta:"Commande ton livre — €{price}",
    print_ordered_body:"Les vraies commandes ne sont pas encore actives — nous préparons l'impression réelle. Ton livre reste ici, prêt, pour le moment venu.",
    print_pdf_test_label:"Test admin — PDF d'impression réel",
    print_pdf_test_sub:"Génère le PDF prêt à imprimer (fond perdu et zone de sécurité inclus) à envoyer manuellement à un prestataire d'impression externe.",
    print_pdf_test_cta:"Générer le PDF d'impression",
    print_pdf_test_busy:"Génération en cours…",
    print_pdf_test_ready:"PDF prêt — touche pour l'ouvrir/le télécharger",
    print_pdf_test_error:"Échec de la génération du PDF. Réessaie dans un instant.",
    print_format_test_badge:"Test Gelato",
    print_price_not_final_note:"Prix provisoire, pas encore évalué pour ce format — pour le test PDF uniquement.",
    print_hub_header:"Que veux-tu imprimer ?",
    print_hub_annual_section:"Livre de l'année",
    print_hub_micro_section:"Micro-livres",
    print_hub_empty_title:"Rien à imprimer pour l'instant",
    print_hub_empty_sub:"Écris quelques pages dans ton livre de l'année ou dans un micro-livre, puis reviens ici.",
    print_hub_status_done:"Terminé",
    print_copies_label:"Nombre de copies",
    print_copies_sub:"Utile si plusieurs personnes veulent chacune leur copie du même livre.",
    print_copies_shared_hint:"Partagé avec {n} personnes — elles en veulent peut-être une copie aussi.",
    print_copies_multiplier_note:"€{unit} × {n} copies",
    auth_signup0_birthdate_label:"Date de naissance",
    auth_birthdate_day:"Jour",
    auth_birthdate_month:"Mois",
    auth_birthdate_year:"Année",
    auth_err_underage:"Tu dois avoir au moins 18 ans pour utiliser Oncer.",
  },
};

export function t(key,vars){
  const dict=STR[LANG]||STR.it;
  let str=(key in dict)?dict[key]:((key in STR.it)?STR.it[key]:key);
  if(vars){
    Object.keys(vars).forEach(k=>{
      str=str.split("{"+k+"}").join(vars[k]);
    });
  }
  return str;
}
